import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  Ban,
  CheckCircle2,
  Copy,
  Download,
  Eye,
  EyeOff,
  ExternalLink,
  FileText,
  KeyRound,
  LifeBuoy,
  Loader2,
  LockKeyhole,
  LogOut,
  Play,
  RadioTower,
  Search,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Table2,
  UserRound,
} from 'lucide-react';
import {
  authorityWorkarounds,
  carrierCertificationMatrix,
  carrierCertificationSummary,
  implementationApi,
  launchGates,
  navSections,
  proofEvents,
  readinessBadges,
  SectionId,
  StatusTone,
  workstreams,
} from './data';

const billingApiBase = '/api/billing';

function billingFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.delete('X-VAERION-Tenant');
  headers.delete('X-VAERION-Actor');
  headers.delete('X-VAERION-Request-ID');
  headers.delete('X-VAERION-Portal-Timestamp');
  headers.delete('X-VAERION-Portal-Nonce');
  headers.delete('X-VAERION-Portal-Signature');
  return fetch(`${billingApiBase}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });
}

function initialSectionFromHash(): SectionId {
  if (typeof window === 'undefined') return 'launch';
  const hash = window.location.hash.replace('#', '') as SectionId;
  return navSections.some((section) => section.id === hash) ? hash : 'launch';
}

const toneLabel: Record<StatusTone, string> = {
  complete: 'Complete',
  ready: 'Ready',
  action: 'Needs action',
  blocked: 'Counterparty authority',
  neutral: 'Observed',
};

type EvidenceDossierItem = {
  surface: string;
  evidence_type: string;
  source: string;
  receipt_id: string;
  recorded_at: string;
  method: string;
  result: string;
  artifact: string;
  proves: string;
  does_not_prove: string;
  authority_limit: string;
};

type ApprovalPacketId =
  | 'payer-trading-partner'
  | 'epic-production-access'
  | 'hospital-cutover'
  | 'financial-authority';

type ApprovalPacketTemplate = {
  id: ApprovalPacketId;
  title: string;
  counterparty: string;
  purpose: string;
  authorityEvidenceType: string;
  evidenceIssuer: string;
  evidenceScope: string;
  evidenceReference: string;
  evidenceNeeded: string[];
  vaerionPrefill: string[];
  externalFields: string[];
  proofReceipts: string[];
  portalChecklist: string[];
  submissionBoundary: string;
  downloadName: string;
  nextStep: string;
};

type AuthorityAcquisitionTrack = {
  id: string;
  title: string;
  counterparty: string;
  portalLabel: string;
  portalUrl: string;
  sourceLabel: string;
  sourceUrl: string;
  packetId: ApprovalPacketId;
  authorityEvidenceType: string;
  evidenceIssuer: string;
  evidenceScope: string;
  referencePlaceholder: string;
  vaerionCanDoNow: string[];
  counterpartyMustReturn: string[];
  proofToRunFirst: string;
  proofAction: 'payer' | 'ehr' | 'payment' | 'hospital';
};

type PreparedApprovalPacket = ApprovalPacketTemplate & {
  packet_id: string;
  generated_at: string;
  prepared_by: 'partner_billing_console';
  status: 'prepared_for_counterparty_review';
  live_external_action: 'not_submitted_by_vaerion';
  readiness_receipt_id?: string;
};

type SessionState = 'checking' | 'authenticated' | 'unauthenticated';

type PartnerProofPacket = {
  packet_type: 'partner_launch_packet';
  packet_id: string;
  generated_at: string;
  generated_by: string;
  live_external_action: 'blocked_until_authority_proof';
  storage: {
    current_location: string;
    durable_receipt: string;
    durable_packet_artifact: string;
  };
  proof_standard: {
    purpose: string;
    evidence_scope: string;
    production_authority_boundary: string;
  };
  launch_dossier_snapshot?: {
    tenant: string;
    generated_at: string;
    verdict: string;
    vaerion_owned_status: string;
    production_cutover_status: string;
    proof_packet_count: number;
    enterprise_surface_receipt_count: number;
    required_surface_count: number;
    proven_surface_count: number;
    remaining_surface_ids: string[];
    remaining_external_authority: string[];
    live_external_action: string;
  };
  enterprise_readiness_snapshot?: {
    generated_at: string;
    product_boundary: string;
    designed_purpose: string;
    vaerion_owned_readiness: string;
    live_production_cutover: string;
    deployment_modes: Array<{ mode: string; status: string; what_it_proves: string; limit: string }>;
    evidence_surface_count: number;
    recorded_surface_count: number;
    proof_packet_count: number;
    live_production_requirements: Array<{ requirement: string; owner: string; authority_boundary: string }>;
  };
  evidence_dossier: EvidenceDossierItem[];
  verification_matrix: Array<{
    domain: string;
    method: string;
    artifact: string;
    result: string;
    receipt: string;
    production_authority: string;
  }>;
  server_vault: {
    packet_count: number;
    saved_packet_ids: string[];
    packet_artifact_state: string;
  };
  partner_review_checklist: string[];
  summary: string;
  launch_gates: Array<{
    label: string;
    state: string;
    proof: string;
  }>;
  workstreams: Array<{
    title: string;
    owner: string;
    summary: string;
    metrics: Array<{ label: string; value: string }>;
    authority_gated_steps: string[];
  }>;
  carrier_routes: Array<{
    carrier: string;
    transactions: string[];
    certification_receipt: string;
    production_gate: string;
    does_not_prove: string;
  }>;
  authority_workarounds: Array<{
    authority: string;
    cannot_bypass: string;
    vaerion_owned_path: string;
    proof_receipt: string;
  }>;
  approval_packets?: Array<{
    packet_id: string;
    packet_type: ApprovalPacketId;
    title: string;
    counterparty: string;
    status: PreparedApprovalPacket['status'];
    generated_at: string;
    readiness_receipt_id?: string;
    live_external_action: PreparedApprovalPacket['live_external_action'];
    evidence_needed: string[];
    vaerion_prefill: string[];
    external_fields: string[];
    next_step: string;
  }>;
  payment_proof?: {
    receipt_id: string;
    scenario_id: string;
    claim_validation: string;
    acknowledgement: string;
    era_posting: string;
    deposit_reconciliation: string;
    duplicate_remittance_guard: string;
    refund_void_reversal: string;
    proves: string;
    does_not_prove: string;
  };
  payment_rail_sandbox?: {
    receipt_id: string;
    scenario_id: string;
    nacha_file_validation: string;
    bank_response_emulator: string;
    deposit_ledger: string;
    replay_vault: string;
    accounting_controls: string;
    proves: string;
    does_not_prove: string;
  };
  enterprise_proof_verifier?: {
    verifier_name: string;
    generated_at: string;
    verdict: string;
    proof_domain_count: number;
    proof_counts: EnterpriseProofVerifier['proof_counts'];
    boundary: string;
  };
  production_activation_tracks?: NonNullable<EnterpriseProofVerifier['production_activation_tracks']>;
  authority_evidence_receipts?: Array<{
    receipt_id: string;
    evidence_type: string;
    lifecycle_state: string;
    issuer: string;
    authority_scope: string;
    artifact_reference: string;
    final_cutover_review_eligible: boolean;
    production_authority_unlocked: boolean;
    raw_phi_stored: boolean;
  }>;
  enterprise_surface_receipts?: Array<{
    surface_id: string;
    title: string;
    receipt_id: string;
    verdict: string;
    proves: string;
    does_not_prove: string;
    remaining_external_authority: string[];
  }>;
  proof_events: Array<{ event: string; detail: string }>;
  implementation_api: string[];
  external_authority_still_required: string[];
  guardrails: string[];
};

type SavedProofPacketSummary = {
  packet_id: string;
  packet_type: string;
  created_at: string;
  actor_id: string;
  request_id: string;
  summary: string;
  live_external_action: string;
  download_path: string;
};

type LaunchDossier = {
  ok: boolean;
  tenant: string;
  generated_at: string;
  verdict: string;
  readiness: {
    vaerion_owned_status: string;
    production_cutover_status: string;
    proof_packet_count: number;
    enterprise_surface_receipt_count: number;
    required_surface_count: number;
    proven_surface_count: number;
    missing_surface_ids: string[];
  };
  proof_packets: SavedProofPacketSummary[];
  enterprise_surface_receipts: Array<{
    receipt_id: string;
    surface_id: string;
    title: string;
    created_at: string;
    actor_id: string;
    request_id: string;
    verdict: string;
    live_external_action: string;
    remaining_external_authority: string[];
  }>;
  remaining_external_authority: string[];
  next_actions: string[];
  live_external_action: string;
};

type EnterpriseReadiness = {
  ok: boolean;
  tenant: string;
  generated_at: string;
  product_boundary: string;
  designed_purpose: string;
  status: {
    vaerion_owned_readiness: string;
    vaerion_owned_surface_count: number;
    required_vaerion_owned_surface_count: number;
    proof_packet_count: number;
    live_production_cutover: string;
  };
  deployment_modes: Array<{
    mode: string;
    status: string;
    what_it_proves: string;
    limit: string;
  }>;
  evidence_surfaces: Array<{
    surface_id: string;
    title: string;
    status: string;
    latest_receipt_id: string | null;
    latest_receipt_at: string | null;
    evidence_method: string[];
    artifacts: string[];
    proves: string;
    does_not_prove: string;
    counterparty_authority_required: string[];
    live_external_action: string;
  }>;
  proof_endpoints: string[];
  current_verified_evidence: {
    proof_packets: Array<{ packet_id: string; created_at: string; summary: string }>;
    latest_surface_receipts: Array<{ surface_id: string; receipt_id: string; created_at: string }>;
  };
  live_production_requirements: Array<{
    requirement: string;
    owner: string;
    vaerion_can_prepare: string;
    authority_boundary: string;
  }>;
  non_negotiable_guardrails: string[];
  next_best_actions: string[];
};

type EnterpriseProofVerifier = {
  ok: boolean;
  tenant: string;
  generated_at: string;
  verifier_name: string;
  verdict: {
    vaerion_owned_evidence: string;
    production_authority: string;
    plain_english: string;
  };
  proof_domains: Array<{
    domain_id: string;
    title: string;
    vaerion_proof_available_now: string[] | string;
    counterparty_authority_required: string[];
    inspectable_receipts: string[];
    accepted_evidence_type: string | null;
    proof_method: string;
    verification_ladder?: Array<{
      level: string;
      proves: string;
      account_or_evidence_needed: string;
      production_meaning: string;
    }>;
    latest_authority_evidence?: {
      receipt_id: string;
      lifecycle_state: string;
      issuer: string;
      authority_scope: string;
      artifact_reference: string;
      artifact_sha256: string;
      reviewer?: string;
      reviewed_at?: string;
      review_rationale?: string;
      final_cutover_review_eligible?: boolean;
    } | null;
  }>;
  production_activation_tracks?: Array<{
    track_id: string;
    title: string;
    purpose: string;
    authority_evidence_type: string;
    prepare_surface: string;
    counterparty: string;
    evidence_label: string;
    final_review: string;
    current_state: string;
    next_best_action: string;
    latest_authority_evidence?: {
      receipt_id: string;
      lifecycle_state: string;
      issuer: string;
      authority_scope: string;
      artifact_reference: string;
      artifact_sha256: string;
      reviewer?: string;
      reviewed_at?: string;
      review_rationale?: string;
      final_cutover_review_eligible?: boolean;
    } | null;
    states: Array<{
      state_id: string;
      label: string;
      owner: string;
      what_vaerion_can_do: string;
      proof_or_action: string;
      status: string;
      sequence: number;
    }>;
  }>;
  proof_counts: {
    proof_packets: number;
    enterprise_surface_receipts: number;
    authority_evidence: number;
    accepted_authority_evidence: number;
  };
  cto_review_script: string[];
  boundary: string;
  authority_evidence_intake: string;
};

type AuthorityEvidenceReceipt = {
  ok: boolean;
  receipt_id: string;
  review_receipt_id?: string;
  evidence_type: string;
  lifecycle_state: string;
  issuer: string;
  authority_scope: string;
  artifact_reference: string;
  artifact_sha256: string;
  final_cutover_review_eligible?: boolean;
  production_authority_unlocked: boolean;
  raw_phi_stored: boolean;
  reviewer?: string;
  reviewed_at?: string;
  review_rationale?: string;
  live_external_action?: string;
  proves?: string;
  does_not_prove?: string;
  next_step: string;
};

type EpicSandboxReceipt = {
  receipt_id: string;
  created_at: string;
  mode: string;
  capability_statement: {
    endpoint: string;
    fhir_version: string;
    software: string;
    publisher?: string;
    status?: string;
    required_resource_count?: number;
    advertised_required_resource_count?: number;
    missing_required_resources?: string[];
    interaction_warnings?: string[];
    interaction_coverage?: Array<{
      resource: string;
      required_interaction: string;
      resource_advertised: boolean;
      advertised_interactions: string[];
      required_interaction_advertised: boolean;
      status: string;
    }>;
  };
  smart_configuration: {
    endpoint: string;
    endpoint_attempts?: Array<{ endpoint: string; status: number; ok: boolean }>;
    authorization_endpoint: string;
    token_endpoint: string;
    jwks_uri?: string;
    scopes_supported?: string[];
    capabilities?: string[];
    status?: string;
  };
  resource_support: Record<'Patient' | 'Coverage' | 'Encounter' | 'DocumentReference', boolean>;
  epic_registration_package?: {
    application_name: string;
    application_audience: string;
    use_case: string;
    public_documentation_url: string;
    non_production_jwks_url: string;
    production_jwks_url: string;
    requested_fhir_r4_capabilities: string[];
    billing_use: string[];
    secret_policy: string;
  };
  backend_oauth_readiness?: {
    status: string;
    client_id_reference_present: boolean;
    public_jwks_url_present: boolean;
    credential_secret_reference_present: boolean;
    token_endpoint_present: boolean;
    missing_references: string[];
    secret_handling: string;
  };
  authenticated_resource_tests?: Record<
    string,
    | {
        status: string;
        required_scope?: string;
        proof_available_now?: string;
      }
    | string
  >;
  mapping_validation?: Array<{
    resource: string;
    fixture_id: string;
    status: string;
    proof: string;
    live_epic_call: string;
  }>;
  conformance_checklist?: Array<{
    step: string;
    status: string;
    evidence: string;
  }>;
  mapping_tests: string[];
  proves: string;
  does_not_prove: string;
  live_external_action: string;
};

type EpicSandboxTokenReceipt = {
  receipt_id: string;
  created_at: string;
  mode: string;
  fhir_base_url: string;
  public_jwks_url: string;
  smart_configuration: {
    endpoint: string;
    token_endpoint: string;
    authorization_endpoint?: string;
    status?: string;
  };
  client_assertion: {
    client_id: string;
    token_endpoint: string;
    kid: string;
    alg: string;
    jti: string;
    expires_at: string;
    private_key_exported: false;
    raw_assertion_stored: false;
  };
  token_exchange: {
    token_endpoint: string;
    status: string;
    http_status: number;
    token_type: string;
    expires_in_seconds: number | null;
    granted_scope: string;
    access_token_received: boolean;
    access_token_stored: false;
    raw_response_stored: false;
  };
  sandbox_fixture?: {
    patient_id: string;
    patient_label: string;
    fixture_source: string;
    phi_payload_stored: false;
  };
  live_resource_tests: Record<
    string,
    {
      status: string;
      reason?: string;
      http_status?: number;
      resource?: string;
      operation?: string;
      expected_resource_type?: string;
      returned_resource_type?: string;
      entry_count?: number | null;
      total?: number | null;
      id_matched?: boolean | null;
      endpoint_path?: string;
      required_scope?: string;
      raw_payload_stored?: false;
      fixture_id?: string;
      fixture_source?: string;
    }
  >;
  proves: string;
  does_not_prove: string;
  live_external_action: string;
};

const defaultEpicSandboxClientId = '999a7f63-39c4-4b75-922b-e1b168aa0a3c';
const defaultEpicSandboxSecretReference = 'aws-secretsmanager:vaerion/billing/epic/sandbox/private-jwk';

const billingEpicRegistrationPackage = {
  application_name: 'VAERION Billing',
  application_audience: 'Backend Systems',
  use_case: 'General',
  public_documentation_url: 'https://billing.vaerionsystems.com/#developer',
  non_production_jwks_url: 'https://billing.vaerionsystems.com/.well-known/epic-sandbox-jwks.json',
  production_jwks_url: 'https://billing.vaerionsystems.com/.well-known/epic-production-jwks.json',
  requested_fhir_r4_capabilities: [
    'Patient.read',
    'Coverage.read',
    'Encounter.read',
    'DocumentReference.read',
    'DocumentReference.create',
  ],
  billing_use: [
    'Patient demographic crosswalk',
    'Coverage and eligibility context',
    'Encounter context for claim validation',
    'DocumentReference attachment package readiness',
  ],
  secret_policy:
    'Only public JWKS URLs and secret references are used here. Private keys, tokens, and client secrets stay out of the browser and proof packet.',
};

type PaymentProofReceipt = {
  receipt_id: string;
  created_at: string;
  mode: string;
  scenario_id: string;
  claim_validation: {
    status: string;
    transaction_set: string;
    artifact: string;
  };
  payer_acknowledgement: {
    status: string;
    transaction_set: string;
    artifact: string;
  };
  era: {
    transaction_set: string;
    posting_status: string;
    paid_cents: number;
    adjustment_cents: number;
    patient_responsibility_cents: number;
    artifact: string;
  };
  deposit_reconciliation: {
    status: string;
    deposit_reference: string;
    variance_cents: number;
    artifact: string;
  };
  duplicate_remittance_guard: {
    status: string;
    duplicate_key: string;
    artifact: string;
  };
  refund_void_reversal: {
    status: string;
    refund: string;
    void: string;
    reversal: string;
    artifact: string;
  };
  proves: string;
  does_not_prove: string;
  live_external_action: string;
};

type PaymentRailSandboxReceipt = {
  receipt_id: string;
  created_at: string;
  mode: string;
  scenario_id: string;
  nacha_file_validation: {
    status: string;
    artifact: string;
  };
  bank_response_emulator: {
    status: string;
    artifact: string;
  };
  deposit_ledger: {
    status: string;
    artifact: string;
  };
  replay_vault: {
    status: string;
    artifact: string;
  };
  accounting_controls: {
    status: string;
    artifact: string;
  };
  proves: string;
  does_not_prove: string;
  live_external_action: string;
};

type WorkflowRun = {
  title: string;
  status: 'running' | 'recorded' | 'blocked';
  summary: string;
  receipt: string;
  nextSteps: string[];
  evidence: string[];
  blockers: string[];
};

type ActionFeedback = {
  title: string;
  detail: string;
  artifact?: string;
  targetId?: string;
  tone: 'neutral' | 'success' | 'blocked' | 'running';
};

type DeveloperOpsReceipt = {
  receipt_id: string;
  created_at: string;
  key_reference?: string;
  key_scope?: string;
  allowed_scopes?: string[];
  secret_value_returned?: boolean;
  event_type?: string;
  delivery?: { status: string; attempts: number; idempotency_key: string };
  signature?: { algorithm: string; signing_secret_reference: string; signing_secret_returned: boolean };
  dlq?: { status: string; replayable: boolean };
  proves: string;
  does_not_prove: string;
  live_external_action?: string;
};

type CompanionGuideReceipt = {
  receipt_id: string;
  created_at: string;
  profile: {
    payer_name: string;
    payer_id: string;
    x12_guide: string;
    transaction_sets: string[];
    acknowledgements: string[];
    required_identifiers: string[];
    validation_profile: Record<string, string>;
    production_route_authority: string;
  };
  proves: string;
  does_not_prove: string;
  live_external_action: string;
};

type ReplayImportReceipt = {
  receipt_id: string;
  created_at: string;
  manifest_id: string;
  deidentification_attestation: string;
  fixture_inventory: {
    total_artifacts: number;
    total_records: number;
    artifact_types: string[];
    artifact_hashes: string[];
  };
  replay_readiness: {
    status: string;
    next_step: string;
  };
  raw_phi_stored: false;
  proves: string;
  does_not_prove: string;
};

type ProofReviewReceipt = {
  receipt_id: string;
  object_type: string;
  object_id: string;
  lifecycle_state: string;
  reviewer: string;
  reviewed_at: string;
  external_authority_unlocked: false;
  proves: string;
  does_not_prove: string;
};

type EnterpriseSurfaceReceipt = {
  receipt_id: string;
  created_at: string;
  mode: string;
  surface_id: string;
  title: string;
  plain_english_verdict: string;
  checks: Array<{ label: string; status: 'passed' | 'ready' | 'blocked' | string; evidence: string }>;
  automations: string[];
  artifacts: string[];
  remaining_external_authority: string[];
  proves: string;
  does_not_prove: string;
  live_external_action: string;
};

const proofPacketStorageKey = 'vaerion-billing-partner-proof-packet';

const sectionProofGuide: Record<SectionId, { proves: string; use: string; limit: string }> = {
  launch: {
    proves: 'The server-side dossier can show which VAERION-owned deployment surfaces have receipts, which packets are stored, and which external authorities remain.',
    use: 'Start here, refresh the dossier, then run any surface awaiting evidence or generate the partner packet for review.',
    limit: 'It does not authorize live patient billing. Cutover remains authority-gated until hospital, payer, Epic/customer, and payment authority evidence exists.',
  },
  authority: {
    proves: 'VAERION can replace waiting time with non-PHI sandbox, synthetic, packet, and replay evidence without bypassing legal authority.',
    use: 'Run each non-PHI proof, then attach the receipt to the partner packet.',
    limit: 'A proof receipt is evidence of readiness, not permission to transmit claims, write to Epic, file appeals, or move money.',
  },
  approvals: {
    proves: 'VAERION can generate the counterparty-ready packet set needed for payer trading partner approval, Epic/customer access, hospital cutover, and financial authority.',
    use: 'Prepare the packet, download it, copy the portal checklist, then record the counterparty-issued evidence reference when it comes back.',
    limit: 'Packet preparation is not external submission, signature, payer acceptance, Epic production access, hospital authorization, or bank authority.',
  },
  onboarding: {
    proves: 'The tenant, facility, provider, payer, role, webhook, and ownership facts needed for implementation are modeled and checkable.',
    use: 'Use this area to prepare the hospital roster, provider records, billing roles, and security-review handoff.',
    limit: 'Real facility rosters, BAA/security approval, and provider/TIN/NPI authority must be supplied by the hospital or implementation owner.',
  },
  ehr: {
    proves: 'Epic public FHIR discovery, SMART/OIDC metadata, resource support, and VAERION mapping readiness can be tested without PHI.',
    use: 'Run the Epic sandbox proof, review the receipt, then add customer-issued Epic credential proof when available.',
    limit: 'Epic sandbox proof does not prove production patient access or production writes inside a hospital Epic tenant.',
  },
  payer: {
    proves: 'Payer route readiness can be tracked per carrier with companion-guide, 837, 999, 277CA, 835, and route certification evidence.',
    use: 'Select a carrier, run payer-route proof, generate a test packet, and review production cutover requirements.',
    limit: 'No live payer route can send until that exact payer issues trading-partner approval and endpoint authority.',
  },
  claims: {
    proves: 'Claim payloads can be checked for required facts, coding guardrails, route readiness, and pre-submission blockers before live billing.',
    use: 'Validate sample claims, review blockers, and use the developer schema handoff before connecting partner traffic.',
    limit: 'A valid claim packet still cannot be transmitted live until direct payer route certification exists.',
  },
  payments: {
    proves: 'VAERION can simulate claim payment, 835 ERA posting, deposit reconciliation, duplicate-remittance protection, refund, void, and reversal controls.',
    use: 'Run payment proof, run rail sandbox, then attach those receipts to the partner packet.',
    limit: 'No bank, ACH, EFT, lockbox, processor, refund, void, reversal, or treasury action happens without financial authority.',
  },
  denials: {
    proves: 'Denial classification, appeal packet drafting, correction readiness, deadlines, and payer-learning loops can be prepared.',
    use: 'Run denial/appeal proof and include it in the packet for RCM and compliance review.',
    limit: 'External appeal filing remains authority-gated until customer approval and payer route requirements are recorded.',
  },
  audit: {
    proves: 'Plain-English proof packets, evidence streams, saved server packets, and guarded action receipts can be generated and reviewed.',
    use: 'Generate a packet, save it to the proof vault, open saved packets, download JSON, or download the plain-English proof.',
    limit: 'Audit proof records what VAERION did; it does not replace outside acceptance by payers, hospitals, Epic, or financial institutions.',
  },
  developer: {
    proves: 'The implementation API surfaces, request headers, webhook-ready concepts, and no-secret handoff can be copied and reviewed.',
    use: 'Open developer docs, copy API surfaces, and use them for integration planning with the partner engineering team.',
    limit: 'Production API credentials, SSO/MFA/SCIM, webhook secrets, and customer security acceptance still require tenant setup.',
  },
};

const approvalPacketTemplates: ApprovalPacketTemplate[] = [
  {
    id: 'payer-trading-partner',
    title: 'Payer trading partner enrollment packet',
    counterparty: 'Payer EDI enrollment team',
    purpose: 'Request payer-issued production authority for direct VAERION claim, eligibility, acknowledgement, and ERA exchange.',
    authorityEvidenceType: 'payer_trading_partner_acceptance',
    evidenceIssuer: 'Payer EDI enrollment team',
    evidenceScope: '837P/837I/999/277CA/835 direct production route for the partner billing tenant',
    evidenceReference: 'payer-portal-case-or-trading-partner-id',
    evidenceNeeded: [
      'Trading partner ID or submitter ID issued by the payer.',
      'Payer portal case number or certification acceptance reference.',
      'Approved transaction families and payer IDs.',
      'Production endpoint, certificate, mailbox, or connection authority.',
      'Effective date and payer contact for cutover support.',
    ],
    vaerionPrefill: [
      'VAERION product description and non-PHI proof packet ID.',
      'Direct route architecture, callback, retry, DLQ, and acknowledgement lifecycle.',
      'Companion-guide validation receipt and 837/999/277CA/835 proof receipts.',
      'Security boundary: no live transmission until this exact payer route is accepted.',
    ],
    externalFields: [
      'Billing entity legal name, TIN/EIN, NPI, taxonomy, facility addresses, and contact owner.',
      'Payer-specific enrollment forms, payer IDs, line-of-business selection, and submitter agreement.',
      'Customer signature or authorized submitter confirmation when the payer requires it.',
    ],
    proofReceipts: [
      'Direct payer packet factory receipt.',
      'Companion-guide parser receipt.',
      'X12 test packet and acknowledgement lifecycle receipt.',
      'Authority evidence intake receipt after payer response.',
    ],
    portalChecklist: [
      'Open the payer EDI enrollment or trading partner portal.',
      'Select direct submitter or software vendor enrollment where available.',
      'Attach the VAERION non-PHI proof packet and transaction-family checklist.',
      'Enter payer IDs, billing entity TIN/NPI, submitter contact, endpoint, and certificate references.',
      'Save the portal case number, acceptance email, and file hashes as authority evidence.',
    ],
    submissionBoundary:
      'VAERION prepares this packet and can record returned evidence; the authorized customer or payer portal owner controls external submission.',
    downloadName: 'payer-trading-partner',
    nextStep: 'Send this packet to the payer EDI enrollment owner and record the payer-issued acceptance reference when returned.',
  },
  {
    id: 'epic-production-access',
    title: 'Epic/customer production access packet',
    counterparty: 'Epic app owner or customer EHR security team',
    purpose: 'Request production SMART/FHIR access for patient, coverage, encounter, and document workflows approved by the hospital.',
    authorityEvidenceType: 'ehr_production_access_approval',
    evidenceIssuer: 'Epic/customer EHR app owner',
    evidenceScope: 'Epic production SMART/FHIR access for VAERION billing integration',
    evidenceReference: 'epic-app-approval-or-customer-change-ticket',
    evidenceNeeded: [
      'Epic app approval or customer change-ticket reference.',
      'Production issuer/base URL and approved client ID reference.',
      'Approved scopes, launch mode, redirect URI, JWKS URL, and contact owner.',
      'Go-live window, rollback owner, and support escalation path.',
    ],
    vaerionPrefill: [
      'Epic public sandbox capability receipt.',
      'SMART/OIDC registration manifest, redirect URI, and public JWKS URL.',
      'Synthetic Patient, Coverage, Encounter, and DocumentReference mapping receipts.',
      'Secret handling proof: private keys and tokens are not returned to the browser.',
    ],
    externalFields: [
      'Hospital Epic tenant, production client ID, environment URL, approved scopes, and app owner.',
      'Security review outcome, go-live date, and customer approval signature or ticket.',
      'Any customer-specific FHIR profile, extension, or document-write policy.',
    ],
    proofReceipts: [
      'Epic CapabilityStatement receipt.',
      'Backend OAuth token proof receipt when sandbox credentials exist.',
      'FHIR mapping validation receipt.',
      'Authority evidence intake receipt after customer approval.',
    ],
    portalChecklist: [
      'Open Epic on FHIR app record or the customer EHR change-management portal.',
      'Attach VAERION sandbox proof and registration manifest.',
      'Confirm scopes, redirect URI, JWKS URL, backend-service eligibility, and app owner.',
      'Capture approval reference, production client ID reference, environment URL, and approved scopes.',
      'Record only references and hashes in VAERION; do not paste secrets or PHI.',
    ],
    submissionBoundary:
      'VAERION can prove sandbox and mapping readiness; Epic/customer production access is issued by Epic or the hospital customer.',
    downloadName: 'epic-production-access',
    nextStep: 'Send this packet to the Epic/customer app owner and record the approval ticket plus artifact hash when issued.',
  },
  {
    id: 'hospital-cutover',
    title: 'Hospital security and cutover packet',
    counterparty: 'Hospital executive sponsor, security, compliance, and implementation owner',
    purpose: 'Collect hospital authority for PHI-bearing production traffic, BAA boundary, SSO/MFA, role matrix, support, and go-live.',
    authorityEvidenceType: 'hospital_go_live_authorization',
    evidenceIssuer: 'Hospital implementation owner',
    evidenceScope: 'Hospital production cutover authorization for VAERION billing deployment',
    evidenceReference: 'hospital-go-live-ticket-or-security-review-id',
    evidenceNeeded: [
      'BAA/security review reference and production cutover approval.',
      'Facility roster, provider roster, billing entity/TIN/NPI ownership proof, and role matrix.',
      'SSO/OIDC/SAML metadata, MFA policy, access-review owner, and emergency access process.',
      'Incident contacts, runbook acceptance, support escalation, and rollback decision owner.',
    ],
    vaerionPrefill: [
      'Tenant isolation and audit proof surface summary.',
      'Role-aware console, HttpOnly session, proof export, and no-PHI packet behavior.',
      'Pilot script and launch dossier receipt set.',
      'Security boundary: hospital approval required before PHI production traffic.',
    ],
    externalFields: [
      'Hospital legal entity, security reviewer, compliance owner, implementation owner, and support contacts.',
      'Identity provider metadata, access-review cadence, facilities, providers, payer routes, and go-live date.',
      'Signed BAA or contract reference if applicable to the deployment.',
    ],
    proofReceipts: [
      'Enterprise identity proof receipt.',
      'Hospital onboarding proof receipt.',
      'Pilot script proof receipt.',
      'Authority evidence intake receipt after hospital approval.',
    ],
    portalChecklist: [
      'Open the hospital security review, procurement, or implementation ticket.',
      'Attach VAERION launch dossier, proof packet, incident runbook, role matrix, and tenant-isolation summary.',
      'Enter facility roster, provider roster, identity provider metadata, and cutover contacts.',
      'Capture approval reference, reviewer, effective date, and artifact hash.',
      'Record the approval reference in VAERION before any PHI-bearing cutover.',
    ],
    submissionBoundary:
      'VAERION prepares the cutover evidence set; the hospital authorizes production use, PHI access, and operational go-live.',
    downloadName: 'hospital-security-cutover',
    nextStep: 'Send this packet to the hospital implementation owner and record the go-live authorization evidence when approved.',
  },
  {
    id: 'financial-authority',
    title: 'Financial authority packet',
    counterparty: 'Bank, processor, lockbox, treasury, or customer finance owner',
    purpose: 'Request authority for ERA deposit reconciliation, EFT/ACH, refund, void, reversal, lockbox, and treasury workflows.',
    authorityEvidenceType: 'bank_processor_money_movement_authority',
    evidenceIssuer: 'Bank, processor, or treasury owner',
    evidenceScope: 'Financial production authority for VAERION payment posting and reconciliation workflows',
    evidenceReference: 'treasury-approval-or-processor-case-id',
    evidenceNeeded: [
      'Bank, processor, lockbox, or treasury authority reference.',
      'Approved payment functions: ERA posting, deposit match, EFT/ACH, refund, void, reversal, lockbox.',
      'Account/token references, settlement file channel, cutoff windows, and reconciliation owner.',
      'Dual-control, approval matrix, exception handling, and rollback procedure.',
    ],
    vaerionPrefill: [
      'Payment end-to-end proof receipt.',
      'Payment rail sandbox receipt.',
      'Duplicate remittance guard, deposit matching, refund/void/reversal dry-run proof.',
      'Boundary: no real funds movement until finance authority is recorded and approved.',
    ],
    externalFields: [
      'Treasury owner, bank/processor implementation owner, approved functions, account references, and settlement channel.',
      'EFT/ERA enrollment references, lockbox references, NACHA/processor test receipts, and approval matrix.',
      'Customer finance acceptance and effective cutover date.',
    ],
    proofReceipts: [
      'Payment proof receipt.',
      'Payment rail sandbox receipt.',
      'De-identified payment replay receipt.',
      'Authority evidence intake receipt after financial approval.',
    ],
    portalChecklist: [
      'Open the processor, bank, lockbox, treasury, or customer finance approval workflow.',
      'Attach VAERION payment proof, rail sandbox proof, duplicate guard proof, and reconciliation controls.',
      'Enter approved functions, account/token references, settlement channel, cutoff windows, and approval owners.',
      'Capture approval reference, effective date, and artifact hash.',
      'Record only references and hashes; do not paste raw account numbers or banking secrets.',
    ],
    submissionBoundary:
      'VAERION can reconcile, post, and prove payment workflows; the bank, processor, or customer treasury owner controls money-movement authority.',
    downloadName: 'financial-authority',
    nextStep: 'Send this packet to the finance authority owner and record the authority reference before enabling money-movement workflows.',
  },
];

const authorityAcquisitionTracks: AuthorityAcquisitionTrack[] = [
  {
    id: 'medicare-direct-edi',
    title: 'Direct payer trading partner authority',
    counterparty: 'Medicare MAC or payer EDI enrollment team',
    portalLabel: 'CMS Medicare EDI enrollment',
    portalUrl: 'https://www.cms.gov/medicare/coding-billing/electronic-billing/how-to-enroll-medicare-electronic-data-interchange',
    sourceLabel: 'CMS Medicare EDI enrollment guidance',
    sourceUrl: 'https://www.cms.gov/medicare/coding-billing/electronic-billing/how-to-enroll-medicare-electronic-data-interchange',
    packetId: 'payer-trading-partner',
    authorityEvidenceType: 'payer_trading_partner_acceptance',
    evidenceIssuer: 'Payer EDI enrollment team',
    evidenceScope: 'Direct 837P/837I/999/277CA/835 route authority for the partner billing tenant',
    referencePlaceholder: 'trading-partner-id-or-payer-case-reference',
    vaerionCanDoNow: [
      'Generate payer-ready packet with X12 route proof, acknowledgement lifecycle, callback, retry, and DLQ evidence.',
      'Validate companion-guide text into a payer route profile and attach the proof receipt.',
      'Produce non-PHI 837, 999, 277CA, and 835 lifecycle evidence for payer testing review.',
    ],
    counterpartyMustReturn: [
      'Trading partner or submitter ID for the exact payer route.',
      'Accepted transaction families, payer IDs, endpoint or mailbox authority, and effective date.',
      'Certification or enrollment approval reference from the payer-controlled process.',
    ],
    proofToRunFirst: 'Run payer-route proof and attach the generated receipt before opening the payer enrollment case.',
    proofAction: 'payer',
  },
  {
    id: 'epic-production-access',
    title: 'Epic/customer production FHIR authority',
    counterparty: 'Epic app owner or customer EHR security team',
    portalLabel: 'Epic developer resources',
    portalUrl: 'https://open.epic.com/DeveloperResources',
    sourceLabel: 'Epic open developer resources',
    sourceUrl: 'https://open.epic.com/DeveloperResources',
    packetId: 'epic-production-access',
    authorityEvidenceType: 'ehr_production_access_approval',
    evidenceIssuer: 'Epic/customer EHR app owner',
    evidenceScope: 'Epic production SMART/FHIR access for VAERION billing integration',
    referencePlaceholder: 'epic-app-id-or-customer-change-ticket',
    vaerionCanDoNow: [
      'Run public Epic sandbox CapabilityStatement and SMART/OIDC discovery.',
      'Generate the Epic registration manifest, redirect/JWKS references, and mapping proof for Patient, Coverage, Encounter, and DocumentReference.',
      'Attach sandbox token and mapping receipts without exposing private keys or PHI.',
    ],
    counterpartyMustReturn: [
      'Production app approval or hospital change-ticket reference.',
      'Approved client ID reference, production issuer/base URL, scopes, redirect URI, JWKS URL, and launch mode.',
      'Customer app owner, go-live window, rollback owner, and support escalation path.',
    ],
    proofToRunFirst: 'Run Epic/FHIR sandbox proof and attach the registration manifest before requesting production access.',
    proofAction: 'ehr',
  },
  {
    id: 'financial-authority',
    title: 'Treasury and money-movement authority',
    counterparty: 'Bank, ODFI, processor, lockbox, or customer treasury owner',
    portalLabel: 'Nacha third-party ACH guidance',
    portalUrl: 'https://www.nacha.org/content/third-parties-ach-network',
    sourceLabel: 'Nacha ACH third-party guidance',
    sourceUrl: 'https://www.nacha.org/content/third-parties-ach-network',
    packetId: 'financial-authority',
    authorityEvidenceType: 'bank_processor_money_movement_authority',
    evidenceIssuer: 'Bank, processor, or treasury owner',
    evidenceScope: 'Financial production authority for VAERION payment posting and reconciliation workflows',
    referencePlaceholder: 'treasury-approval-or-processor-case-id',
    vaerionCanDoNow: [
      'Run 835 posting, deposit reconciliation, duplicate-remittance, refund, void, reversal, and replay proof.',
      'Generate a finance-ready packet that explains controls without including bank secrets or account numbers.',
      'Attach payment rail sandbox proof and dual-control evidence for treasury review.',
    ],
    counterpartyMustReturn: [
      'Approved payment functions and account/token references.',
      'Settlement file channel, cutoff windows, lockbox or EFT/ACH references, and reconciliation owner.',
      'Approval matrix, effective date, exception handling, and rollback procedure.',
    ],
    proofToRunFirst: 'Run payment rail sandbox and payment proof before requesting treasury or processor authority.',
    proofAction: 'payment',
  },
  {
    id: 'hospital-cutover',
    title: 'Hospital production cutover authority',
    counterparty: 'Hospital executive sponsor, security, compliance, and implementation owner',
    portalLabel: 'Prepare hospital cutover packet',
    portalUrl: 'https://billing.vaerionsystems.com/#approvals',
    sourceLabel: 'VAERION approval-packet workspace',
    sourceUrl: 'https://billing.vaerionsystems.com/#approvals',
    packetId: 'hospital-cutover',
    authorityEvidenceType: 'hospital_go_live_authorization',
    evidenceIssuer: 'Hospital implementation owner',
    evidenceScope: 'Hospital production cutover authorization for VAERION billing deployment',
    referencePlaceholder: 'hospital-go-live-ticket-or-security-review-id',
    vaerionCanDoNow: [
      'Generate tenant isolation, RBAC, incident runbook, pilot script, and audit proof receipts.',
      'Prepare facility, provider, payer-route, role, BAA, and security-review evidence checklists.',
      'Package a non-PHI launch dossier for security and implementation review.',
    ],
    counterpartyMustReturn: [
      'BAA/security review reference and production cutover approval.',
      'Facility roster, provider roster, billing entity/TIN/NPI ownership proof, and role matrix.',
      'SSO/MFA/SCIM metadata, support contacts, go-live date, and rollback owner.',
    ],
    proofToRunFirst: 'Run hospital onboarding proof, identity proof, and pilot script proof before requesting cutover authority.',
    proofAction: 'hospital',
  },
];

const enterpriseDeploymentSurfaces = [
  {
    id: 'identity-security',
    title: 'Enterprise identity and access',
    section: 'developer' as SectionId,
    cta: 'Run identity proof',
    summary: 'Credential gates, session controls, access-review slots, SSO/MFA/SCIM evidence, and tenant identity tasks.',
  },
  {
    id: 'hospital-onboarding',
    title: 'Hospital onboarding dossier',
    section: 'onboarding' as SectionId,
    cta: 'Run onboarding proof',
    summary: 'Tenant, facility, provider, payer, role, BAA, security review, and go-live owner proof surface.',
  },
  {
    id: 'direct-payer-packet',
    title: 'Direct payer packet factory',
    section: 'payer' as SectionId,
    cta: 'Run payer packet proof',
    summary: 'Companion guides, 837 packet factory, 999/277CA receipts, 835 route lifecycle, and production route gates.',
  },
  {
    id: 'eligibility-auth',
    title: 'Eligibility and prior authorization',
    section: 'claims' as SectionId,
    cta: 'Run eligibility/auth proof',
    summary: '270/271 shape, benefit facts, prior-auth evidence, packet drafting, and payer filing authority separation.',
  },
  {
    id: 'claim-correctness',
    title: 'Claim correctness gauntlet',
    section: 'claims' as SectionId,
    cta: 'Run claim gauntlet proof',
    summary: 'Required facts, coding checks, NCCI/OIG guardrails, route separation, and denial-prevention proof.',
  },
  {
    id: 'deidentified-payment-replay',
    title: 'De-identified payment replay',
    section: 'payments' as SectionId,
    cta: 'Run de-identified replay',
    summary: 'ERA replay, EOB fixtures, deposit matching, refund/void/reversal dry-runs, and accounting controls.',
  },
  {
    id: 'denial-appeal',
    title: 'Denial and appeal packet proof',
    section: 'denials' as SectionId,
    cta: 'Run denial/appeal proof',
    summary: 'Correction-ready classification, appeal packet drafting, deadline proof, and payer-learning loop.',
  },
  {
    id: 'ops-incident',
    title: 'Operations and incident center',
    section: 'audit' as SectionId,
    cta: 'Run ops proof',
    summary: 'Audit exports, runbooks, incident states, status updates, and live-action guardrail proof.',
  },
  {
    id: 'developer-handoff',
    title: 'Developer handoff and API conformance',
    section: 'developer' as SectionId,
    cta: 'Run developer proof',
    summary: 'API contracts, webhook replay, schema proof, request headers, and no-secret handoff receipts.',
  },
  {
    id: 'pilot-script',
    title: 'Full enterprise pilot script',
    section: 'launch' as SectionId,
    cta: 'Run full pilot proof',
    summary: 'Pre-visit, eligibility, auth, claim, payer test, ERA, posting, denial, appeal, and audit loop.',
  },
];

const enterpriseCapabilityMap = [
  {
    area: 'Enterprise security',
    section: 'developer' as SectionId,
    state: 'Portal protected',
    capability: 'Credential-gated access, HttpOnly session cookie, role-aware launch workspaces, audit-safe proof exports.',
    gap: 'MFA, SSO/OIDC/SAML, SCIM, access reviews, and customer identity-provider production setup still need tenant configuration.',
    action: 'Open Developer Integration and record tenant identity proof.',
  },
  {
    area: 'Hospital onboarding',
    section: 'onboarding' as SectionId,
    state: 'Guided setup',
    capability: 'Tenant, facility, provider, payer, role, webhook, and proof packet setup are modeled in the console.',
    gap: 'Real facility roster, provider enrollment evidence, TIN/NPI ownership proof, and BAA/security signoff must be recorded per hospital.',
    action: 'Run Partner Onboarding and attach roster proof.',
  },
  {
    area: 'Direct payer network',
    section: 'payer' as SectionId,
    state: 'Infrastructure ready, authority gated',
    capability: 'Carrier matrix, direct route registry, companion-guide slots, X12 packet proof, 999/277CA/835 lifecycle proof.',
    gap: 'No live payer route can send until each payer issues trading partner approval and production endpoint authority.',
    action: 'Open Carrier Matrix and run payer-route proof.',
  },
  {
    area: 'EHR / FHIR',
    section: 'ehr' as SectionId,
    state: 'Public sandbox runner available',
    capability: 'Epic public FHIR R4 CapabilityStatement and SMART/OIDC discovery receipt, mapping receipts, synthetic replay path.',
    gap: 'Production Epic/customer credentials and hospital-approved PHI access are still external authority gates.',
    action: 'Run Epic public sandbox proof and attach credential proof when issued.',
  },
  {
    area: 'Eligibility and benefits',
    section: 'claims' as SectionId,
    state: 'Readiness workflow modeled',
    capability: 'Eligibility should normalize 270/271-style coverage facts, copay, deductible, plan, group, and patient responsibility before claim creation.',
    gap: 'Live payer eligibility requires direct payer route authority; internal benefit simulation and response-shape validation are represented for implementation review.',
    action: 'Run eligibility readiness proof.',
  },
  {
    area: 'Prior authorization',
    section: 'payer' as SectionId,
    state: 'Packet workflow modeled',
    capability: 'Auth requirement evidence, packet generation, payer policy proof, clinical attachment checklist, and approval-gated submission should be first-class.',
    gap: 'External prior-auth filing requires payer/customer authority and route credentials; packet drafting is represented for implementation review.',
    action: 'Open Carrier Matrix and run payer-route proof.',
  },
  {
    area: 'Claim correctness',
    section: 'claims' as SectionId,
    state: 'Pre-production validation',
    capability: 'Claim intake sandbox, required-fact checks, NCCI/OIG guardrails, payer-route certification separation.',
    gap: 'Production claim submission remains authority-gated until direct payer route certification exists.',
    action: 'Run Claim Intake validation.',
  },
  {
    area: 'Payments and ERA',
    section: 'payments' as SectionId,
    state: 'Posting/reconciliation simulated',
    capability: '835 parsing model, duplicate-remittance guard, deposit matching, refund/void/reversal dry-run proof.',
    gap: 'Bank, EFT, ACH, lockbox, or processor authority is required for real money movement.',
    action: 'Run Payment simulation and record finance authority proof.',
  },
  {
    area: 'Denials and appeals',
    section: 'denials' as SectionId,
    state: 'Packet draftable',
    capability: 'Correction-ready classification, appeal packet generation, payer learning loop, approval-gated external filing.',
    gap: 'No external appeal is filed until authorized by the customer and payer route requirements.',
    action: 'Run Denial packet workflow.',
  },
  {
    area: 'Operations and audit',
    section: 'audit' as SectionId,
    state: 'Proof vault available',
    capability: 'Plain-English proof receipts, implementation packet export, executive PDF export, formal review lifecycle, server-side proof vault, action ledger preview, and live-action guardrails.',
    gap: 'Customer compliance acceptance remains counterparty authority; VAERION can generate PDF proof and record review lifecycle receipts now.',
    action: 'Generate, save, and download partner proof packet.',
  },
  {
    area: 'Developer handoff',
    section: 'developer' as SectionId,
    state: 'Surface documented',
    capability: 'API surfaces, sandbox key-reference rotation, webhook replay, DLQ proof, request headers, and proof packet handoff are visible to partner implementation teams.',
    gap: 'Production API keys still require tenant launch approval; sandbox key and webhook proof receipts are runnable now without exposing secrets.',
    action: 'Open Developer Integration and record tenant identity proof.',
  },
];

function readStoredProofPacket() {
  try {
    const raw = localStorage.getItem(proofPacketStorageKey);
    if (!raw) return null;
    const packet = JSON.parse(raw) as Partial<PartnerProofPacket>;
    if (!packet.proof_standard || !packet.evidence_dossier || !packet.server_vault) {
      localStorage.removeItem(proofPacketStorageKey);
      return null;
    }
    return packet as PartnerProofPacket;
  } catch {
    localStorage.removeItem(proofPacketStorageKey);
    return null;
  }
}

function hasModernProofPacketSchema(packet?: Partial<PartnerProofPacket> | null) {
  return Boolean(packet?.proof_standard && packet.evidence_dossier && packet.server_vault && packet.partner_review_checklist);
}

function readableAuthorityState(value?: string) {
  if (!value) return 'Counterparty authority evidence required before live production action.';
  const normalized = value.replace(/_/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  const knownStates: Record<string, string> = {
    'blocked until authority proof':
      'Live external action remains gated until counterparty authority evidence is accepted.',
    'authority gated until authority proof':
      'Live external action remains gated until counterparty authority evidence is accepted.',
    'authority gated until external authority':
      'Production cutover awaits payer, EHR, hospital, and financial authority evidence.',
    'external authority required':
      'Counterparty authority evidence required before live production action.',
    'vaerion owned ready':
      'VAERION-owned readiness evidence is complete for partner review.',
    'ready for partner review':
      'Ready for partner technical and executive review.',
  };

  if (knownStates[normalized]) return knownStates[normalized];

  return value
    .replace(/_/g, ' ')
    .replace(/\bExternal authority still required\b/gi, 'Counterparty authority evidence required')
    .replace(/\bExternal authority\b/gi, 'Counterparty authority')
    .replace(/\bauthority-gated until\b/gi, 'awaiting')
    .replace(/\bblocked\b/gi, 'authority gated')
    .replace(/\bmissing\b/gi, 'awaiting evidence')
    .replace(/\bproof\b/gi, 'evidence')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatList(value?: string[] | string, fallback = 'Run proof to record receipt evidence.') {
  if (Array.isArray(value)) {
    return value.length ? value.join('; ') : fallback;
  }
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  return fallback;
}

function dedupeEnterpriseReceipts(
  browserReceipts: EnterpriseSurfaceReceipt[],
  dossierReceipts: LaunchDossier['enterprise_surface_receipts'] = [],
) {
  const receiptMap = new Map<
    string,
    {
      surface_id: string;
      title: string;
      receipt_id: string;
      verdict: string;
      proves: string;
      does_not_prove: string;
      remaining_external_authority: string[];
      created_at?: string;
      source: string;
      live_external_action?: string;
    }
  >();

  for (const receipt of browserReceipts) {
    receiptMap.set(receipt.receipt_id, {
      surface_id: receipt.surface_id,
      title: receipt.title,
      receipt_id: receipt.receipt_id,
      verdict: receipt.plain_english_verdict,
      proves: receipt.proves,
      does_not_prove: receipt.does_not_prove,
      remaining_external_authority: receipt.remaining_external_authority,
      created_at: receipt.created_at,
      source: 'Browser proof runner',
      live_external_action: receipt.live_external_action,
    });
  }

  for (const receipt of dossierReceipts) {
    receiptMap.set(receipt.receipt_id, {
      surface_id: receipt.surface_id,
      title: receipt.title,
      receipt_id: receipt.receipt_id,
      verdict: receipt.verdict,
      proves: `${receipt.title} receipt exists in the server-side launch dossier for tenant ${receipt.actor_id}.`,
      does_not_prove: 'This server receipt does not replace payer, Epic/customer, hospital, or financial-institution authority.',
      remaining_external_authority: receipt.remaining_external_authority,
      created_at: receipt.created_at,
      source: 'AWS proof vault launch dossier',
      live_external_action: receipt.live_external_action,
    });
  }

  return Array.from(receiptMap.values()).sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
}

function buildEvidenceDossier(args: {
  packetId: string;
  generatedAt: string;
  launchDossier: LaunchDossier | null;
  enterpriseReadiness: EnterpriseReadiness | null;
  enterpriseReceipts: ReturnType<typeof dedupeEnterpriseReceipts>;
  paymentProofReceipt?: PaymentProofReceipt | null;
  paymentRailSandboxReceipt?: PaymentRailSandboxReceipt | null;
  ehrSandboxReceipt?: EpicSandboxReceipt | null;
  ehrSandboxTokenReceipt?: EpicSandboxTokenReceipt | null;
  developerOpsReceipt?: DeveloperOpsReceipt | null;
  companionGuideReceipt?: CompanionGuideReceipt | null;
  replayImportReceipt?: ReplayImportReceipt | null;
  proofReviewReceipt?: ProofReviewReceipt | null;
  enterpriseProofVerifier?: EnterpriseProofVerifier | null;
  authorityEvidenceReceipts?: AuthorityEvidenceReceipt[];
  savedPackets: SavedProofPacketSummary[];
}) {
  const {
    packetId,
    generatedAt,
    launchDossier,
    enterpriseReadiness,
    enterpriseReceipts,
    paymentProofReceipt,
    paymentRailSandboxReceipt,
    ehrSandboxReceipt,
    ehrSandboxTokenReceipt,
    developerOpsReceipt,
    companionGuideReceipt,
    replayImportReceipt,
    proofReviewReceipt,
    enterpriseProofVerifier,
    authorityEvidenceReceipts = [],
    savedPackets,
  } = args;

  const dossier: EvidenceDossierItem[] = [
    {
      surface: 'Partner proof packet',
      evidence_type: 'Generated artifact',
      source: 'Partner billing console',
      receipt_id: packetId,
      recorded_at: generatedAt,
      method: 'Browser generated a non-PHI launch packet and opened the packet viewer.',
      result: 'Packet contains launch gates, workstreams, route matrix, implementation API, proof events, and authority boundaries.',
      artifact: `vaerion-partner-proof-packet-${packetId}.json`,
      proves: 'VAERION can package the implementation evidence into a partner-review artifact with stable identifiers.',
      does_not_prove: 'A generated packet alone is not payer, Epic, hospital, or banking approval.',
      authority_limit: 'Production traffic remains counterparty-authority gated.',
    },
  ];

  if (launchDossier) {
    dossier.push({
      surface: 'Server launch dossier',
      evidence_type: 'AWS proof-vault index',
      source: 'billing-api.vaerionsystems.com / launch-dossier',
      receipt_id: `launch-dossier-${launchDossier.generated_at}`,
      recorded_at: launchDossier.generated_at,
      method: 'Billing API read the server proof vault and returned proof packet and enterprise receipt counts.',
      result: `${launchDossier.readiness.proven_surface_count} of ${launchDossier.readiness.required_surface_count} VAERION-owned surfaces recorded; ${launchDossier.readiness.proof_packet_count} server packet(s).`,
      artifact: 'GET /api/billing/launch-dossier response',
      proves: launchDossier.verdict,
      does_not_prove: 'Server-side receipt counts do not grant external production authority.',
      authority_limit: readableAuthorityState(launchDossier.live_external_action),
    });
  }

  if (enterpriseReadiness) {
    dossier.push({
      surface: 'Enterprise readiness contract',
      evidence_type: 'Machine-readable deployment truth table',
      source: 'billing-api.vaerionsystems.com / enterprise-readiness',
      receipt_id: `enterprise-readiness-${enterpriseReadiness.generated_at}`,
      recorded_at: enterpriseReadiness.generated_at,
      method:
        'Billing API returned deployment modes, evidence surfaces, proof endpoints, live-production requirements, and non-negotiable guardrails from the AWS billing runtime.',
      result: `${enterpriseReadiness.status.vaerion_owned_surface_count} of ${enterpriseReadiness.status.required_vaerion_owned_surface_count} enterprise surfaces have server receipts; live cutover is ${readableAuthorityState(
        enterpriseReadiness.status.live_production_cutover,
      )}.`,
      artifact: 'GET /api/billing/enterprise-readiness response',
      proves:
        'VAERION can state exactly which deployment modes and evidence surfaces are provable now, and exactly which live-production authorities remain outside VAERION-controlled software.',
      does_not_prove: 'This readiness contract does not replace payer, Epic/customer, hospital, or financial-institution production authority.',
      authority_limit: enterpriseReadiness.live_production_requirements.map((item) => item.requirement).join('; '),
    });
  }

  if (enterpriseProofVerifier) {
    dossier.push({
      surface: 'Enterprise Proof Verifier',
      evidence_type: 'Executive inspection surface',
      source: 'billing-api.vaerionsystems.com / enterprise-proof-verifier',
      receipt_id: `enterprise-proof-verifier-${enterpriseProofVerifier.generated_at}`,
      recorded_at: enterpriseProofVerifier.generated_at,
      method:
        'Billing API joined proof packets, enterprise deployment receipts, authority evidence references, and review boundaries into one verifier.',
      result: `${enterpriseProofVerifier.proof_domains.length} proof domains inspectable; production authority is ${readableAuthorityState(
        enterpriseProofVerifier.verdict.production_authority,
      )}.`,
      artifact: 'GET /api/billing/enterprise-proof-verifier response',
      proves: enterpriseProofVerifier.verdict.plain_english,
      does_not_prove:
        'The verifier is evidence of VAERION-owned readiness and evidence intake; it does not self-issue payer, Epic, hospital, or financial authority.',
      authority_limit: enterpriseProofVerifier.boundary,
    });

    if (enterpriseProofVerifier.production_activation_tracks?.length) {
      const acceptedTracks = enterpriseProofVerifier.production_activation_tracks.filter(
        (track) => track.current_state === 'eligible_for_cutover_review',
      ).length;
      dossier.push({
        surface: 'Production activation workflow',
        evidence_type: 'Cutover workflow control plane',
        source: 'billing-api.vaerionsystems.com / enterprise-proof-verifier',
        receipt_id: `production-activation-${enterpriseProofVerifier.generated_at}`,
        recorded_at: enterpriseProofVerifier.generated_at,
        method:
          'Billing API returned the five-step activation workflow for EHR, payer submission, payment rails, and hospital cutover.',
        result: `${enterpriseProofVerifier.production_activation_tracks.length} activation tracks inspectable; ${acceptedTracks} track(s) have accepted authority evidence and are eligible for final cutover review.`,
        artifact: 'enterprise-proof-verifier.production_activation_tracks',
        proves:
          'VAERION can show the exact operational path from VAERION-owned preparation through counterparty evidence receipt, review, and final cutover review.',
        does_not_prove:
          'A workflow track does not replace payer, Epic/customer, hospital, bank, processor, or treasury authority.',
        authority_limit:
          'Live activation remains limited to the exact authority evidence accepted for each production track.',
      });
    }
  }

  for (const receipt of authorityEvidenceReceipts) {
    dossier.push({
      surface: `Authority evidence: ${receipt.evidence_type}`,
      evidence_type: 'Counterparty authority evidence reference',
      source: receipt.issuer,
      receipt_id: receipt.receipt_id,
      recorded_at: generatedAt,
      method:
        'VAERION recorded a non-PHI artifact reference and SHA-256 hash for counterparty authority evidence. The raw document is held outside the browser proof packet.',
      result: `${receipt.lifecycle_state}; final cutover review eligible: ${receipt.final_cutover_review_eligible ? 'yes' : 'no'}; production auto-activation: no; raw PHI stored: ${receipt.raw_phi_stored ? 'yes' : 'no'}.`,
      artifact: `${receipt.artifact_reference} · ${receipt.artifact_sha256}`,
      proves:
        'The system can accept authority evidence references, keep PHI/secrets out of the packet, and require review before any live production action is considered.',
      does_not_prove:
        'A recorded authority reference does not unlock production until Compliance/Admin review accepts it and the specific route/system/rail is enabled.',
      authority_limit: receipt.next_step,
    });
  }

  for (const receipt of enterpriseReceipts) {
    dossier.push({
      surface: receipt.title,
      evidence_type: 'Enterprise deployment surface receipt',
      source: receipt.source,
      receipt_id: receipt.receipt_id,
      recorded_at: receipt.created_at ?? generatedAt,
      method: 'Non-PHI proof runner executed the implementation surface and stored a receipt.',
      result: receipt.verdict,
      artifact: receipt.surface_id,
      proves: receipt.proves,
      does_not_prove: receipt.does_not_prove,
      authority_limit: receipt.remaining_external_authority.join('; ') || readableAuthorityState(receipt.live_external_action),
    });
  }

  if (ehrSandboxReceipt) {
    dossier.push({
      surface: 'Epic/FHIR conformance lab',
      evidence_type: 'Public sandbox discovery receipt',
      source: ehrSandboxReceipt.capability_statement.endpoint,
      receipt_id: ehrSandboxReceipt.receipt_id,
      recorded_at: ehrSandboxReceipt.created_at,
      method:
        'Fetched Epic public FHIR metadata, tested SMART/OIDC discovery fallbacks, summarized required billing resources, validated synthetic R4 mappings, and evaluated backend OAuth configuration references.',
      result: `FHIR ${ehrSandboxReceipt.capability_statement.fhir_version}; ${
        ehrSandboxReceipt.capability_statement.advertised_required_resource_count ?? 0
      }/${ehrSandboxReceipt.capability_statement.required_resource_count ?? 4} required billing resources advertised; backend OAuth ${readableAuthorityState(
        ehrSandboxReceipt.backend_oauth_readiness?.status,
      )}.`,
      artifact: ehrSandboxReceipt.smart_configuration.endpoint,
      proves: ehrSandboxReceipt.proves,
      does_not_prove: ehrSandboxReceipt.does_not_prove,
      authority_limit: readableAuthorityState(ehrSandboxReceipt.live_external_action),
    });
  }

  if (ehrSandboxTokenReceipt) {
    dossier.push({
      surface: 'Epic backend OAuth token proof',
      evidence_type: 'Authenticated sandbox token receipt',
      source: ehrSandboxTokenReceipt.smart_configuration.token_endpoint,
      receipt_id: ehrSandboxTokenReceipt.receipt_id,
      recorded_at: ehrSandboxTokenReceipt.created_at,
      method:
        'Loaded the Epic private-key reference server-side, signed a backend OAuth client assertion, exchanged it at the Epic sandbox token endpoint, discarded the raw token, and stored only sanitized receipt data.',
      result: `${readableAuthorityState(ehrSandboxTokenReceipt.token_exchange.status)}; access token received: ${
        ehrSandboxTokenReceipt.token_exchange.access_token_received ? 'yes' : 'no'
      }; raw token stored: no; raw assertion stored: no.`,
      artifact: `kid ${ehrSandboxTokenReceipt.client_assertion.kid}; ${ehrSandboxTokenReceipt.public_jwks_url}`,
      proves: ehrSandboxTokenReceipt.proves,
      does_not_prove: ehrSandboxTokenReceipt.does_not_prove,
      authority_limit: readableAuthorityState(ehrSandboxTokenReceipt.live_external_action),
    });
  }

  if (paymentProofReceipt) {
    dossier.push({
      surface: 'End-to-end payment proof',
      evidence_type: 'Synthetic payment lifecycle receipt',
      source: 'VAERION payment proof lab',
      receipt_id: paymentProofReceipt.receipt_id,
      recorded_at: paymentProofReceipt.created_at,
      method: 'Ran synthetic 837 validation, payer acknowledgement, 835 ERA posting, deposit reconciliation, duplicate guard, refund/void/reversal controls.',
      result: `${paymentProofReceipt.claim_validation.status}; ${paymentProofReceipt.era.posting_status}; ${paymentProofReceipt.deposit_reconciliation.status}.`,
      artifact: paymentProofReceipt.deposit_reconciliation.artifact,
      proves: paymentProofReceipt.proves,
      does_not_prove: paymentProofReceipt.does_not_prove,
      authority_limit: readableAuthorityState(paymentProofReceipt.live_external_action),
    });
  }

  if (paymentRailSandboxReceipt) {
    dossier.push({
      surface: 'Payment rail sandbox',
      evidence_type: 'Bank-style non-money-movement receipt',
      source: 'VAERION payment rail sandbox',
      receipt_id: paymentRailSandboxReceipt.receipt_id,
      recorded_at: paymentRailSandboxReceipt.created_at,
      method: 'Validated NACHA-style controls, bank response emulator, deposit ledger, replay vault, and accounting approvals.',
      result: `${paymentRailSandboxReceipt.nacha_file_validation.status}; ${paymentRailSandboxReceipt.bank_response_emulator.status}; ${paymentRailSandboxReceipt.deposit_ledger.status}.`,
      artifact: paymentRailSandboxReceipt.replay_vault.artifact,
      proves: paymentRailSandboxReceipt.proves,
      does_not_prove: paymentRailSandboxReceipt.does_not_prove,
      authority_limit: readableAuthorityState(paymentRailSandboxReceipt.live_external_action),
    });
  }

  if (developerOpsReceipt) {
    dossier.push({
      surface: developerOpsReceipt.key_reference ? 'Developer API key lifecycle' : 'Webhook replay and DLQ proof',
      evidence_type: developerOpsReceipt.key_reference ? 'API key rotation receipt' : 'Webhook replay receipt',
      source: 'VAERION developer operations proof',
      receipt_id: developerOpsReceipt.receipt_id,
      recorded_at: developerOpsReceipt.created_at,
      method: developerOpsReceipt.key_reference
        ? 'Rotated a sandbox API key reference without returning secret material.'
        : 'Replayed a non-PHI webhook event with signing-reference and DLQ receipt.',
      result: developerOpsReceipt.key_reference
        ? `${developerOpsReceipt.key_reference}; scopes ${(developerOpsReceipt.allowed_scopes ?? []).join(', ')}; secret returned: no.`
        : `${developerOpsReceipt.event_type}; ${readableAuthorityState(developerOpsReceipt.delivery?.status)}; ${readableAuthorityState(developerOpsReceipt.dlq?.status)}.`,
      artifact: developerOpsReceipt.key_reference ?? developerOpsReceipt.delivery?.idempotency_key ?? developerOpsReceipt.receipt_id,
      proves: developerOpsReceipt.proves,
      does_not_prove: developerOpsReceipt.does_not_prove,
      authority_limit: readableAuthorityState(developerOpsReceipt.live_external_action),
    });
  }

  if (companionGuideReceipt) {
    dossier.push({
      surface: 'Companion-guide X12 profile',
      evidence_type: 'Payer-specific validation profile',
      source: 'VAERION companion-guide parser',
      receipt_id: companionGuideReceipt.receipt_id,
      recorded_at: companionGuideReceipt.created_at,
      method: 'Parsed payer companion-guide text into X12 transaction, acknowledgement, identifier, and envelope rules.',
      result: `${companionGuideReceipt.profile.payer_name} ${companionGuideReceipt.profile.x12_guide}; transactions ${companionGuideReceipt.profile.transaction_sets.join(', ')}.`,
      artifact: companionGuideReceipt.profile.payer_id,
      proves: companionGuideReceipt.proves,
      does_not_prove: companionGuideReceipt.does_not_prove,
      authority_limit: readableAuthorityState(companionGuideReceipt.live_external_action),
    });
  }

  if (replayImportReceipt) {
    dossier.push({
      surface: 'De-identified customer replay import',
      evidence_type: 'Replay manifest receipt',
      source: 'VAERION replay vault importer',
      receipt_id: replayImportReceipt.receipt_id,
      recorded_at: replayImportReceipt.created_at,
      method: 'Accepted a de-identified export manifest with artifact hashes and record counts; raw PHI was not stored.',
      result: `${replayImportReceipt.fixture_inventory.total_artifacts} artifact(s), ${replayImportReceipt.fixture_inventory.total_records} record(s), ${readableAuthorityState(
        replayImportReceipt.replay_readiness.status,
      )}.`,
      artifact: replayImportReceipt.manifest_id,
      proves: replayImportReceipt.proves,
      does_not_prove: replayImportReceipt.does_not_prove,
      authority_limit: 'Replay proof does not authorize live production traffic or money movement.',
    });
  }

  if (proofReviewReceipt) {
    dossier.push({
      surface: 'Proof review lifecycle',
      evidence_type: 'Compliance review decision',
      source: 'VAERION proof review workflow',
      receipt_id: proofReviewReceipt.receipt_id,
      recorded_at: proofReviewReceipt.reviewed_at,
      method: `Reviewer ${proofReviewReceipt.reviewer} marked ${proofReviewReceipt.object_type} ${proofReviewReceipt.object_id} as ${proofReviewReceipt.lifecycle_state}.`,
      result: `Lifecycle state ${proofReviewReceipt.lifecycle_state}; external authority unlocked: no.`,
      artifact: proofReviewReceipt.object_id,
      proves: proofReviewReceipt.proves,
      does_not_prove: proofReviewReceipt.does_not_prove,
      authority_limit: 'Internal proof review does not replace counterparty production authority.',
    });
  }

  for (const savedPacket of savedPackets.slice(0, 5)) {
    dossier.push({
      surface: 'Saved server proof packet',
      evidence_type: 'Proof vault record',
      source: savedPacket.download_path,
      receipt_id: savedPacket.packet_id,
      recorded_at: savedPacket.created_at,
      method: 'Billing API proof-vault index returned a stored packet reference.',
      result: savedPacket.summary,
      artifact: savedPacket.download_path,
      proves: 'The non-PHI packet has a server-side record outside browser local storage.',
      does_not_prove: 'Stored packet presence is not external production authority.',
      authority_limit: readableAuthorityState(savedPacket.live_external_action),
    });
  }

  return dossier;
}

function CarrierCertificationMatrix({ onCutoverReview }: { onCutoverReview: (carrier: string, blockers: string[]) => void }) {
  const [query, setQuery] = useState('');
  const [selectedCarrierId, setSelectedCarrierId] = useState(carrierCertificationMatrix[0]?.id ?? '');
  const [routeReceipt, setRouteReceipt] = useState('No payer-route proof has been generated in this browser session.');

  const filteredCarriers = carrierCertificationMatrix.filter((carrier) => {
    const haystack = [
      carrier.carrier,
      carrier.market,
      carrier.routeType,
      carrier.routeStatus,
      carrier.transactions.join(' '),
      carrier.productionGate,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });
  const selectedCarrier =
    filteredCarriers.find((carrier) => carrier.id === selectedCarrierId) ??
    filteredCarriers[0] ??
    carrierCertificationMatrix[0];

  async function runPayerRouteProof() {
    setRouteReceipt(`Running non-PHI payer-route proof for ${selectedCarrier.carrier}...`);
    try {
      const response = await billingFetch(`/authority-workarounds/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workaround_id: 'payer-direct',
          mode: 'synthetic_non_phi',
          carrier_route: {
            carrier_id: selectedCarrier.id,
            carrier_name: selectedCarrier.carrier,
            transaction_scope: selectedCarrier.transactions,
            production_gate: selectedCarrier.productionGate,
          },
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        receipt_id?: string;
        status?: string;
        next_step?: string;
        error?: string;
      };
      if (!response.ok) {
        setRouteReceipt(payload.error ?? 'Route proof could not be completed. Keep production transmission authority-gated.');
        return;
      }
      setRouteReceipt(
        `Internal payer-route proof recorded: ${payload.receipt_id}. ${payload.next_step ?? 'Carrier-issued acceptance is still required before production send.'} Production remains authority-gated until carrier acceptance is recorded.`,
      );
    } catch {
      setRouteReceipt('Route proof could not reach the billing edge. Keep production transmission authority-gated.');
    }
  }

  function generateInternalPacket() {
    setRouteReceipt(
      `${selectedCarrier.carrier}: internal ${selectedCarrier.transactions.join('/')} test packet prepared with synthetic non-PHI fixtures. ${selectedCarrier.cutoverDecision}`,
    );
  }

  return (
    <section className="panel carrier-matrix" id="carrier-certification-matrix" data-testid="carrier-certification-matrix">
      <div className="section-heading">
        <div>
          <span>Carrier certification matrix</span>
          <h2>Prove every payer route before live billing is even considered.</h2>
          <p>
            Each row separates VAERION-owned readiness from carrier-issued production acceptance. Internal tests can
            prove packet structure, X12 validation, acknowledgement handling, ERA posting, and audit receipts; they do
            not prove a carrier has approved live submission. Production remains authority-gated until the exact carrier route
            has acceptance evidence.
          </p>
        </div>
        <Table2 size={32} aria-hidden="true" />
      </div>

      <div className="carrier-summary" aria-label="Carrier certification summary">
        {carrierCertificationSummary.map((item) => (
          <div key={item.label}>
            <span className={`status-pill ${item.tone}`}>{toneLabel[item.tone]}</span>
            <strong>{item.value}</strong>
            <small>{item.label}</small>
          </div>
        ))}
      </div>

      <div className="carrier-toolbar">
        <label className="search carrier-search">
          <Search size={18} aria-hidden="true" />
          <input
            aria-label="Search carrier certification matrix"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search carrier, transaction, route, or gate..."
            value={query}
          />
        </label>
        <button className="button primary" onClick={runPayerRouteProof} type="button">
          <RadioTower size={17} />
          Run payer-route proof
        </button>
        <button className="button secondary" onClick={generateInternalPacket} type="button">
          <FileText size={17} />
          Generate test packet
        </button>
      </div>

      <div className="carrier-layout">
        <div className="carrier-table-wrap">
          <table className="carrier-table">
            <thead>
              <tr>
                <th scope="col">Carrier</th>
                <th scope="col">Transactions</th>
                <th scope="col">Guide / packet</th>
                <th scope="col">Receipts</th>
                <th scope="col">Production gate</th>
                <th scope="col">Decision</th>
              </tr>
            </thead>
            <tbody>
              {filteredCarriers.map((carrier) => (
                <tr
                  className={carrier.id === selectedCarrier.id ? 'selected' : ''}
                  key={carrier.id}
                  onClick={() => setSelectedCarrierId(carrier.id)}
                >
                  <td>
                    <button className="carrier-select" type="button" onClick={() => setSelectedCarrierId(carrier.id)}>
                      <strong>{carrier.carrier}</strong>
                      <span>{carrier.market}</span>
                      <em>{carrier.routeType}</em>
                    </button>
                  </td>
                  <td>
                    <div className="transaction-list">
                      {carrier.transactions.map((transaction) => (
                        <span key={`${carrier.id}-${transaction}`}>{transaction}</span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <strong>{carrier.routeStatus}</strong>
                    <span>{carrier.companionGuide}</span>
                  </td>
                  <td>
                    <span>{carrier.acknowledgements}</span>
                    <span>{carrier.eraLifecycle}</span>
                  </td>
                  <td>
                    <span className={`status-pill ${carrier.tone}`}>{toneLabel[carrier.tone]}</span>
                    <p>{carrier.productionGate}</p>
                  </td>
                  <td>
                    <strong>{carrier.cutoverDecision}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <aside className="carrier-detail" aria-label={`${selectedCarrier.carrier} route proof detail`}>
          <div className="carrier-detail-header">
            <span className={`status-pill ${selectedCarrier.tone}`}>{toneLabel[selectedCarrier.tone]}</span>
            <h3>{selectedCarrier.carrier}</h3>
            <p>{selectedCarrier.routeType}</p>
          </div>
          <dl>
            <div>
              <dt>Certification receipt</dt>
              <dd>{selectedCarrier.certificationReceipt}</dd>
            </div>
            <div>
              <dt>Cutover decision</dt>
              <dd>{selectedCarrier.cutoverDecision}</dd>
            </div>
            <div>
              <dt>What VAERION proves</dt>
              <dd>{selectedCarrier.proves}</dd>
            </div>
            <div>
              <dt>What remains unproven</dt>
              <dd>{selectedCarrier.doesNotProve}</dd>
            </div>
            <div>
              <dt>Owner and next action</dt>
              <dd>
                <strong>{selectedCarrier.owner}:</strong> {selectedCarrier.nextAction}
              </dd>
            </div>
          </dl>
          <div className="carrier-blocker">
            <Ban size={18} aria-hidden="true" />
            <p>Live production send is fail-closed until the carrier issues acceptance evidence for this exact route.</p>
          </div>
          <button
            className="button secondary full"
            onClick={() =>
              onCutoverReview(selectedCarrier.carrier, [
                selectedCarrier.productionGate,
                selectedCarrier.doesNotProve,
                selectedCarrier.nextAction,
              ])
            }
            type="button"
          >
            Review production cutover requirements
          </button>
        </aside>
      </div>

      <div className="authority-receipt carrier-receipt" role="status">
        <strong>Latest carrier proof receipt</strong>
        <p>{routeReceipt}</p>
      </div>
    </section>
  );
}

function EnterpriseSignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [username, setUsername] = useState(() => localStorage.getItem('vaerion-billing-username') ?? '');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(Boolean(localStorage.getItem('vaerion-billing-username')));
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const payload = (await response.json().catch(() => ({}))) as { error?: string };

      if (!response.ok) {
        setError(payload.error ?? 'Sign-in failed. Check the username and password.');
        return;
      }

      if (remember) {
        localStorage.setItem('vaerion-billing-username', username.trim());
      } else {
        localStorage.removeItem('vaerion-billing-username');
      }

      setPassword('');
      onSignedIn();
    } catch {
      setError('The billing portal could not reach the sign-in service. Try again or contact VAERION support.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="login-app">
      <section className="login-shell" aria-label="VAERION Billing sign-in">
        <div className="login-brand-panel">
          <img src="/vaerion-logo-horizontal.png" alt="VAERION Systems" />
          <div className="login-brand-copy">
            <h1>VAERION Clinical Billing System</h1>
            <p>
              A protected implementation console for partner billing launch, payer-route proof, EHR conformance,
              claim validation, payment reconciliation, and audit-ready go-live evidence.
            </p>
          </div>
          <div className="login-proof-grid" aria-label="Enterprise access controls">
            <div>
              <ShieldCheck size={18} aria-hidden="true" />
              <span>HttpOnly session cookie</span>
            </div>
            <div>
              <LockKeyhole size={18} aria-hidden="true" />
              <span>Credential-gated portal</span>
            </div>
            <div>
              <KeyRound size={18} aria-hidden="true" />
              <span>Partner access only</span>
            </div>
          </div>
        </div>

        <form className="login-card" onSubmit={handleSubmit}>
          <div className="login-card-heading">
            <span>billing.vaerionsystems.com</span>
            <h2>Enterprise sign-in</h2>
            <p>Use your VAERION-issued billing portal credentials. Live claim transmission remains separately gated.</p>
          </div>

          <label className="field">
            <span>Username or email</span>
            <div className="field-control">
              <UserRound size={18} aria-hidden="true" />
              <input
                autoComplete="username"
                inputMode="email"
                name="username"
                onChange={(event) => setUsername(event.target.value)}
                placeholder="billing-admin@organization.com"
                required
                type="text"
                value={username}
              />
            </div>
          </label>

          <label className="field">
            <span>Password</span>
            <div className="field-control">
              <LockKeyhole size={18} aria-hidden="true" />
              <input
                autoComplete="current-password"
                name="password"
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter password"
                required
                type={showPassword ? 'text' : 'password'}
                value={password}
              />
              <button
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="field-icon-button"
                onClick={() => setShowPassword((value) => !value)}
                type="button"
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </label>

          <div className="login-options">
            <label>
              <input checked={remember} onChange={(event) => setRemember(event.target.checked)} type="checkbox" />
              <span>Remember this workstation</span>
            </label>
            <a href="mailto:support@vaerionsystems.com?subject=VAERION%20Billing%20Portal%20Password%20Help">
              Forgot username/password?
            </a>
          </div>

          {error ? <p className="login-error" role="alert">{error}</p> : null}

          <button className="button primary full" disabled={isSubmitting} type="submit">
            {isSubmitting ? <Loader2 className="spin" size={17} /> : <ShieldCheck size={17} />}
            Sign in securely
          </button>

          <div className="login-support">
            <LifeBuoy size={18} aria-hidden="true" />
            <p>
              Need access? <a href="mailto:support@vaerionsystems.com?subject=VAERION%20Billing%20Portal%20Access">Request a partner billing account</a>.
            </p>
          </div>
        </form>
      </section>
    </main>
  );
}

export function BillingConsoleApp() {
  const [sessionState, setSessionState] = useState<SessionState>('checking');
  const [activeSection, setActiveSection] = useState<SectionId>(() => initialSectionFromHash());
  const [claimStatus, setClaimStatus] = useState('Ready to validate');
  const [claimPayload, setClaimPayload] = useState(
    '{\n  "payer": "Aetna",\n  "provider_npi": "2222222222",\n  "cpt": "99214",\n  "dx": "F41.1"\n}',
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [proofPacket, setProofPacket] = useState<PartnerProofPacket | null>(() => readStoredProofPacket());
  const [packetOpen, setPacketOpen] = useState(false);
  const [packetViewMode, setPacketViewMode] = useState<'plain' | 'ledger' | 'json'>('plain');
  const [packetReceipt, setPacketReceipt] = useState('No AWS proof receipt recorded for this packet yet.');
  const [packetVaultStatus, setPacketVaultStatus] = useState('No server-side proof packet saved yet.');
  const [savedPackets, setSavedPackets] = useState<SavedProofPacketSummary[]>([]);
  const [ehrSandboxReceipt, setEhrSandboxReceipt] = useState<EpicSandboxReceipt | null>(null);
  const [ehrSandboxStatus, setEhrSandboxStatus] = useState('Epic public sandbox conformance has not been run in this browser session.');
  const [epicBaseUrl, setEpicBaseUrl] = useState('https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4');
  const [epicClientIdReference, setEpicClientIdReference] = useState(defaultEpicSandboxClientId);
  const [epicPublicJwksUrl, setEpicPublicJwksUrl] = useState(billingEpicRegistrationPackage.non_production_jwks_url);
  const [epicCredentialSecretRef, setEpicCredentialSecretRef] = useState(defaultEpicSandboxSecretReference);
  const [ehrSandboxTokenReceipt, setEhrSandboxTokenReceipt] = useState<EpicSandboxTokenReceipt | null>(null);
  const [ehrSandboxTokenStatus, setEhrSandboxTokenStatus] = useState(
    'Epic backend OAuth token proof has not been run in this browser session.',
  );
  const [paymentProofReceipt, setPaymentProofReceipt] = useState<PaymentProofReceipt | null>(null);
  const [paymentProofStatus, setPaymentProofStatus] = useState(
    'End-to-end payment proof has not been run in this browser session.',
  );
  const [paymentRailSandboxReceipt, setPaymentRailSandboxReceipt] = useState<PaymentRailSandboxReceipt | null>(null);
  const [paymentRailSandboxStatus, setPaymentRailSandboxStatus] = useState(
    'Payment rail sandbox has not been run in this browser session.',
  );
  const [enterpriseSurfaceReceipts, setEnterpriseSurfaceReceipts] = useState<Record<string, EnterpriseSurfaceReceipt>>({});
  const [enterpriseSurfaceStatus, setEnterpriseSurfaceStatus] = useState(
    'No enterprise deployment surface proof has been run in this browser session.',
  );
  const [launchDossier, setLaunchDossier] = useState<LaunchDossier | null>(null);
  const [launchDossierStatus, setLaunchDossierStatus] = useState(
    'Refresh the server-side dossier before partner review. It summarizes proof packets, enterprise receipts, VAERION-owned evidence surfaces, and external authority gates.',
  );
  const [enterpriseReadiness, setEnterpriseReadiness] = useState<EnterpriseReadiness | null>(null);
  const [enterpriseReadinessStatus, setEnterpriseReadinessStatus] = useState(
    'Refresh enterprise readiness to show what VAERION can prove now, which deployment modes are ready, and which live-production authorities remain counterparty controlled.',
  );
  const [enterpriseProofVerifier, setEnterpriseProofVerifier] = useState<EnterpriseProofVerifier | null>(null);
  const [enterpriseProofVerifierStatus, setEnterpriseProofVerifierStatus] = useState(
    'Refresh the verifier to join proof packets, enterprise receipts, authority evidence, and production-boundary decisions into one executive inspection surface.',
  );
  const [authorityEvidenceReceipts, setAuthorityEvidenceReceipts] = useState<AuthorityEvidenceReceipt[]>([]);
  const [authorityEvidenceStatus, setAuthorityEvidenceStatus] = useState(
    'No counterparty authority evidence reference recorded in this browser session yet.',
  );
  const [authorityEvidenceIntakeOpen, setAuthorityEvidenceIntakeOpen] = useState(false);
  const [authorityEvidenceType, setAuthorityEvidenceType] = useState('payer_trading_partner_acceptance');
  const [authorityEvidenceIssuer, setAuthorityEvidenceIssuer] = useState('Aetna EDI Enrollment');
  const [authorityEvidenceScope, setAuthorityEvidenceScope] = useState(
    'Aetna commercial 837P/999/277CA/835 production route for DRAI Health pilot',
  );
  const [authorityEvidenceReference, setAuthorityEvidenceReference] = useState('payer-portal-case-AETNA-EDI-10001');
  const [authorityEvidenceHash, setAuthorityEvidenceHash] = useState('a'.repeat(64));
  const [preparedApprovalPackets, setPreparedApprovalPackets] = useState<Partial<Record<ApprovalPacketId, PreparedApprovalPacket>>>(
    {},
  );
  const [selectedApprovalPacketId, setSelectedApprovalPacketId] = useState<ApprovalPacketId>('payer-trading-partner');
  const [approvalPacketStatus, setApprovalPacketStatus] = useState(
    'Select a packet, prepare it, then send it to the counterparty owner. VAERION records the returned evidence reference when issued.',
  );
  const [authorityAcquisitionStatus, setAuthorityAcquisitionStatus] = useState(
    'Choose an authority track. VAERION can prepare packets, proof receipts, portal checklists, and evidence intake now; counterparties still issue production authority.',
  );
  const [developerOpsReceipt, setDeveloperOpsReceipt] = useState<DeveloperOpsReceipt | null>(null);
  const [developerOpsStatus, setDeveloperOpsStatus] = useState(
    'Developer operations proof has not been run yet. Rotate a sandbox key reference or replay a webhook to create an audit-safe receipt.',
  );
  const [companionGuideReceipt, setCompanionGuideReceipt] = useState<CompanionGuideReceipt | null>(null);
  const [companionGuideStatus, setCompanionGuideStatus] = useState(
    'Companion-guide validation has not been run yet. Use the sample text or paste payer implementation requirements without PHI.',
  );
  const [companionGuideText, setCompanionGuideText] = useState(
    'Aetna 5010X222A1 companion guide. Submitter ID required. Receiver ID 60054. 837P accepted. 999 and 277CA acknowledgements returned. 835 ERA enrollment separate.',
  );
  const [replayImportReceipt, setReplayImportReceipt] = useState<ReplayImportReceipt | null>(null);
  const [replayImportStatus, setReplayImportStatus] = useState(
    'No de-identified replay manifest has been imported yet. Import a manifest with artifact hashes, not raw patient or claim payloads.',
  );
  const [proofReviewReceipt, setProofReviewReceipt] = useState<ProofReviewReceipt | null>(null);
  const [proofReviewStatus, setProofReviewStatus] = useState(
    'No formal proof review decision recorded yet. Review decisions are internal accountability and do not unlock external authority.',
  );
  const [proofReviewDecision, setProofReviewDecision] = useState<'reviewed' | 'accepted' | 'rejected' | 'expired'>('reviewed');
  const [authorityReceipt, setAuthorityReceipt] = useState('No workaround receipt generated yet.');
  const [workflowRun, setWorkflowRun] = useState<WorkflowRun>({
    title: 'No workflow run selected',
    status: 'blocked',
    summary: 'Choose a workstream action to see exactly what VAERION ran, what it proved, and which authority evidence is still controlled by counterparties.',
    receipt: 'No workflow receipt yet.',
    nextSteps: ['Run readiness path, validate a claim, run payer-route proof, or generate the partner packet.'],
    evidence: ['Every workflow result shown here is non-PHI implementation proof.'],
    blockers: ['Live payer submission, Epic writes, money movement, and hospital PHI access require authority proof.'],
  });
  const [docsOpen, setDocsOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState('Not copied yet');
  const [manualCopyText, setManualCopyText] = useState('');
  const [actionFeedback, setActionFeedback] = useState<ActionFeedback>({
    title: 'Start here',
    detail: 'Refresh the launch dossier or run the readiness path. Every action will report what it proved, where the artifact went, and which authority evidence is still required.',
    artifact: 'No proof artifact generated in this session yet.',
    targetId: 'launch-dossier',
    tone: 'neutral',
  });
  const generatedPacket = Boolean(proofPacket);
  const activeWorkstream = workstreams.find((item) => item.id === activeSection) ?? workstreams[0];
  const ActiveWorkstreamIcon = activeWorkstream.icon;
  const activeGuide = sectionProofGuide[activeSection];
  const activeWorkstreamMetrics =
    activeWorkstream.id === 'ehr'
      ? [
          {
            label: 'FHIR version',
            value: ehrSandboxReceipt?.capability_statement.fhir_version ?? 'R4',
          },
          {
            label: 'Sandbox discovery',
            value: ehrSandboxReceipt ? 'Verified' : 'Ready to run',
            tone: ehrSandboxReceipt ? 'complete' : 'action',
          },
          {
            label: 'Sandbox OAuth',
            value: ehrSandboxTokenReceipt ? 'Verified' : 'Not run yet',
            tone: ehrSandboxTokenReceipt ? 'complete' : 'action',
          },
          { label: 'Production writes', value: 'Authority required', tone: 'blocked' },
        ]
      : activeWorkstream.metrics.map((metric) =>
          activeWorkstream.id === 'approvals' && metric.label === 'Packet templates'
            ? { ...metric, value: `${Object.keys(preparedApprovalPackets).length}/${approvalPacketTemplates.length}` }
            : activeWorkstream.id === 'developer' && metric.label === 'API surfaces'
            ? { ...metric, value: String(implementationApi.length) }
            : metric,
        );

  function reportAction(feedback: ActionFeedback) {
    setActionFeedback(feedback);
  }

  function scrollToId(id: string, block: ScrollLogicalPosition = 'start') {
    window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'auto', block });
    }, 40);
    window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'auto', block });
    }, 180);
  }

  function sectionTargetId(section: SectionId) {
    const targets: Partial<Record<SectionId, string>> = {
      launch: 'launch-dossier',
      authority: 'authority-workaround-lab',
      approvals: 'authority-acquisition-console',
      ehr: 'ehr-sandbox-lab',
      payer: 'carrier-certification-matrix',
      payments: 'payment-proof-lab',
      audit: proofPacket ? 'partner-proof-packet-viewer' : 'active-workstream',
      developer: 'developer-docs',
    };
    return targets[section] ?? 'active-workstream';
  }

  function selectSection(section: SectionId, targetId = sectionTargetId(section)) {
    setActiveSection(section);
    setAuthorityEvidenceIntakeOpen(false);
    setSearchQuery('');
    window.history.replaceState(null, '', `#${section}`);
    if (section === 'developer') {
      setDocsOpen(true);
    }
    const navTarget = navSections.find((item) => item.id === section);
    reportAction({
      title: `Opened ${navTarget?.label ?? section}`,
      detail: sectionProofGuide[section].use,
      artifact: sectionProofGuide[section].proves,
      targetId,
      tone: 'neutral',
    });
    scrollToId(targetId);
  }

  function openDeveloperDocs() {
    setDocsOpen(true);
    reportAction({
      title: 'Developer docs opened',
      detail: 'The API handoff section is now visible. Use Copy API surfaces to place the implementation endpoints on the clipboard.',
      artifact: 'Developer handoff is an implementation guide, not a credential or production authority grant.',
      targetId: 'developer-docs',
      tone: 'success',
    });
    scrollToId('developer-docs');
  }

  function activationTrackTarget(trackId: string): { section: SectionId; targetId: string; label: string } {
    const targets: Record<string, { section: SectionId; targetId: string; label: string }> = {
      'ehr-production-access': {
        section: 'ehr' as SectionId,
        targetId: 'ehr-sandbox-lab',
        label: 'EHR / FHIR proof lab',
      },
      'payer-production-submission': {
        section: 'payer' as SectionId,
        targetId: 'carrier-certification-matrix',
        label: 'direct payer route proof',
      },
      'money-movement-activation': {
        section: 'payments' as SectionId,
        targetId: 'payment-rail-sandbox-lab',
        label: 'payment rail sandbox',
      },
      'hospital-production-cutover': {
        section: 'onboarding' as SectionId,
        targetId: 'enterprise-readiness',
        label: 'hospital deployment readiness',
      },
    };
    return targets[trackId] ?? { section: 'launch' as SectionId, targetId: 'enterprise-proof-verifier', label: 'launch control' };
  }

  function handleActivationTrackAction(
    track: NonNullable<EnterpriseProofVerifier['production_activation_tracks']>[number],
    stateId: string,
  ) {
    const target = activationTrackTarget(track.track_id);

    if (stateId === 'prepare_now') {
      selectSection(target.section, target.targetId);
      reportAction({
        title: `Prepared ${track.title}`,
        detail: `Opened ${target.label}. Run the VAERION-owned proof receipts, then attach those receipts to the launch packet.`,
        artifact: track.states.find((state) => state.state_id === stateId)?.proof_or_action,
        targetId: target.targetId,
        tone: 'success',
      });
      return;
    }

    if (stateId === 'send_to_counterparty') {
      void generatePartnerProofPacket();
      reportAction({
        title: `Counterparty packet prepared for ${track.title}`,
        detail:
          'The packet viewer opens with non-PHI proof, receipt IDs, evidence boundaries, and the activation workflow for external review.',
        artifact: `${track.evidence_label}: packet generated for ${track.counterparty}.`,
        targetId: 'partner-proof-packet-viewer',
        tone: 'success',
      });
      return;
    }

    if (stateId === 'evidence_received') {
      setAuthorityEvidenceIntakeOpen(true);
      setAuthorityEvidenceType(track.authority_evidence_type);
      setAuthorityEvidenceIssuer(track.counterparty.split(',')[0] ?? track.counterparty);
      setAuthorityEvidenceScope(`${track.title} for DRAI Health partner deployment`);
      setAuthorityEvidenceReference(`${track.track_id}-authority-reference`);
      setAuthorityEvidenceHash('');
      reportAction({
        title: `Evidence intake opened for ${track.title}`,
        detail:
          'Paste the counterparty-issued artifact reference and SHA-256 hash. Do not paste PHI, secrets, tokens, account numbers, or raw banking data.',
        artifact: track.evidence_label,
        targetId: 'authority-evidence-intake',
        tone: 'neutral',
      });
      scrollToId('authority-evidence-intake', 'center');
      return;
    }

    if (stateId === 'review_evidence') {
      if (track.latest_authority_evidence) {
        reportAction({
          title: `Review ${track.latest_authority_evidence.receipt_id}`,
          detail:
            'Review issuer, scope, artifact hash, lifecycle state, and exact authority boundary before any production activation decision.',
          artifact: `${track.latest_authority_evidence.artifact_reference} · ${track.latest_authority_evidence.artifact_sha256}`,
          targetId: 'authority-evidence-intake',
          tone: 'neutral',
        });
      } else {
        reportAction({
          title: 'Authority evidence not recorded yet',
          detail:
            'Record the counterparty-issued reference and hash first. VAERION can prepare the review, but there is no external artifact to accept yet.',
          artifact: track.evidence_label,
          targetId: 'authority-evidence-intake',
          tone: 'neutral',
        });
      }
      scrollToId('authority-evidence-intake', 'center');
      return;
    }

    void generatePartnerProofPacket();
    reportAction({
      title:
        track.current_state === 'eligible_for_cutover_review'
          ? `${track.title} is ready for final cutover review`
          : `${track.title} needs accepted authority evidence before cutover review`,
      detail: track.next_best_action,
      artifact: track.final_review,
      targetId: 'partner-proof-packet-viewer',
      tone: track.current_state === 'eligible_for_cutover_review' ? 'success' : 'neutral',
    });
  }

  const searchResults = useMemo(() => {
    const normalized = searchQuery.trim().toLowerCase();
    if (!normalized) return [];
    return navSections
      .filter((section) =>
        [section.label, section.description, workstreams.find((stream) => stream.id === section.id)?.summary ?? '']
          .join(' ')
          .toLowerCase()
          .includes(normalized),
      )
      .slice(0, 5);
  }, [searchQuery]);

  const nextActions = useMemo(
    () => [
      'Import facility and provider roster through the guarded onboarding validator.',
      'Run Epic/FHIR conformance simulation, then record customer credential proof when available.',
      'Attach payer companion-guide and test-cycle receipts before enabling live route review.',
      'Run the 13-stage pilot packet and export proof for partner implementation review.',
    ],
    [],
  );

  useEffect(() => {
    let cancelled = false;

    async function loadSession() {
      try {
        const response = await fetch('/api/session', {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        if (cancelled) return;
        const contentType = response.headers.get('content-type') ?? '';
        const payload = contentType.includes('application/json')
          ? ((await response.json().catch(() => null)) as { authenticated?: boolean } | null)
          : null;
        if (payload?.authenticated === true || (import.meta.env.DEV && response.status === 404)) {
          setSessionState('authenticated');
          return;
        }
        setSessionState('unauthenticated');
      } catch {
        if (cancelled) return;
        setSessionState(import.meta.env.DEV ? 'authenticated' : 'unauthenticated');
      }
    }

    loadSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    function syncSectionFromHash() {
      const hash = window.location.hash.replace('#', '') as SectionId;
      if (navSections.some((section) => section.id === hash)) {
        setActiveSection(hash);
        setAuthorityEvidenceIntakeOpen(false);
      }
    }

    syncSectionFromHash();
    window.addEventListener('hashchange', syncSectionFromHash);
    return () => window.removeEventListener('hashchange', syncSectionFromHash);
  }, []);

  async function handleSignOut() {
    await fetch('/api/logout', { method: 'POST', credentials: 'include' }).catch(() => undefined);
    setSessionState('unauthenticated');
  }

  async function recordWorkflowReceipt(section: SectionId, actionLabel: string) {
    try {
      const response = await billingFetch(`/proof-receipts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          proof_type: `${section}_workflow`,
          object_type: 'billing_console_workflow',
          object_id: section,
          summary: `${actionLabel} executed from the VAERION partner billing console as non-PHI implementation proof.`,
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        receipt_id?: string;
        created_at?: string;
        live_external_action?: string;
        error?: string;
      };
      if (!response.ok) {
        return payload.error ?? 'AWS proof receipt could not be recorded. Workflow result remains visible in this browser.';
      }
      return `${payload.receipt_id}: internal proof recorded at ${payload.created_at ?? 'current time'}. Live external action remains gated until authority proof is recorded.`;
    } catch {
      return 'AWS proof receipt could not be reached. Workflow result remains visible in this browser.';
    }
  }

  function workflowRunFor(
    section: SectionId,
    actionLabel: string,
    receipt = 'Recording proof receipt...',
    status: WorkflowRun['status'] = 'running',
  ): WorkflowRun {
    const stream = workstreams.find((item) => item.id === section) ?? workstreams[0];
    const blockers = stream.steps
      .filter((step) => step.tone === 'blocked')
      .map((step) => `${step.label}: ${step.detail}`);
    return {
      title: `${actionLabel}: ${stream.title}`,
      status,
      summary: stream.summary,
      receipt,
      nextSteps: [
        stream.primaryAction,
        stream.secondaryAction,
        'Attach external authority proof before enabling any live payer, EHR, hospital PHI, or money-movement operation.',
      ],
      evidence: stream.steps.map((step) => `${step.label}: ${step.state}. ${step.detail}`),
      blockers,
    };
  }

  async function runConsoleWorkflow(section: SectionId, actionLabel: string) {
    setActiveSection(section);
    reportAction({
      title: `${actionLabel} started`,
      detail: `Running ${workstreams.find((item) => item.id === section)?.title ?? section} as non-PHI implementation proof.`,
      artifact: 'Waiting for workflow receipt.',
      targetId: 'workflow-result',
      tone: 'running',
    });
    setWorkflowRun(workflowRunFor(section, actionLabel));
    scrollToId('workflow-result');
    const receipt = await recordWorkflowReceipt(section, actionLabel);
    setWorkflowRun(workflowRunFor(section, actionLabel, receipt, 'recorded'));
    reportAction({
      title: `${actionLabel} recorded`,
      detail: sectionProofGuide[section].proves,
      artifact: receipt,
      targetId: 'workflow-result',
      tone: receipt.includes('could not') ? 'blocked' : 'success',
    });
    scrollToId('workflow-result');
  }

  function buildApprovalPacket(template: ApprovalPacketTemplate): PreparedApprovalPacket {
    return {
      ...template,
      packet_id: `${template.id}-${Date.now()}`,
      generated_at: new Date().toISOString(),
      prepared_by: 'partner_billing_console',
      status: 'prepared_for_counterparty_review',
      live_external_action: 'not_submitted_by_vaerion',
    };
  }

  function preparedApprovalPacketFor(packetId = selectedApprovalPacketId) {
    const template = approvalPacketTemplates.find((item) => item.id === packetId) ?? approvalPacketTemplates[0];
    return preparedApprovalPackets[packetId] ?? buildApprovalPacket(template);
  }

  function approvalPacketMarkdown(packet: PreparedApprovalPacket) {
    return [
      `# VAERION ${packet.title}`,
      ``,
      `Packet ID: ${packet.packet_id}`,
      `Generated: ${packet.generated_at}`,
      `Prepared by: ${packet.prepared_by}`,
      `Status: Prepared for counterparty review`,
      `Live external action: Live submission not performed by VAERION`,
      ``,
      `## Purpose`,
      packet.purpose,
      ``,
      `## Counterparty`,
      packet.counterparty,
      ``,
      `## VAERION Prefilled Evidence`,
      ...packet.vaerionPrefill.map((item) => `- ${item}`),
      ``,
      `## Counterparty Fields Required`,
      ...packet.externalFields.map((item) => `- ${item}`),
      ``,
      `## Evidence Required Before Production Authority`,
      ...packet.evidenceNeeded.map((item) => `- ${item}`),
      ``,
      `## Proof Receipts To Attach`,
      ...packet.proofReceipts.map((item) => `- ${item}`),
      ``,
      `## Portal Checklist`,
      ...packet.portalChecklist.map((item) => `- ${item}`),
      ``,
      `## Submission Boundary`,
      packet.submissionBoundary,
      ``,
      `## Next Step`,
      packet.nextStep,
      ``,
      `## Guardrail`,
      `This packet does not prove or grant live payer acceptance, Epic production access, hospital go-live authorization, or money movement authority. It is a counterparty-review artifact and evidence checklist.`,
      ``,
    ].join('\n');
  }

  function prepareApprovalPacket(packetId: ApprovalPacketId, scroll = true) {
    const template = approvalPacketTemplates.find((item) => item.id === packetId) ?? approvalPacketTemplates[0];
    const packet = buildApprovalPacket(template);
    setPreparedApprovalPackets((current) => ({ ...current, [packetId]: packet }));
    setSelectedApprovalPacketId(packetId);
    setApprovalPacketStatus(
      `${packet.title} prepared for counterparty review. Live submission not performed by VAERION; attach returned authority evidence when the counterparty issues it.`,
    );
    reportAction({
      title: `${packet.title} prepared`,
      detail:
        'The packet now shows VAERION-prefilled proof, counterparty fields, portal checklist, submission boundary, and evidence intake path.',
      artifact: packet.packet_id,
      targetId: 'approval-packet-workbench',
      tone: 'success',
    });
    if (scroll) scrollToId('approval-packet-workbench', 'center');
    return packet;
  }

  function prepareAllApprovalPackets() {
    const nextPackets = Object.fromEntries(
      approvalPacketTemplates.map((template) => [template.id, buildApprovalPacket(template)]),
    ) as Record<ApprovalPacketId, PreparedApprovalPacket>;
    setPreparedApprovalPackets(nextPackets);
    setSelectedApprovalPacketId('payer-trading-partner');
    setApprovalPacketStatus(
      'All four counterparty approval packets are prepared for review. No external submission, signature, production access, or money movement was performed by VAERION.',
    );
    reportAction({
      title: 'Approval packet set prepared',
      detail: 'Payer, Epic/customer, hospital cutover, and financial authority packets are ready to download or attach to evidence intake.',
      artifact: `${Object.keys(nextPackets).length} approval packet(s) prepared.`,
      targetId: 'approval-packet-workbench',
      tone: 'success',
    });
    scrollToId('approval-packet-workbench', 'center');
  }

  function downloadApprovalPacket(packet = preparedApprovalPacketFor()) {
    setPreparedApprovalPackets((current) => ({ ...current, [packet.id]: packet }));
    setSelectedApprovalPacketId(packet.id);
    const blob = new Blob([approvalPacketMarkdown(packet)], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vaerion-approval-packet-${packet.downloadName}-${packet.packet_id}.md`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setApprovalPacketStatus(`${packet.title} Markdown download started.`);
    reportAction({
      title: 'Approval packet download started',
      detail: 'The packet is a non-PHI counterparty-review artifact with evidence checklist and authority boundary.',
      artifact: `vaerion-approval-packet-${packet.downloadName}-${packet.packet_id}.md`,
      targetId: 'approval-packet-workbench',
      tone: 'success',
    });
  }

  function downloadAllApprovalPackets() {
    const packets =
      approvalPacketTemplates.map((template) => preparedApprovalPackets[template.id] ?? buildApprovalPacket(template));
    const nextPackets = Object.fromEntries(packets.map((packet) => [packet.id, packet])) as Record<
      ApprovalPacketId,
      PreparedApprovalPacket
    >;
    setPreparedApprovalPackets(nextPackets);
    setSelectedApprovalPacketId('payer-trading-partner');
    const markdown = [
      '# VAERION Counterparty Approval Packet Set',
      '',
      `Generated: ${new Date().toISOString()}`,
      'Prepared by: partner_billing_console',
      'Live external action: Live submission not performed by VAERION',
      '',
      'This non-PHI packet set includes payer trading partner, Epic/customer production access, hospital cutover, and financial authority packets. It does not grant production authority.',
      '',
      ...packets.flatMap((packet) => ['---', '', approvalPacketMarkdown(packet)]),
    ].join('\n');
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vaerion-approval-packet-set-${Date.now()}.md`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setApprovalPacketStatus('All counterparty approval packets downloaded as one non-PHI Markdown packet set.');
    reportAction({
      title: 'Approval packet set download started',
      detail: 'The packet set includes every counterparty checklist while keeping production authority and live submission gated.',
      artifact: link.download,
      targetId: 'approval-packet-workbench',
      tone: 'success',
    });
  }

  function authorityAcquisitionMarkdown() {
    const packets = Object.fromEntries(
      approvalPacketTemplates.map((template) => [
        template.id,
        preparedApprovalPackets[template.id] ?? buildApprovalPacket(template),
      ]),
    ) as Record<ApprovalPacketId, PreparedApprovalPacket>;

    return [
      '# VAERION Live Authority Acquisition Dossier',
      '',
      `Generated: ${new Date().toISOString()}`,
      'Prepared by: partner_billing_console',
      'Purpose: give implementation operators the exact real-world path for obtaining payer, Epic/customer, hospital, and financial production authority.',
      '',
      '## Truth Boundary',
      'VAERION can prepare packets, proof receipts, non-PHI sandbox evidence, validation artifacts, checklists, and evidence intake. The counterparty still issues production authority, credentials, approvals, and financial movement permission.',
      '',
      ...authorityAcquisitionTracks.flatMap((track) => {
        const packet = packets[track.packetId];
        return [
          '---',
          '',
          `## ${track.title}`,
          '',
          `Counterparty: ${track.counterparty}`,
          `Official path: ${track.portalLabel} (${track.portalUrl})`,
          `Source: ${track.sourceLabel} (${track.sourceUrl})`,
          `Prepared packet: ${packet.title} (${packet.packet_id})`,
          '',
          '### VAERION Can Do Now',
          ...track.vaerionCanDoNow.map((item) => `- ${item}`),
          '',
          '### Counterparty Must Return',
          ...track.counterpartyMustReturn.map((item) => `- ${item}`),
          '',
          '### Run First',
          track.proofToRunFirst,
          '',
          '### Evidence Intake',
          `Evidence type: ${track.authorityEvidenceType}`,
          `Issuer: ${track.evidenceIssuer}`,
          `Scope: ${track.evidenceScope}`,
          `Reference placeholder: ${track.referencePlaceholder}`,
          '',
          '### Packet Checklist',
          ...packet.portalChecklist.map((item) => `- ${item}`),
        ];
      }),
      '',
      '## Guardrails',
      '- Do not paste PHI, secrets, tokens, passwords, private keys, bank account numbers, or raw documents into evidence intake.',
      '- Record only counterparty references, metadata, and SHA-256 hashes.',
      '- Live payer submission, Epic/customer production writes, PHI-bearing hospital cutover, and money movement remain disabled until the returned authority evidence is reviewed and accepted.',
      '',
    ].join('\n');
  }

  function downloadAuthorityAcquisitionDossier() {
    const markdown = authorityAcquisitionMarkdown();
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vaerion-live-authority-acquisition-dossier-${Date.now()}.md`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setAuthorityAcquisitionStatus(
      'Live authority acquisition dossier download started. It includes every official path, packet, proof action, and evidence intake requirement.',
    );
    reportAction({
      title: 'Authority acquisition dossier download started',
      detail:
        'The dossier gives the implementation owner a real filing path for payer, Epic/customer, hospital, and financial authority while keeping production actions gated.',
      artifact: link.download,
      targetId: 'authority-acquisition-console',
      tone: 'success',
    });
  }

  function prepareAuthorityAcquisitionPacket(track: AuthorityAcquisitionTrack) {
    const packet = prepareApprovalPacket(track.packetId);
    setAuthorityAcquisitionStatus(
      `${track.title}: ${packet.title} prepared. Use the official path, then record the returned ${track.authorityEvidenceType} reference.`,
    );
  }

  async function copyAuthorityAcquisitionChecklist(track: AuthorityAcquisitionTrack) {
    const packet = preparedApprovalPackets[track.packetId] ?? buildApprovalPacket(
      approvalPacketTemplates.find((template) => template.id === track.packetId) ?? approvalPacketTemplates[0],
    );
    setAuthorityAcquisitionStatus(
      `${track.title}: filing checklist generated. Copying now; if browser clipboard permission is unavailable, use the manual copy buffer below.`,
    );
    const copied = await copyTextToClipboard(
      [
        `${track.title} filing checklist`,
        `Official path: ${track.portalLabel} - ${track.portalUrl}`,
        `Source: ${track.sourceLabel} - ${track.sourceUrl}`,
        '',
        'VAERION can do now:',
        ...track.vaerionCanDoNow.map((item) => `- ${item}`),
        '',
        'Counterparty must return:',
        ...track.counterpartyMustReturn.map((item) => `- ${item}`),
        '',
        'Run first:',
        `- ${track.proofToRunFirst}`,
        '',
        'Portal packet checklist:',
        ...packet.portalChecklist.map((item) => `- ${item}`),
        '',
        `Evidence type: ${track.authorityEvidenceType}`,
        `Evidence issuer: ${track.evidenceIssuer}`,
        `Evidence scope: ${track.evidenceScope}`,
        `Reference placeholder: ${track.referencePlaceholder}`,
      ].join('\n'),
      `${track.title} filing checklist copied.`,
      `${track.title} filing checklist is visible for manual copy because clipboard permission was unavailable.`,
      'authority-acquisition-console',
    );
    if (copied) {
      setAuthorityAcquisitionStatus(
        `${track.title}: filing checklist copied with official path, packet checklist, and evidence fields.`,
      );
    } else {
      setAuthorityAcquisitionStatus(
        `${track.title}: filing checklist is visible in the manual copy buffer with official path, packet checklist, and evidence fields.`,
      );
    }
  }

  function recordReturnedAuthorityEvidence(track: AuthorityAcquisitionTrack) {
    setAuthorityEvidenceIntakeOpen(true);
    setAuthorityEvidenceType(track.authorityEvidenceType);
    setAuthorityEvidenceIssuer(track.evidenceIssuer);
    setAuthorityEvidenceScope(track.evidenceScope);
    setAuthorityEvidenceReference(track.referencePlaceholder);
    setAuthorityEvidenceHash('');
    setAuthorityAcquisitionStatus(
      `${track.title}: authority evidence intake opened. Record only the issuer reference and artifact hash when the counterparty returns it.`,
    );
    reportAction({
      title: `Evidence intake opened for ${track.title}`,
      detail:
        'This records a reference to an external approval artifact. It does not submit a claim, write to Epic, access PHI, or move funds.',
      artifact: track.authorityEvidenceType,
      targetId: 'authority-evidence-intake',
      tone: 'neutral',
    });
    scrollToId('authority-evidence-intake', 'center');
  }

  async function runAuthorityAcquisitionProof(track: AuthorityAcquisitionTrack) {
    setAuthorityAcquisitionStatus(
      `${track.title}: running the VAERION-owned proof that should be attached before external authority is requested.`,
    );
    if (track.proofAction === 'payer') {
      await runEnterpriseSurface('direct-payer-packet');
      return;
    }
    if (track.proofAction === 'ehr') {
      selectSection('ehr', 'ehr-sandbox-lab');
      await runEhrSandboxLab();
      return;
    }
    if (track.proofAction === 'payment') {
      selectSection('payments', 'payment-rail-sandbox-lab');
      await runPaymentRailSandbox();
      return;
    }
    await runEnterpriseSurface('hospital-onboarding');
  }

  async function copyApprovalChecklist(packet = preparedApprovalPacketFor()) {
    setPreparedApprovalPackets((current) => ({ ...current, [packet.id]: packet }));
    setSelectedApprovalPacketId(packet.id);
    const copied = await copyTextToClipboard(
      [`${packet.title} portal checklist`, ...packet.portalChecklist.map((item) => `- ${item}`)].join('\n'),
      `Portal checklist copied for ${packet.title}.`,
      `Browser clipboard permission was unavailable. The portal checklist for ${packet.title} remains visible for manual copy.`,
      'approval-packet-workbench',
    );
    if (copied) {
      setApprovalPacketStatus(`Portal checklist copied for ${packet.title}.`);
      reportAction({
        title: 'Approval packet checklist copied',
        detail: 'Use the copied checklist in the payer, Epic/customer, hospital, or finance portal controlled by that counterparty.',
        artifact: packet.title,
        targetId: 'approval-packet-workbench',
        tone: 'success',
      });
    }
  }

  function openApprovalEvidenceIntake(packet = preparedApprovalPacketFor()) {
    setPreparedApprovalPackets((current) => ({ ...current, [packet.id]: packet }));
    setSelectedApprovalPacketId(packet.id);
    setAuthorityEvidenceIntakeOpen(true);
    setAuthorityEvidenceType(packet.authorityEvidenceType);
    setAuthorityEvidenceIssuer(packet.evidenceIssuer);
    setAuthorityEvidenceScope(packet.evidenceScope);
    setAuthorityEvidenceReference(packet.evidenceReference);
    setAuthorityEvidenceHash('');
    setApprovalPacketStatus(`Evidence intake opened for ${packet.title}. Paste only the counterparty-issued reference and artifact hash.`);
    reportAction({
      title: `Evidence intake opened for ${packet.title}`,
      detail: 'Record the counterparty-issued approval reference after the payer, Epic/customer, hospital, or finance owner issues it.',
      artifact: packet.authorityEvidenceType,
      targetId: 'authority-evidence-intake',
      tone: 'neutral',
    });
    scrollToId('authority-evidence-intake', 'center');
  }

  async function markApprovalPacketReady(packet = preparedApprovalPacketFor()) {
    setSelectedApprovalPacketId(packet.id);
    setApprovalPacketStatus(`${packet.title} is being recorded as ready for counterparty review...`);
    const receipt = await recordWorkflowReceipt('approvals', `${packet.title} ready for counterparty review`);
    const nextPacket = { ...packet, readiness_receipt_id: receipt };
    setPreparedApprovalPackets((current) => ({ ...current, [packet.id]: nextPacket }));
    setApprovalPacketStatus(`Counterparty review receipt recorded for ${packet.title}: ${receipt}`);
    reportAction({
      title: 'Approval packet review readiness recorded',
      detail: 'Internal readiness was recorded without submitting externally or unlocking live production authority.',
      artifact: receipt,
      targetId: 'approval-packet-workbench',
      tone: receipt.includes('could not') ? 'blocked' : 'success',
    });
  }

  async function runReadinessPath() {
    setClaimStatus('Readiness path running across launch, EHR, payer route, claim, payment, denial, and audit checks...');
    await runConsoleWorkflow('launch', 'Full readiness path');
    setClaimStatus('Readiness path recorded. Live submission remains authority-gated until external authority evidence exists.');
  }

  async function validateClaimSandbox() {
    try {
      const parsed = JSON.parse(claimPayload) as Record<string, unknown>;
      const missing = ['payer', 'provider_npi', 'cpt', 'dx'].filter((key) => !parsed[key]);
      if (missing.length > 0) {
        setClaimStatus(`Validation held for required non-PHI test fields: ${missing.join(', ')}.`);
        return;
      }
      setClaimStatus('Validation passed. Claim can enter pre-production pilot only.');
      await runConsoleWorkflow('claims', 'Claim sandbox validation');
    } catch {
      setClaimStatus('Validation held. The sample claim payload is not valid JSON.');
    }
  }

  async function runEhrSandboxLab() {
    setEhrSandboxStatus('Running Epic public sandbox CapabilityStatement and SMART/OIDC discovery...');
    setWorkflowRun({
      title: 'Epic/FHIR sandbox conformance: running',
      status: 'running',
      summary:
        'VAERION is checking Epic public FHIR sandbox discovery without PHI. This proves client readiness, not production customer authority.',
      receipt: 'Waiting for Epic public sandbox response...',
      nextSteps: [
        'Parse CapabilityStatement.',
        'Check Patient, Coverage, Encounter, and DocumentReference support.',
        'Keep production access authority-gated until customer approval exists.',
      ],
      evidence: ['No patient, member, claim, or clinical payload is sent in this sandbox discovery run.'],
      blockers: ['Production Epic reads and writes require customer-approved Epic credentials and launch approval.'],
    });
    scrollToId('workflow-result');

    try {
      const response = await billingFetch(`/ehr/epic-sandbox/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          base_url: epicBaseUrl.trim() || 'https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4',
          mode: 'public_sandbox_non_phi',
          ...(epicClientIdReference.trim() ? { epic_client_id: epicClientIdReference.trim() } : {}),
          ...(epicPublicJwksUrl.trim() ? { public_jwks_url: epicPublicJwksUrl.trim() } : {}),
          ...(epicCredentialSecretRef.trim() ? { credential_secret_ref: epicCredentialSecretRef.trim() } : {}),
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as EpicSandboxReceipt & { error?: string; next_step?: string };
      if (!response.ok) {
        const message = payload.error ?? 'Epic sandbox conformance could not be completed.';
        setEhrSandboxStatus(`${message} ${payload.next_step ?? ''}`.trim());
        setWorkflowRun({
          title: 'Epic/FHIR sandbox conformance: authority gated',
          status: 'blocked',
          summary: message,
          receipt: payload.next_step ?? 'No receipt recorded.',
          nextSteps: [
            'Confirm the Epic public sandbox base URL.',
            'Rerun the EHR lab.',
            'Do not claim production readiness from a failed sandbox run.',
          ],
          evidence: ['The console attempted a non-PHI sandbox discovery call.'],
          blockers: ['Epic sandbox proof is incomplete until the public CapabilityStatement can be reached and parsed.'],
        });
        return;
      }

      setEhrSandboxReceipt(payload);
      setEhrSandboxStatus(
        `${payload.receipt_id}: Epic public sandbox conformance receipt recorded. Production Epic access remains externally gated.`,
      );
      reportAction({
        title: 'Epic sandbox proof recorded',
        detail: payload.proves,
        artifact: `${payload.receipt_id}: ${payload.does_not_prove}`,
        targetId: 'ehr-sandbox-lab',
        tone: 'success',
      });
      setWorkflowRun({
        title: 'Epic/FHIR sandbox conformance: recorded',
        status: 'recorded',
        summary: payload.proves,
        receipt: `${payload.receipt_id}: ${payload.does_not_prove}`,
        nextSteps: [
          'Attach this sandbox receipt to the partner proof packet.',
          'Run SMART launch with partner/customer-issued non-production credentials when issued.',
          'Keep production reads and writes authority-gated until customer Epic authority is recorded.',
        ],
        evidence: [
          `CapabilityStatement endpoint: ${payload.capability_statement.endpoint}`,
          `FHIR version: ${payload.capability_statement.fhir_version}`,
          `SMART/OIDC endpoint checked: ${payload.smart_configuration.endpoint}`,
          ...Object.entries(payload.resource_support).map(
            ([resource, supported]) => `${resource}: ${supported ? 'advertised by sandbox' : 'not advertised by sandbox'}`,
          ),
          ...payload.mapping_tests,
        ],
        blockers: [payload.live_external_action, payload.does_not_prove],
      });
      scrollToId('workflow-result');
    } catch {
      setEhrSandboxStatus('Epic sandbox conformance could not be reached from this browser session.');
      setWorkflowRun({
        title: 'Epic/FHIR sandbox conformance: connection unavailable',
        status: 'blocked',
        summary: 'The billing API was not reachable for the Epic sandbox conformance run.',
        receipt: 'No receipt recorded.',
        nextSteps: ['Check billing-api.vaerionsystems.com health.', 'Rerun the EHR lab after API connectivity is restored.'],
        evidence: ['No PHI was sent.'],
        blockers: ['Sandbox proof remains incomplete until a receipt is recorded.'],
      });
    }
  }

  async function runEhrSandboxTokenTest(runLiveResourceTests = false) {
    setEhrSandboxTokenStatus(
      runLiveResourceTests
        ? 'Running Epic backend OAuth token exchange plus live non-PHI sandbox FHIR reads...'
        : 'Running Epic backend OAuth token exchange proof...',
    );
    setWorkflowRun({
      title: runLiveResourceTests
        ? 'Epic live sandbox resource proof: running'
        : 'Epic backend OAuth token proof: running',
      status: 'running',
      summary:
        runLiveResourceTests
          ? 'VAERION is exchanging a backend OAuth token, using it against Epic public non-PHI sandbox FHIR endpoints, then storing only sanitized receipt data.'
          : 'VAERION is using only non-secret references from the browser. The private key stays server-side in AWS Secrets Manager and the raw token is not stored.',
      receipt: runLiveResourceTests
        ? 'Waiting for Epic sandbox token endpoint and FHIR resource responses...'
        : 'Waiting for Epic sandbox token endpoint response...',
      nextSteps: [
        'Load AWS Secrets Manager private-key reference inside the billing API.',
        'Sign the backend OAuth client assertion server-side.',
        'Exchange the assertion at the Epic sandbox token endpoint.',
        ...(runLiveResourceTests
          ? ['Read Patient, Coverage, Encounter, and DocumentReference public sandbox fixture endpoints.']
          : []),
        'Store only a sanitized non-PHI receipt.',
      ],
      evidence: ['No private key, client secret, access token, authorization code, patient ID, or PHI is sent from the browser.'],
      blockers: ['Production Epic access and real patient reads remain customer-authority gated.'],
    });
    scrollToId('workflow-result');

    try {
      const response = await billingFetch(`/ehr/epic-sandbox/token-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          base_url: epicBaseUrl.trim() || 'https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4',
          epic_client_id: epicClientIdReference.trim() || defaultEpicSandboxClientId,
          public_jwks_url: epicPublicJwksUrl.trim() || billingEpicRegistrationPackage.non_production_jwks_url,
          credential_secret_ref: epicCredentialSecretRef.trim() || defaultEpicSandboxSecretReference,
          scopes: [
            'system/Patient.read',
            'system/Coverage.read',
            'system/Encounter.read',
            'system/DocumentReference.read',
          ],
          run_live_resource_tests: runLiveResourceTests,
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as EpicSandboxTokenReceipt & {
        error?: string;
        next_step?: string;
        detail?: string;
        sanitized_error?: string;
      };
      if (!response.ok) {
        const message = payload.error ?? 'Epic backend OAuth token proof could not be completed.';
        const nextStep = payload.next_step ?? payload.detail ?? payload.sanitized_error ?? 'No token proof receipt recorded.';
        setEhrSandboxTokenStatus(`${message} ${nextStep}`.trim());
        setWorkflowRun({
          title: 'Epic backend OAuth token proof: setup needed',
          status: 'blocked',
          summary: message,
          receipt: nextStep,
          nextSteps: [
            'Confirm the Epic app client ID matches the registered sandbox app.',
            'Confirm the AWS Secrets Manager private JWK matches the public JWKS URL.',
            'Confirm the billing Lambda role can read only that secret reference.',
            'Rerun this token proof after the setup receipt is corrected.',
          ],
          evidence: ['The browser sent only non-secret configuration references.'],
          blockers: ['Authenticated Epic sandbox resource reads remain unavailable until the token proof passes.'],
        });
        return;
      }

      setEhrSandboxTokenReceipt(payload);
      setEhrSandboxTokenStatus(
        runLiveResourceTests
          ? `${payload.receipt_id}: Epic backend OAuth passed and live public sandbox FHIR reads were attempted. Raw token and FHIR payloads were discarded; only sanitized resource receipts were stored.`
          : `${payload.receipt_id}: Epic backend OAuth token exchange passed. Access token received and discarded; no raw token or assertion stored.`,
      );
      reportAction({
        title: runLiveResourceTests
          ? 'Epic live sandbox resource proof recorded'
          : 'Epic backend OAuth proof recorded',
        detail: payload.proves,
        artifact: `${payload.receipt_id}: token stored=${payload.token_exchange.access_token_stored}; assertion stored=${payload.client_assertion.raw_assertion_stored}`,
        targetId: 'ehr-sandbox-lab',
        tone: 'success',
      });
      setWorkflowRun({
        title: runLiveResourceTests
          ? 'Epic live sandbox resource proof: recorded'
          : 'Epic backend OAuth token proof: recorded',
        status: 'recorded',
        summary: payload.proves,
        receipt: `${payload.receipt_id}: ${payload.does_not_prove}`,
        nextSteps: [
          'Attach this token receipt to the partner proof packet.',
          runLiveResourceTests
            ? 'Use this sanitized sandbox resource receipt as Epic integration proof for partner review.'
            : 'Run the live sandbox resource proof to verify Patient, Coverage, Encounter, and DocumentReference read endpoints with Epic public fixture data.',
          'Keep production Epic reads and writes blocked until customer-approved production authority is recorded.',
        ],
        evidence: [
          `Token endpoint: ${payload.token_exchange.token_endpoint}`,
          `Assertion kid: ${payload.client_assertion.kid}`,
          `Access token received: ${payload.token_exchange.access_token_received ? 'yes' : 'no'}`,
          `Access token stored: ${payload.token_exchange.access_token_stored ? 'yes' : 'no'}`,
          `Raw assertion stored: ${payload.client_assertion.raw_assertion_stored ? 'yes' : 'no'}`,
          `Granted scope: ${payload.token_exchange.granted_scope}`,
          ...Object.entries(payload.live_resource_tests).map(
            ([testName, test]) =>
              `${readableAuthorityState(testName)}: ${readableAuthorityState(test.status)}${
                test.http_status ? `, HTTP ${test.http_status}` : ''
              }${typeof test.entry_count === 'number' ? `, entries ${test.entry_count}` : ''}`,
          ),
        ],
        blockers: [payload.live_external_action, payload.does_not_prove],
      });
      scrollToId('workflow-result');
    } catch {
      setEhrSandboxTokenStatus('Epic backend OAuth token proof could not reach the billing API from this browser session.');
      setWorkflowRun({
        title: 'Epic backend OAuth token proof: connection unavailable',
        status: 'blocked',
        summary: 'The billing API was not reachable for the Epic token proof run.',
        receipt: 'No receipt recorded.',
        nextSteps: ['Check billing-api.vaerionsystems.com health.', 'Rerun the token proof after API connectivity is restored.'],
        evidence: ['No PHI and no raw credential was sent.'],
        blockers: ['Token proof remains incomplete until a sanitized receipt is recorded.'],
      });
    }
  }

  async function runPaymentProofLab() {
    setPaymentProofStatus('Running synthetic claim-to-ERA-to-posting-to-deposit proof...');
    setWorkflowRun({
      title: 'Payment proof: running',
      status: 'running',
      summary:
        'VAERION is proving the internal billing payment path using synthetic non-PHI data. This does not move money.',
      receipt: 'Waiting for payment proof receipt...',
      nextSteps: [
        'Validate synthetic 837 claim readiness.',
        'Generate payer acknowledgement lifecycle.',
        'Parse 835 ERA, post payment, reconcile deposit, and block duplicate remittance.',
      ],
      evidence: ['No patient, member, bank-account, processor, or live payer payload is sent in this proof run.'],
      blockers: ['Real settlement, EFT, ACH, lockbox, refund, void, and reversal require bank or processor authority.'],
    });
    scrollToId('workflow-result');

    try {
      const response = await billingFetch(`/payments/end-to-end-proof/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scenario_id: 'synthetic-professional-claim-paid-with-adjustment',
          mode: 'synthetic_non_phi',
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as PaymentProofReceipt & {
        error?: string;
        next_step?: string;
      };
      if (!response.ok) {
        const message = payload.error ?? 'End-to-end payment proof could not be completed.';
        setPaymentProofStatus(`${message} ${payload.next_step ?? ''}`.trim());
        setWorkflowRun({
          title: 'Payment proof: authority gated',
          status: 'blocked',
          summary: message,
          receipt: payload.next_step ?? 'No receipt recorded.',
          nextSteps: [
            'Confirm the billing API payment proof endpoint is healthy.',
            'Rerun with synthetic non-PHI fixtures only.',
            'Do not claim money movement readiness from a failed proof run.',
          ],
          evidence: ['The console attempted a non-PHI payment proof run.'],
          blockers: ['Payment proof remains incomplete until a receipt is recorded.'],
        });
        return;
      }

      setPaymentProofReceipt(payload);
      setPaymentProofStatus(
        `${payload.receipt_id}: synthetic payment proof recorded. Real money movement requires financial authority until bank or processor authority exists.`,
      );
      reportAction({
        title: 'End-to-end payment proof recorded',
        detail: payload.proves,
        artifact: `${payload.receipt_id}: ${payload.does_not_prove}`,
        targetId: 'payment-proof-lab',
        tone: 'success',
      });
      setWorkflowRun({
        title: 'Payment proof: recorded',
        status: 'recorded',
        summary: payload.proves,
        receipt: `${payload.receipt_id}: ${payload.does_not_prove}`,
        nextSteps: [
          'Attach this payment proof to the partner packet.',
          'Record bank, EFT, ACH, lockbox, or processor authority when issued.',
          'Keep real settlement, refund, void, reversal, and deposit movement authority-gated until authority evidence is accepted.',
        ],
        evidence: [
          `Claim validation: ${payload.claim_validation.status}. ${payload.claim_validation.artifact}`,
          `Payer acknowledgement: ${payload.payer_acknowledgement.transaction_set} ${payload.payer_acknowledgement.status}.`,
          `835 ERA posting: ${payload.era.posting_status}; paid $${(payload.era.paid_cents / 100).toFixed(2)}.`,
          `Deposit reconciliation: ${payload.deposit_reconciliation.status}; variance $${(payload.deposit_reconciliation.variance_cents / 100).toFixed(2)}.`,
          `Duplicate remittance guard: ${payload.duplicate_remittance_guard.status}.`,
          `Refund/void/reversal: ${payload.refund_void_reversal.status}.`,
        ],
        blockers: [payload.live_external_action, payload.does_not_prove],
      });
      scrollToId('workflow-result');
    } catch {
      setPaymentProofStatus('Payment proof could not reach the billing API. No money movement was attempted.');
      setWorkflowRun({
        title: 'Payment proof: connection unavailable',
        status: 'blocked',
        summary: 'The billing API was not reachable for the synthetic payment proof run.',
        receipt: 'No receipt recorded.',
        nextSteps: ['Check billing-api.vaerionsystems.com health.', 'Rerun the payment proof lab after API connectivity is restored.'],
        evidence: ['No PHI and no banking payload was sent.'],
        blockers: ['Payment proof remains incomplete until a receipt is recorded.'],
      });
    }
  }

  async function runPaymentRailSandbox() {
    setPaymentRailSandboxStatus('Running VAERION-owned payment rail sandbox...');
    setWorkflowRun({
      title: 'Payment rail sandbox: running',
      status: 'running',
      summary:
        'VAERION is proving payment-rail file validation, bank-response emulation, de-identified replay, and accounting controls without moving money.',
      receipt: 'Waiting for payment rail sandbox receipt...',
      nextSteps: [
        'Validate synthetic ACH-style file controls.',
        'Emulate bank acceptance, settlement, return-window, and rejection receipts.',
        'Replay de-identified ERA and deposit fixtures through posting and reconciliation controls.',
      ],
      evidence: ['No patient, member, bank-account, routing-account, processor, or live payment instruction is sent.'],
      blockers: ['Actual settlement requires financial-institution, ODFI, processor, lockbox, or partner treasury authority.'],
    });
    scrollToId('workflow-result');

    try {
      const response = await billingFetch(`/payments/rail-sandbox/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scenario_id: 'synthetic-ach-era-deposit-replay',
          mode: 'synthetic_non_phi',
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as PaymentRailSandboxReceipt & {
        error?: string;
        next_step?: string;
      };
      if (!response.ok) {
        const message = payload.error ?? 'Payment rail sandbox could not be completed.';
        setPaymentRailSandboxStatus(`${message} ${payload.next_step ?? ''}`.trim());
        setWorkflowRun({
          title: 'Payment rail sandbox: authority gated',
          status: 'blocked',
          summary: message,
          receipt: payload.next_step ?? 'No receipt recorded.',
          nextSteps: [
            'Confirm the payment rail sandbox endpoint is healthy.',
            'Rerun with synthetic or de-identified non-PHI fixtures only.',
            'Do not claim settlement readiness from a failed sandbox run.',
          ],
          evidence: ['The console attempted a non-PHI payment rail sandbox run.'],
          blockers: ['Payment rail proof remains incomplete until a receipt is recorded.'],
        });
        return;
      }

      setPaymentRailSandboxReceipt(payload);
      setPaymentRailSandboxStatus(
        `${payload.receipt_id}: payment rail sandbox recorded. Actual settlement remains authority-gated until financial authority exists.`,
      );
      reportAction({
        title: 'Payment rail sandbox recorded',
        detail: payload.proves,
        artifact: `${payload.receipt_id}: ${payload.does_not_prove}`,
        targetId: 'payment-rail-sandbox-lab',
        tone: 'success',
      });
      setWorkflowRun({
        title: 'Payment rail sandbox: recorded',
        status: 'recorded',
        summary: payload.proves,
        receipt: `${payload.receipt_id}: ${payload.does_not_prove}`,
        nextSteps: [
          'Attach this rail sandbox receipt to the partner packet.',
          'Run de-identified partner ERA/deposit replay when customer exports are available.',
          'Keep real ACH, EFT, lockbox, refund, void, reversal, and deposit movement authority-gated until authority evidence is accepted.',
        ],
        evidence: [
          `NACHA-style file validation: ${payload.nacha_file_validation.status}. ${payload.nacha_file_validation.artifact}`,
          `Bank response emulator: ${payload.bank_response_emulator.status}. ${payload.bank_response_emulator.artifact}`,
          `Deposit ledger: ${payload.deposit_ledger.status}. ${payload.deposit_ledger.artifact}`,
          `Replay vault: ${payload.replay_vault.status}. ${payload.replay_vault.artifact}`,
          `Accounting controls: ${payload.accounting_controls.status}. ${payload.accounting_controls.artifact}`,
        ],
        blockers: [payload.live_external_action, payload.does_not_prove],
      });
      scrollToId('workflow-result');
    } catch {
      setPaymentRailSandboxStatus('Payment rail sandbox could not reach the billing API. No money movement was attempted.');
      setWorkflowRun({
        title: 'Payment rail sandbox: connection unavailable',
        status: 'blocked',
        summary: 'The billing API was not reachable for the payment rail sandbox run.',
        receipt: 'No receipt recorded.',
        nextSteps: ['Check billing-api.vaerionsystems.com health.', 'Rerun the payment rail sandbox after API connectivity is restored.'],
        evidence: ['No PHI and no banking payload was sent.'],
        blockers: ['Payment rail proof remains incomplete until a receipt is recorded.'],
      });
    }
  }

  async function runEnterpriseSurface(surfaceId: string) {
    const surface = enterpriseDeploymentSurfaces.find((item) => item.id === surfaceId);
    const title = surface?.title ?? surfaceId;
    setEnterpriseSurfaceStatus(`Running ${title} proof with non-PHI implementation context...`);
    reportAction({
      title: `${title} proof started`,
      detail: 'Recording a VAERION-owned enterprise deployment surface with non-PHI implementation context.',
      artifact: 'Waiting for enterprise deployment receipt.',
      targetId: `enterprise-surface-${surfaceId}`,
      tone: 'running',
    });
    setActiveSection(surface?.section ?? 'launch');
    setWorkflowRun({
      title: `${title}: running`,
      status: 'running',
      summary:
        'VAERION is running an enterprise deployment proof surface. This records what VAERION can prove internally and keeps live external actions gated.',
      receipt: 'Waiting for enterprise proof receipt...',
      nextSteps: ['Run non-PHI surface checks.', 'Attach receipt to partner packet.', 'Keep production cutover authority-gated until outside authority exists.'],
      evidence: ['No patient, member, bank-account, claim payload, clinical note, or live payer file is sent in this surface proof.'],
      blockers: ['Live payer, Epic, hospital PHI, appeal filing, and real funds actions require external authority proof.'],
    });
    scrollToId('workflow-result');

    try {
      const response = await billingFetch(`/enterprise-deployment/surface/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          surface_id: surfaceId,
          mode: 'non_phi_enterprise_surface_proof',
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as EnterpriseSurfaceReceipt & {
        error?: string;
        next_step?: string;
      };
      if (!response.ok) {
        const message = payload.error ?? `${title} proof could not be completed.`;
        setEnterpriseSurfaceStatus(`${message} ${payload.next_step ?? ''}`.trim());
        setWorkflowRun({
          title: `${title}: authority gated`,
          status: 'blocked',
          summary: message,
          receipt: payload.next_step ?? 'No receipt recorded.',
          nextSteps: ['Confirm the billing API enterprise surface endpoint is healthy.', 'Rerun with non-PHI implementation context only.'],
          evidence: ['The console attempted a non-PHI enterprise deployment surface proof.'],
          blockers: ['This surface remains awaiting receipt evidence. Live external actions stay authority-gated.'],
        });
        return;
      }

      setEnterpriseSurfaceReceipts((current) => ({ ...current, [surfaceId]: payload }));
      setEnterpriseSurfaceStatus(`${payload.receipt_id}: ${payload.plain_english_verdict}`);
      reportAction({
        title: `${payload.title} proof recorded`,
        detail: payload.proves,
        artifact: `${payload.receipt_id}: ${payload.does_not_prove}`,
        targetId: `enterprise-surface-${surfaceId}`,
        tone: 'success',
      });
      setWorkflowRun({
        title: `${payload.title}: recorded`,
        status: 'recorded',
        summary: payload.plain_english_verdict,
        receipt: `${payload.receipt_id}: ${payload.live_external_action}`,
        nextSteps: [
          'Attach this receipt to the partner proof packet.',
          'Use the listed artifacts during partner diligence and hospital implementation review.',
          'Do not enable live production action until every remaining external authority item is recorded.',
        ],
        evidence: payload.checks.map((check) => `${check.label}: ${check.status}. ${check.evidence}`),
        blockers: [...payload.remaining_external_authority, payload.does_not_prove],
      });
      scrollToId(`enterprise-surface-${surfaceId}`, 'center');
    } catch {
      setEnterpriseSurfaceStatus(`${title} proof could not reach the billing API. Live external actions remain authority-gated.`);
      setWorkflowRun({
        title: `${title}: connection unavailable`,
        status: 'blocked',
        summary: 'The billing API was not reachable for the enterprise surface proof run.',
        receipt: 'No receipt recorded.',
        nextSteps: ['Check billing-api.vaerionsystems.com health.', 'Rerun the enterprise proof after API connectivity is restored.'],
        evidence: ['No PHI, banking payload, or live payer payload was sent.'],
        blockers: ['Surface proof remains incomplete until a receipt is recorded.'],
      });
    }
  }

  async function fetchLaunchDossier() {
    const response = await billingFetch(`/launch-dossier`, {
      headers: {
      },
    });
    const payload = (await response.json().catch(() => ({}))) as LaunchDossier & {
      error?: string;
      next_step?: string;
    };
    if (!response.ok) {
      throw new Error(`${payload.error ?? 'Launch dossier could not be loaded.'} ${payload.next_step ?? ''}`.trim());
    }
    return payload;
  }

  async function loadLaunchDossier() {
    setLaunchDossierStatus('Refreshing server-side enterprise launch dossier...');
    reportAction({
      title: 'Refreshing launch dossier',
      detail: 'Reading the server-side proof vault for saved proof packets and enterprise deployment receipts.',
      artifact: 'Waiting for AWS proof-vault response.',
      targetId: 'launch-dossier',
      tone: 'running',
    });
    try {
      const payload = await fetchLaunchDossier();
      setLaunchDossier(payload);
      setLaunchDossierStatus(
        `${payload.readiness.proven_surface_count} of ${payload.readiness.required_surface_count} VAERION-owned enterprise evidence surfaces are recorded. ${readableAuthorityState(payload.live_external_action)}`,
      );
      reportAction({
        title: 'Launch dossier refreshed',
        detail: `${payload.readiness.proven_surface_count} of ${payload.readiness.required_surface_count} VAERION-owned enterprise evidence surfaces are recorded in the server-side dossier.`,
        artifact: `${payload.readiness.proof_packet_count} server proof packet(s); ${readableAuthorityState(payload.live_external_action)}`,
        targetId: 'launch-dossier',
        tone: 'success',
      });
    } catch (error) {
      setLaunchDossierStatus(
        error instanceof Error
          ? error.message
          : 'The billing API was not reachable, so the server-side launch dossier could not be refreshed. Browser-local proof is not enough for partner diligence.',
      );
    }
  }

  async function fetchEnterpriseReadiness() {
    const response = await billingFetch(`/enterprise-readiness`, {
      headers: {
      },
    });
    const payload = (await response.json().catch(() => ({}))) as EnterpriseReadiness & {
      error?: string;
      next_step?: string;
    };
    if (!response.ok) {
      throw new Error(`${payload.error ?? 'Enterprise readiness could not be loaded.'} ${payload.next_step ?? ''}`.trim());
    }
    return payload;
  }

  async function loadEnterpriseReadiness() {
    setEnterpriseReadinessStatus('Refreshing the AWS-backed enterprise readiness contract...');
    reportAction({
      title: 'Refreshing enterprise readiness',
      detail: 'Reading the machine-readable deployment truth table from the billing API.',
      artifact: 'Waiting for enterprise readiness response.',
      targetId: 'enterprise-readiness',
      tone: 'running',
    });
    try {
      const payload = await fetchEnterpriseReadiness();
      setEnterpriseReadiness(payload);
      setEnterpriseReadinessStatus(
        `${payload.status.vaerion_owned_surface_count} of ${payload.status.required_vaerion_owned_surface_count} VAERION-owned proof surfaces have server receipts. Live cutover is ${readableAuthorityState(
          payload.status.live_production_cutover,
        )}.`,
      );
      reportAction({
        title: 'Enterprise readiness refreshed',
        detail: payload.designed_purpose,
        artifact: `${payload.deployment_modes.length} deployment modes, ${payload.evidence_surfaces.length} evidence surfaces, ${payload.proof_endpoints.length} proof endpoints.`,
        targetId: 'enterprise-readiness',
        tone: 'success',
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'The billing API was not reachable, so the enterprise readiness contract could not be refreshed.';
      setEnterpriseReadinessStatus(message);
      reportAction({
        title: 'Enterprise readiness unavailable',
        detail: message,
        artifact: 'Keep launch controls authority-gated until the readiness contract can be read.',
        targetId: 'enterprise-readiness',
        tone: 'blocked',
      });
    }
  }

  async function fetchEnterpriseProofVerifier() {
    const response = await billingFetch(`/enterprise-proof-verifier`, {
      headers: {
      },
    });
    const payload = (await response.json().catch(() => ({}))) as EnterpriseProofVerifier & {
      error?: string;
      next_step?: string;
    };
    if (!response.ok) {
      throw new Error(`${payload.error ?? 'Enterprise proof verifier could not be loaded.'} ${payload.next_step ?? ''}`.trim());
    }
    return payload;
  }

  async function loadEnterpriseProofVerifier() {
    setEnterpriseProofVerifierStatus('Refreshing the enterprise proof verifier from the AWS billing runtime...');
    reportAction({
      title: 'Refreshing enterprise proof verifier',
      detail: 'Joining proof packets, enterprise receipts, counterparty evidence references, and production boundaries into one inspection surface.',
      artifact: 'Waiting for enterprise proof verifier response.',
      targetId: 'enterprise-proof-verifier',
      tone: 'running',
    });
    try {
      const payload = await fetchEnterpriseProofVerifier();
      setEnterpriseProofVerifier(payload);
      setEnterpriseProofVerifierStatus(
        `${payload.verifier_name}: ${payload.proof_domains.length} proof domains inspectable. Production authority is ${readableAuthorityState(
          payload.verdict.production_authority,
        )}.`,
      );
      reportAction({
        title: 'Enterprise proof verifier refreshed',
        detail: payload.verdict.plain_english,
        artifact: `${payload.proof_counts.proof_packets} proof packet(s), ${payload.proof_counts.enterprise_surface_receipts} enterprise receipt(s), ${payload.proof_counts.authority_evidence} authority evidence reference(s).`,
        targetId: 'enterprise-proof-verifier',
        tone: 'success',
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'The enterprise proof verifier could not be reached from this browser session.';
      setEnterpriseProofVerifierStatus(message);
      reportAction({
        title: 'Enterprise proof verifier unavailable',
        detail: message,
        artifact: 'Keep the verifier authority-gated until AWS billing runtime proof can be read.',
        targetId: 'enterprise-proof-verifier',
        tone: 'blocked',
      });
    }
  }

  async function recordAuthorityEvidence(event?: FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    setAuthorityEvidenceStatus('Recording counterparty authority evidence reference without PHI, secrets, or raw documents...');
    reportAction({
      title: 'Recording authority evidence',
      detail:
        'The browser is sending only evidence metadata, an artifact reference, and a SHA-256 hash. No PHI, bank payload, private key, password, or raw document is sent.',
      artifact: authorityEvidenceReference,
      targetId: 'enterprise-proof-verifier',
      tone: 'running',
    });
    try {
      const response = await billingFetch(`/authority-evidence/intake`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          evidence_type: authorityEvidenceType,
          issuer: authorityEvidenceIssuer.trim(),
          authority_scope: authorityEvidenceScope.trim(),
          artifact_reference: authorityEvidenceReference.trim(),
          artifact_sha256: authorityEvidenceHash.trim().toLowerCase(),
          status: 'recorded_pending_review',
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as AuthorityEvidenceReceipt & {
        error?: string;
        next_step?: string;
      };
      if (!response.ok) {
        const message = payload.error ?? 'Authority evidence reference could not be recorded.';
        setAuthorityEvidenceStatus(`${message} ${payload.next_step ?? ''}`.trim());
        reportAction({
          title: 'Authority evidence not recorded',
          detail: `${message} ${payload.next_step ?? ''}`.trim(),
          artifact: 'No production authority changed.',
          targetId: 'enterprise-proof-verifier',
          tone: 'blocked',
        });
        return;
      }

      setAuthorityEvidenceReceipts((current) => [
        payload,
        ...current.filter((item) => item.receipt_id !== payload.receipt_id),
      ]);
      setAuthorityEvidenceStatus(
        `${payload.receipt_id}: ${readableAuthorityState(payload.lifecycle_state)}. Final cutover review eligible: ${
          payload.final_cutover_review_eligible ? 'yes' : 'no'
        }. Production auto-activation: no. Raw PHI stored: ${payload.raw_phi_stored ? 'yes' : 'no'}.`,
      );
      reportAction({
        title: 'Authority evidence reference recorded',
        detail:
          'A counterparty authority reference was recorded for review. It does not unlock production until Compliance/Admin review accepts it.',
        artifact: `${payload.receipt_id}: ${payload.artifact_reference}`,
        targetId: 'enterprise-proof-verifier',
        tone: 'success',
      });
    } catch {
      setAuthorityEvidenceStatus('Authority evidence reference could not reach the billing API.');
      reportAction({
        title: 'Authority evidence API unavailable',
        detail: 'The billing API could not be reached. No production authority changed.',
        artifact: 'No receipt recorded.',
        targetId: 'enterprise-proof-verifier',
        tone: 'blocked',
      });
    }
  }

  async function reviewAuthorityEvidence(
    receipt: AuthorityEvidenceReceipt,
    decision: 'reviewed' | 'accepted' | 'rejected' | 'expired',
  ) {
    setAuthorityEvidenceStatus(`Recording ${decision} lifecycle decision for ${receipt.receipt_id}...`);
    reportAction({
      title: 'Reviewing authority evidence',
      detail:
        'Compliance/Admin review is updating the authority evidence lifecycle without sending PHI, secrets, tokens, or raw documents.',
      artifact: `${receipt.receipt_id} · ${decision}`,
      targetId: 'authority-evidence-intake',
      tone: 'running',
    });
    try {
      const response = await billingFetch(`/authority-evidence/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          receipt_id: receipt.receipt_id,
          decision,
          rationale:
            decision === 'accepted'
              ? 'Issuer, authority scope, artifact reference, SHA-256 hash, and launch boundary were reviewed for final human cutover eligibility.'
              : 'Authority evidence lifecycle reviewed for partner diligence; production activation remains gated by final cutover review.',
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as AuthorityEvidenceReceipt & {
        error?: string;
        next_step?: string;
      };
      if (!response.ok) {
        const message = payload.error ?? 'Authority evidence lifecycle review could not be recorded.';
        setAuthorityEvidenceStatus(`${message} ${payload.next_step ?? ''}`.trim());
        reportAction({
          title: 'Authority evidence review failed',
          detail: `${message} ${payload.next_step ?? ''}`.trim(),
          artifact: 'No production authority changed.',
          targetId: 'authority-evidence-intake',
          tone: 'blocked',
        });
        return;
      }

      setAuthorityEvidenceReceipts((current) =>
        current.map((item) => (item.receipt_id === payload.receipt_id ? { ...item, ...payload } : item)),
      );
      setAuthorityEvidenceStatus(
        `${payload.receipt_id}: ${readableAuthorityState(payload.lifecycle_state)} by ${payload.reviewer ?? 'reviewer'}. Final cutover review eligible: ${
          payload.final_cutover_review_eligible ? 'yes' : 'no'
        }. Production auto-activation: no.`,
      );
      reportAction({
        title: 'Authority evidence lifecycle updated',
        detail:
          payload.lifecycle_state === 'accepted'
            ? 'This evidence is eligible for final human cutover review for the exact accepted scope only.'
            : 'This evidence review outcome does not allow final cutover yet.',
        artifact: `${payload.review_receipt_id ?? payload.receipt_id}: ${payload.lifecycle_state}`,
        targetId: 'enterprise-proof-verifier',
        tone: payload.lifecycle_state === 'accepted' ? 'success' : 'blocked',
      });
      void loadEnterpriseProofVerifier();
    } catch {
      setAuthorityEvidenceStatus('Authority evidence review endpoint could not be reached.');
      reportAction({
        title: 'Authority evidence review API unavailable',
        detail: 'The billing API could not be reached. No production authority changed.',
        artifact: 'No lifecycle update recorded.',
        targetId: 'authority-evidence-intake',
        tone: 'blocked',
      });
    }
  }

  async function runNextMissingSurface() {
    const missingFromDossier = launchDossier?.readiness.missing_surface_ids ?? [];
    const nextMissingId =
      missingFromDossier[0] ?? enterpriseDeploymentSurfaces.find((surface) => !enterpriseSurfaceReceipts[surface.id])?.id;
    if (!nextMissingId) {
      setLaunchDossierStatus('Every VAERION-owned enterprise proof surface visible in the console already has a browser receipt. Refresh the dossier for server-side confirmation.');
      return;
    }
    await runEnterpriseSurface(nextMissingId);
    setLaunchDossierStatus(
      `Ran ${nextMissingId} proof. Refresh the launch dossier to confirm the receipt was recorded in the server-side proof vault.`,
    );
  }

  async function copyTextToClipboard(text: string, successMessage: string, fallbackMessage: string, targetId = 'developer-docs') {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setCopyStatus(successMessage);
        setManualCopyText('');
        return true;
      }
      throw new Error('Clipboard API unavailable');
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (!copied) {
        setCopyStatus(fallbackMessage);
        setManualCopyText(text);
        reportAction({
          title: 'Manual copy ready',
          detail: fallbackMessage,
          artifact: text,
          targetId,
          tone: 'blocked',
        });
        return false;
      }
      setCopyStatus(successMessage);
      setManualCopyText('');
      return true;
    }
  }

  async function copyImplementationSurfaces() {
    const text = implementationApi.join('\n');
    const copied = await copyTextToClipboard(
      text,
      `Copied all ${implementationApi.length} implementation API surfaces.`,
      `Browser clipboard permission was unavailable. All ${implementationApi.length} API surfaces remain listed below for manual copy.`,
      'developer-docs',
    );
    if (copied) {
      reportAction({
        title: 'API surfaces copied',
        detail: 'The implementation endpoint list was copied to your clipboard.',
        artifact: `${implementationApi.length} endpoint surface(s) copied.`,
        targetId: 'developer-docs',
        tone: 'success',
      });
    }
  }

  async function copyImplementationEndpoint(endpoint: string) {
    const copied = await copyTextToClipboard(
      endpoint,
      `Copied ${endpoint}.`,
      `Browser clipboard permission was unavailable. ${endpoint} is visible in the API row for manual copy.`,
      'developer-docs',
    );
    if (copied) {
      reportAction({
        title: 'API endpoint copied',
        detail: `${endpoint} was copied to your clipboard.`,
        artifact: endpoint,
        targetId: 'developer-docs',
        tone: 'success',
      });
    }
  }

  async function copyEpicRegistrationManifest() {
    const registrationPackage = ehrSandboxReceipt?.epic_registration_package ?? billingEpicRegistrationPackage;
    const manifest = {
      application_name: registrationPackage.application_name,
      application_audience: registrationPackage.application_audience,
      use_case: registrationPackage.use_case,
      public_documentation_url: registrationPackage.public_documentation_url,
      non_production_jwks_url: epicPublicJwksUrl.trim() || registrationPackage.non_production_jwks_url,
      production_jwks_url: registrationPackage.production_jwks_url,
      requested_incoming_apis: registrationPackage.requested_fhir_r4_capabilities,
      sandbox_fhir_base_url: epicBaseUrl.trim() || 'https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4',
      sandbox_client_id_reference: epicClientIdReference.trim() || defaultEpicSandboxClientId,
      aws_private_jwk_secret_reference: epicCredentialSecretRef.trim() || defaultEpicSandboxSecretReference,
      private_key_handling: 'Private JWK stays in AWS Secrets Manager. Do not paste private keys, tokens, secrets, auth codes, passwords, or PHI into Epic notes or VAERION UI.',
      proof_boundary:
        'This manifest supports Epic sandbox backend OAuth proof. Production Epic access still requires Epic/customer approval and production credential evidence.',
    };
    const copied = await copyTextToClipboard(
      JSON.stringify(manifest, null, 2),
      'Copied Epic app registration manifest.',
      'Browser clipboard permission was unavailable. The Epic registration manifest is available below for manual copy.',
      'ehr-sandbox-lab',
    );
    if (copied) {
      reportAction({
        title: 'Epic registration manifest copied',
        detail: 'The Epic app setup manifest was copied with the sandbox client ID, public JWKS URL, requested FHIR APIs, and AWS private-key reference.',
        artifact: `${manifest.sandbox_client_id_reference} · ${manifest.non_production_jwks_url}`,
        targetId: 'ehr-sandbox-lab',
        tone: 'success',
      });
    }
  }

  function openCutoverReview(carrier: string, blockers: string[]) {
    setWorkflowRun({
      title: `${carrier} production cutover review`,
      status: 'blocked',
      summary:
        'VAERION can prepare the direct payer route and certification packet, but live transmission stays authority-gated until the payer issues acceptance evidence.',
      receipt: 'No live cutover request was submitted. This is a requirements review only.',
      nextSteps: [
        'Attach companion-guide version and payer ID.',
        'Generate non-PHI 837 test packet and acknowledgement lifecycle proof.',
        'Record carrier-issued test-cycle acceptance and production endpoint authority.',
      ],
      evidence: [
        'Carrier matrix selected the exact route instead of claiming generic payer certainty.',
        '999, 277CA, and 835 lifecycle proof is available as internal evidence.',
      ],
      blockers,
    });
    scrollToId('workflow-result');
  }

  function buildPartnerProofPacket(
    dossierOverride?: LaunchDossier | null,
    readinessOverride?: EnterpriseReadiness | null,
  ): PartnerProofPacket {
    const packetId = `vbp-${Date.now()}`;
    const generatedAt = new Date().toISOString();
    const dossier = dossierOverride ?? launchDossier;
    const readiness = readinessOverride ?? enterpriseReadiness;
    const enterpriseReceipts = dedupeEnterpriseReceipts(
      Object.values(enterpriseSurfaceReceipts),
      dossier?.enterprise_surface_receipts ?? [],
    );
    const evidenceDossier = buildEvidenceDossier({
      packetId,
      generatedAt,
      launchDossier: dossier ?? null,
      enterpriseReadiness: readiness ?? null,
      enterpriseReceipts,
      paymentProofReceipt,
      paymentRailSandboxReceipt,
      ehrSandboxReceipt,
      ehrSandboxTokenReceipt,
      developerOpsReceipt,
      companionGuideReceipt,
      replayImportReceipt,
      proofReviewReceipt,
      enterpriseProofVerifier,
      authorityEvidenceReceipts,
      savedPackets: dossier?.proof_packets ?? savedPackets,
    });
    const verificationMatrix = evidenceDossier.map((item) => ({
      domain: item.surface,
      method: item.method,
      artifact: item.artifact,
      result: item.result,
      receipt: item.receipt_id,
      production_authority: item.authority_limit,
    }));
    return {
      packet_type: 'partner_launch_packet',
      packet_id: packetId,
      generated_at: generatedAt,
      generated_by: 'partner_billing_console',
      live_external_action: 'blocked_until_authority_proof',
      storage: {
        current_location: `Browser local storage key ${proofPacketStorageKey}`,
        durable_receipt: 'AWS proof receipt is created separately and shown in the packet viewer when recording succeeds.',
        durable_packet_artifact:
          'The full non-PHI packet can be saved to the isolated AWS billing proof vault and downloaded from this viewer.',
      },
      proof_standard: {
        purpose:
          'Provide executive, technical, compliance, and implementation reviewers with receipt-level evidence for VAERION-owned billing readiness.',
        evidence_scope:
          'Non-PHI launch dossier, server proof-vault index, enterprise deployment surface receipts, counterparty approval packets, synthetic payment/EHR proofs, route matrix, implementation API, and action ledger.',
        production_authority_boundary:
          'The packet proves VAERION-controlled readiness only. External payer, Epic/customer, hospital, and financial authority must still be recorded before live production traffic.',
      },
      launch_dossier_snapshot: dossier
        ? {
            tenant: dossier.tenant,
            generated_at: dossier.generated_at,
            verdict: dossier.verdict,
            vaerion_owned_status: dossier.readiness.vaerion_owned_status,
            production_cutover_status: readableAuthorityState(dossier.readiness.production_cutover_status),
            proof_packet_count: dossier.readiness.proof_packet_count,
            enterprise_surface_receipt_count: dossier.readiness.enterprise_surface_receipt_count,
            required_surface_count: dossier.readiness.required_surface_count,
            proven_surface_count: dossier.readiness.proven_surface_count,
            remaining_surface_ids: dossier.readiness.missing_surface_ids,
            remaining_external_authority: dossier.remaining_external_authority,
            live_external_action: readableAuthorityState(dossier.live_external_action),
          }
        : undefined,
      enterprise_readiness_snapshot: readiness
        ? {
            generated_at: readiness.generated_at,
            product_boundary: readiness.product_boundary,
            designed_purpose: readiness.designed_purpose,
            vaerion_owned_readiness: readableAuthorityState(readiness.status.vaerion_owned_readiness),
            live_production_cutover: readableAuthorityState(readiness.status.live_production_cutover),
            deployment_modes: readiness.deployment_modes,
            evidence_surface_count: readiness.status.required_vaerion_owned_surface_count,
            recorded_surface_count: readiness.status.vaerion_owned_surface_count,
            proof_packet_count: readiness.status.proof_packet_count,
            live_production_requirements: readiness.live_production_requirements.map((item) => ({
              requirement: item.requirement,
              owner: item.owner,
              authority_boundary: item.authority_boundary,
            })),
          }
        : undefined,
      evidence_dossier: evidenceDossier,
      verification_matrix: verificationMatrix,
      server_vault: {
        packet_count: dossier?.readiness.proof_packet_count ?? savedPackets.length,
        saved_packet_ids: (dossier?.proof_packets ?? savedPackets).map((packet) => packet.packet_id),
        packet_artifact_state:
          dossier && dossier.readiness.proof_packet_count > 0
            ? 'Server-side proof packet records are present in the AWS billing proof vault.'
            : 'No server-side packet record was loaded into this browser session yet. Generate and save the packet, then refresh the launch dossier.',
      },
      partner_review_checklist: [
        'Open the Evidence dossier tab and verify every receipt ID, source, artifact, method, result, and authority boundary.',
        'Open Approval Packets and verify the payer, Epic/customer, hospital, and financial authority packets before sending them to counterparty-controlled portals.',
        'Review server proof-vault packet IDs and confirm at least one packet exists outside browser local storage.',
        'Review Epic/FHIR and payment proof receipts if the meeting requires EHR or payment lifecycle proof.',
        'Attach payer, hospital, Epic/customer, and financial authority receipts when those counterparties issue them.',
        'Keep live production traffic authority-gated until counterparty evidence is accepted.',
      ],
      summary:
        'Non-PHI partner launch packet covering VAERION-owned billing readiness, implementation surfaces, external authority gates, and proof receipts.',
      launch_gates: launchGates.map((gate) => ({
        label: gate.label,
        state: gate.state,
        proof: gate.proof,
      })),
      workstreams: workstreams.map((stream) => ({
        title: stream.title,
        owner: stream.owner,
        summary: stream.summary,
        metrics: stream.metrics.map((metric) => ({ label: metric.label, value: metric.value })),
        authority_gated_steps: stream.steps
          .filter((step) => step.tone === 'blocked')
          .map((step) => `${step.label}: ${step.detail}`),
      })),
      carrier_routes: carrierCertificationMatrix.map((carrier) => ({
        carrier: carrier.carrier,
        transactions: carrier.transactions,
        certification_receipt: carrier.certificationReceipt,
        production_gate: carrier.productionGate,
        does_not_prove: carrier.doesNotProve,
      })),
      authority_workarounds: authorityWorkarounds.map((workaround) => ({
        authority: workaround.authority,
        cannot_bypass: workaround.cannotBypass,
        vaerion_owned_path: workaround.vaerionOwnedPath,
        proof_receipt: workaround.proofReceipt,
      })),
      approval_packets: Object.values(preparedApprovalPackets).map((packet) => ({
        packet_id: packet.packet_id,
        packet_type: packet.id,
        title: packet.title,
        counterparty: packet.counterparty,
        status: packet.status,
        generated_at: packet.generated_at,
        readiness_receipt_id: packet.readiness_receipt_id,
        live_external_action: packet.live_external_action,
        evidence_needed: packet.evidenceNeeded,
        vaerion_prefill: packet.vaerionPrefill,
        external_fields: packet.externalFields,
        next_step: packet.nextStep,
      })),
      payment_proof: paymentProofReceipt
        ? {
            receipt_id: paymentProofReceipt.receipt_id,
            scenario_id: paymentProofReceipt.scenario_id,
            claim_validation: paymentProofReceipt.claim_validation.status,
            acknowledgement: `${paymentProofReceipt.payer_acknowledgement.transaction_set} ${paymentProofReceipt.payer_acknowledgement.status}`,
            era_posting: paymentProofReceipt.era.posting_status,
            deposit_reconciliation: paymentProofReceipt.deposit_reconciliation.status,
            duplicate_remittance_guard: paymentProofReceipt.duplicate_remittance_guard.status,
            refund_void_reversal: paymentProofReceipt.refund_void_reversal.status,
            proves: paymentProofReceipt.proves,
            does_not_prove: paymentProofReceipt.does_not_prove,
          }
        : undefined,
      payment_rail_sandbox: paymentRailSandboxReceipt
        ? {
            receipt_id: paymentRailSandboxReceipt.receipt_id,
            scenario_id: paymentRailSandboxReceipt.scenario_id,
            nacha_file_validation: paymentRailSandboxReceipt.nacha_file_validation.status,
            bank_response_emulator: paymentRailSandboxReceipt.bank_response_emulator.status,
            deposit_ledger: paymentRailSandboxReceipt.deposit_ledger.status,
            replay_vault: paymentRailSandboxReceipt.replay_vault.status,
            accounting_controls: paymentRailSandboxReceipt.accounting_controls.status,
            proves: paymentRailSandboxReceipt.proves,
            does_not_prove: paymentRailSandboxReceipt.does_not_prove,
        }
        : undefined,
      enterprise_proof_verifier: enterpriseProofVerifier
        ? {
            verifier_name: enterpriseProofVerifier.verifier_name,
            generated_at: enterpriseProofVerifier.generated_at,
            verdict: enterpriseProofVerifier.verdict.plain_english,
            proof_domain_count: enterpriseProofVerifier.proof_domains.length,
            proof_counts: enterpriseProofVerifier.proof_counts,
            boundary: enterpriseProofVerifier.boundary,
          }
        : undefined,
      production_activation_tracks: enterpriseProofVerifier?.production_activation_tracks,
      authority_evidence_receipts: authorityEvidenceReceipts.map((receipt) => ({
        receipt_id: receipt.receipt_id,
        evidence_type: receipt.evidence_type,
        lifecycle_state: receipt.lifecycle_state,
        issuer: receipt.issuer,
        authority_scope: receipt.authority_scope,
        artifact_reference: receipt.artifact_reference,
        final_cutover_review_eligible: Boolean(receipt.final_cutover_review_eligible),
        production_authority_unlocked: false,
        raw_phi_stored: receipt.raw_phi_stored,
      })),
      enterprise_surface_receipts: enterpriseReceipts.map((receipt) => ({
        surface_id: receipt.surface_id,
        title: receipt.title,
        receipt_id: receipt.receipt_id,
        verdict: receipt.verdict,
        proves: receipt.proves,
        does_not_prove: receipt.does_not_prove,
        remaining_external_authority: receipt.remaining_external_authority,
      })),
      proof_events: proofEvents,
      implementation_api: implementationApi,
      external_authority_still_required: [
        'Carrier-issued trading partner approval for each exact production payer route.',
        'Epic or customer-approved production credentials before real EHR reads or writes.',
        'Bank, processor, or partner treasury authority before money movement.',
        'Hospital go-live authorization before PHI-bearing production traffic.',
      ],
      guardrails: [
        'No live claim submission is unlocked by this packet.',
        'No Epic production write is unlocked by this packet.',
        'No payment movement is unlocked by this packet.',
        'No payer acceptance guarantee is claimed.',
        'Packet content is intentionally non-PHI.',
      ],
    };
  }

  async function recordPacketReceipt(packet: PartnerProofPacket) {
    setPacketReceipt('Recording non-PHI AWS proof receipt for this packet...');
    try {
      const response = await billingFetch(`/proof-receipts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          proof_type: 'partner_launch_packet',
          object_type: 'billing_partner_launch',
          object_id: packet.packet_id,
          summary:
            'Generated VAERION partner proof packet with launch gates, route matrix, EHR/FHIR, claim, payment, denial, audit, and external-authority boundaries.',
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        receipt_id?: string;
        error?: string;
        created_at?: string;
        live_external_action?: string;
      };
      if (!response.ok) {
        setPacketReceipt(payload.error ?? 'AWS proof receipt could not be recorded. The local packet is still available.');
        return;
      }
      setPacketReceipt(
        `${payload.receipt_id}: recorded at ${payload.created_at ?? 'current time'}. ${readableAuthorityState(payload.live_external_action ?? 'Live external action remains authority-gated.')}`,
      );
    } catch {
      setPacketReceipt('AWS proof receipt could not be reached. The local packet is still available.');
    }
  }

  async function saveProofPacketToVault(packet: PartnerProofPacket) {
    setPacketVaultStatus('Saving full non-PHI packet to the AWS billing proof vault...');
    try {
      const response = await billingFetch(`/proof-packets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          packet,
          summary: 'Stored full non-PHI partner billing launch packet in the isolated billing proof vault.',
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        packet_id?: string;
        packet_path?: string;
        error?: string;
        created_at?: string;
        live_external_action?: string;
      };
      if (!response.ok) {
        setPacketVaultStatus(payload.error ?? 'Proof vault save failed. Browser download remains available.');
        return;
      }
      setPacketVaultStatus(
        `${payload.packet_id}: saved to AWS proof vault at ${payload.created_at ?? 'current time'}. ${readableAuthorityState(payload.live_external_action ?? 'Live external action remains authority-gated.')}`,
      );
      setSavedPackets((current) => [
        {
          packet_id: payload.packet_id ?? packet.packet_id,
          packet_type: packet.packet_type,
          created_at: payload.created_at ?? packet.generated_at,
          actor_id: 'partner_billing_console',
          request_id: `proof-vault-${packet.packet_id}`,
          summary: packet.summary,
          live_external_action: payload.live_external_action ?? 'blocked_until_authority_proof',
          download_path: `/v1/proof-packets/${payload.packet_id ?? packet.packet_id}`,
        },
        ...current.filter((item) => item.packet_id !== (payload.packet_id ?? packet.packet_id)),
      ]);
    } catch {
      setPacketVaultStatus('Proof vault could not be reached. Browser download remains available.');
    }
  }

  async function loadProofPacketVault() {
    setPacketVaultStatus('Loading AWS proof vault index...');
    try {
      const response = await billingFetch(`/proof-packets`, {
        headers: {
        },
      });
      const payload = (await response.json().catch(() => ({}))) as {
        packets?: SavedProofPacketSummary[];
        error?: string;
      };
      if (!response.ok) {
        setPacketVaultStatus(payload.error ?? 'Proof vault index could not be loaded.');
        return;
      }
      setSavedPackets(payload.packets ?? []);
      setPacketVaultStatus(`Loaded ${payload.packets?.length ?? 0} server-side proof packet record(s).`);
    } catch {
      setPacketVaultStatus('Proof vault index could not be reached.');
    }
  }

  async function openSavedProofPacket(packetId: string) {
    setPacketVaultStatus(`Opening saved proof packet ${packetId}...`);
    try {
      const response = await billingFetch(`/proof-packets/${encodeURIComponent(packetId)}`, {
        headers: {
        },
      });
      const payload = (await response.json().catch(() => ({}))) as {
        packet?: PartnerProofPacket;
        error?: string;
      };
      if (!response.ok || !payload.packet) {
        setPacketVaultStatus(payload.error ?? 'Saved proof packet could not be opened.');
        return;
      }
      if (!hasModernProofPacketSchema(payload.packet)) {
        const regeneratedPacket = buildPartnerProofPacket(launchDossier);
        storeAndOpenPacket(regeneratedPacket);
        setPacketVaultStatus(
          `Opened legacy server packet ${packetId}; regenerated it into the current evidence-dossier schema so partner reviewers see receipt-level proof.`,
        );
        void recordPacketReceipt(regeneratedPacket);
        void saveProofPacketToVault(regeneratedPacket);
        return;
      }
      storeAndOpenPacket(payload.packet);
      setPacketVaultStatus(`Opened server-side proof packet ${packetId}.`);
    } catch {
      setPacketVaultStatus('Saved proof packet could not be reached.');
    }
  }

  function storeAndOpenPacket(packet: PartnerProofPacket) {
    localStorage.setItem(proofPacketStorageKey, JSON.stringify(packet, null, 2));
    setProofPacket(packet);
    setPacketViewMode('plain');
    setPacketOpen(true);
    reportAction({
      title: 'Partner proof packet opened',
      detail: 'The packet viewer is open with plain-English proof, evidence ledger, raw JSON, server-vault save, and download controls.',
      artifact: `${packet.packet_id} stored in browser local storage under ${proofPacketStorageKey}.`,
      targetId: 'partner-proof-packet-viewer',
      tone: 'success',
    });
    scrollToId('partner-proof-packet-viewer');
  }

  async function generatePartnerProofPacket({ download = false } = {}) {
    let dossierForPacket = launchDossier;
    let readinessForPacket = enterpriseReadiness;
    if (!dossierForPacket) {
      try {
        setLaunchDossierStatus('Refreshing server-side launch dossier before packet generation...');
        dossierForPacket = await fetchLaunchDossier();
        setLaunchDossier(dossierForPacket);
        setLaunchDossierStatus(
          `${dossierForPacket.readiness.proven_surface_count} of ${dossierForPacket.readiness.required_surface_count} VAERION-owned enterprise evidence surfaces are recorded. ${readableAuthorityState(dossierForPacket.live_external_action)}`,
        );
      } catch {
        setLaunchDossierStatus(
          'Packet generated with browser-local evidence because the server-side dossier could not be refreshed in this session.',
        );
      }
    }
    if (!readinessForPacket) {
      try {
        setEnterpriseReadinessStatus('Refreshing enterprise readiness contract before packet generation...');
        readinessForPacket = await fetchEnterpriseReadiness();
        setEnterpriseReadiness(readinessForPacket);
        setEnterpriseReadinessStatus(
          `${readinessForPacket.status.vaerion_owned_surface_count} of ${readinessForPacket.status.required_vaerion_owned_surface_count} VAERION-owned proof surfaces have server receipts. Live cutover is ${readableAuthorityState(
            readinessForPacket.status.live_production_cutover,
          )}.`,
        );
      } catch {
        setEnterpriseReadinessStatus(
          'Packet generated without the enterprise readiness contract because the billing API could not be refreshed in this session.',
        );
      }
    }
    const packet = buildPartnerProofPacket(dossierForPacket, readinessForPacket);
    storeAndOpenPacket(packet);
    void recordPacketReceipt(packet);
    void saveProofPacketToVault(packet);
    if (download) {
      downloadPartnerProofPacket(packet);
    }
    reportAction({
      title: 'Partner proof packet generated',
      detail: 'The packet viewer opened and the packet is being saved to the AWS proof vault when reachable.',
      artifact: `${packet.packet_id}: browser local storage, viewer, downloadable JSON, and AWS proof-vault save attempt.`,
      targetId: 'partner-proof-packet-viewer',
      tone: 'success',
    });
  }

  function downloadPartnerProofPacket(packet = proofPacket) {
    const packetToDownload = packet ?? buildPartnerProofPacket(launchDossier);
    if (!packet) {
      storeAndOpenPacket(packetToDownload);
      void recordPacketReceipt(packetToDownload);
      void saveProofPacketToVault(packetToDownload);
    } else {
      setPacketViewMode('plain');
      setPacketOpen(true);
      scrollToId('partner-proof-packet-viewer');
    }
    const blob = new Blob([JSON.stringify(packetToDownload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vaerion-partner-proof-packet-${packetToDownload.packet_id}.json`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setCopyStatus('Proof packet opened and JSON download started.');
    reportAction({
      title: 'Proof packet export started',
      detail: 'The packet viewer opened and a JSON artifact download was started by the browser.',
      artifact: `vaerion-partner-proof-packet-${packetToDownload.packet_id}.json`,
      targetId: 'partner-proof-packet-viewer',
      tone: 'success',
    });
  }

  function plainEnglishProofMarkdown(packet = proofPacket ?? buildPartnerProofPacket()) {
    const enterpriseReceipts = packet.enterprise_surface_receipts ?? [];
    const evidenceRows = packet.evidence_dossier.length
      ? packet.evidence_dossier
      : [
          {
            surface: 'Partner proof packet',
            evidence_type: 'Generated artifact',
            source: 'Browser local storage',
            receipt_id: packet.packet_id,
            recorded_at: packet.generated_at,
            method: 'Packet generated in this browser session.',
            result: packet.summary,
            artifact: packet.storage.current_location,
            proves: 'VAERION can generate a non-PHI partner launch artifact.',
            does_not_prove: 'This alone is not external production authority.',
            authority_limit: 'Counterparty authority evidence is required before live production action.',
          },
        ];
    const evidenceTable = evidenceRows.map(
      (item) =>
        `| ${item.surface} | ${item.receipt_id} | ${item.method.replace(/\|/g, '/')} | ${item.result.replace(/\|/g, '/')} | ${item.proves.replace(/\|/g, '/')} | ${item.authority_limit.replace(/\|/g, '/')} |`,
    );
    return [
      `# VAERION Partner Billing Proof Packet`,
      ``,
      `Packet ID: ${packet.packet_id}`,
      `Generated: ${packet.generated_at}`,
      `Generated by: ${packet.generated_by}`,
      ``,
      `## Proof Standard`,
      `Purpose: ${packet.proof_standard.purpose}`,
      `Evidence scope: ${packet.proof_standard.evidence_scope}`,
      `Production authority boundary: ${packet.proof_standard.production_authority_boundary}`,
      ``,
      `## Plain-English Verdict`,
      `VAERION-owned billing readiness has receipt-level evidence for partner review. The packet shows the exact receipt IDs, sources, methods, results, and artifacts VAERION can prove now. Live production use remains counterparty-authority gated until payer, Epic/customer, hospital, and bank or processor evidence is recorded.`,
      ``,
      `## How A Partner Verifies This Packet`,
      `1. Match the Packet ID above to the JSON artifact and the saved server proof-vault packet record.`,
      `2. Match each Evidence Index receipt ID to the server launch dossier, AWS proof receipt, browser proof runner receipt, or sandbox receipt shown in the UI.`,
      `3. Confirm the Evidence Surface, Method, Result, and Authority Boundary columns are consistent across the plain-English packet and raw JSON packet.`,
      `4. Confirm the Launch Dossier Snapshot shows server-side packet count and enterprise-surface receipt count rather than relying only on browser-local state.`,
      `5. Treat payer, Epic/customer, hospital, and treasury approvals as counterparty authority evidence that must be added before live production traffic.`,
      ``,
      `## Evidence Index`,
      `| Evidence Surface | Receipt / Artifact | Method | Result | What It Proves | Authority Boundary |`,
      `| --- | --- | --- | --- | --- | --- |`,
      ...evidenceTable,
      ``,
      `## Launch Dossier Snapshot`,
      ...(packet.launch_dossier_snapshot
        ? [
            `Tenant: ${packet.launch_dossier_snapshot.tenant}`,
            `Server dossier generated: ${packet.launch_dossier_snapshot.generated_at}`,
            `Verdict: ${packet.launch_dossier_snapshot.verdict}`,
            `VAERION-owned status: ${packet.launch_dossier_snapshot.vaerion_owned_status}`,
            `Production authority state: ${packet.launch_dossier_snapshot.production_cutover_status}`,
            `Proof packets in server vault: ${packet.launch_dossier_snapshot.proof_packet_count}`,
            `Enterprise receipts: ${packet.launch_dossier_snapshot.proven_surface_count}/${packet.launch_dossier_snapshot.required_surface_count}`,
            `VAERION-owned surfaces awaiting evidence: ${
              packet.launch_dossier_snapshot.remaining_surface_ids.length
                ? packet.launch_dossier_snapshot.remaining_surface_ids.join(', ')
                : 'None in the loaded server dossier'
            }`,
          ]
        : [
            `No server launch dossier snapshot was loaded into this packet. Generate the packet while the launch dossier is reachable to include server-vault counts.`,
          ]),
      ``,
      `## Server Proof Vault`,
      `Packet records loaded: ${packet.server_vault.packet_count}`,
      `Packet IDs: ${packet.server_vault.saved_packet_ids.length ? packet.server_vault.saved_packet_ids.join(', ') : 'No server packet IDs loaded'}`,
      `Artifact state: ${packet.server_vault.packet_artifact_state}`,
      ``,
      `## Enterprise Readiness Contract`,
      ...(packet.enterprise_readiness_snapshot
        ? [
            `Contract generated: ${packet.enterprise_readiness_snapshot.generated_at}`,
            `Product boundary: ${packet.enterprise_readiness_snapshot.product_boundary}`,
            `Designed purpose: ${packet.enterprise_readiness_snapshot.designed_purpose}`,
            `VAERION-owned readiness: ${packet.enterprise_readiness_snapshot.vaerion_owned_readiness}`,
            `Live production cutover: ${packet.enterprise_readiness_snapshot.live_production_cutover}`,
            `Evidence surfaces recorded: ${packet.enterprise_readiness_snapshot.recorded_surface_count}/${packet.enterprise_readiness_snapshot.evidence_surface_count}`,
            `Proof packets counted: ${packet.enterprise_readiness_snapshot.proof_packet_count}`,
            ``,
            `### Deployment Modes`,
            ...packet.enterprise_readiness_snapshot.deployment_modes.map(
              (mode) => `- ${mode.mode}: ${mode.status}. Proves: ${mode.what_it_proves} Limit: ${mode.limit}`,
            ),
            ``,
            `### Live Production Authority Owners`,
            ...packet.enterprise_readiness_snapshot.live_production_requirements.map(
              (item) => `- ${item.requirement}: ${item.owner}. ${item.authority_boundary}`,
            ),
          ]
        : [
            `No enterprise readiness contract was loaded into this packet. Refresh Enterprise Readiness before generating the packet to include deployment modes and proof endpoints.`,
          ]),
      ``,
      `## Enterprise Deployment Receipts`,
      ...(enterpriseReceipts.length > 0
        ? enterpriseReceipts.map(
            (receipt) =>
              `- ${receipt.title}: ${receipt.receipt_id}. ${receipt.verdict} Remaining authority: ${receipt.remaining_external_authority.join('; ')}`,
          )
        : ['- No enterprise deployment surface receipts were loaded into this packet. Use Launch Control to run surface evidence and refresh the dossier.']),
      ``,
      `## Counterparty Approval Packets Prepared`,
      ...(packet.approval_packets?.length
        ? packet.approval_packets.map(
            (approval) =>
              `- ${approval.title}: ${approval.packet_id}. Counterparty: ${approval.counterparty}. Status: ${readableAuthorityState(
                approval.status,
              )}. Live action: ${readableAuthorityState(approval.live_external_action)}. Next step: ${approval.next_step}`,
          )
        : [
            '- No approval packets were attached to this packet yet. Open Approval Packets, prepare the payer, Epic/customer, hospital, and financial authority packets, then regenerate this proof packet.',
          ]),
      ``,
      `## Partner Review Checklist`,
      ...packet.partner_review_checklist.map((item) => `- ${item}`),
      ``,
      `## Counterparty Authority Required Before Live Production`,
      ...packet.external_authority_still_required.map((item) => `- ${item}`),
      ``,
      `## Guardrails`,
      ...packet.guardrails.map((item) => `- ${item}`),
      ``,
    ].join('\n');
  }

  function downloadPlainEnglishProof(packet = proofPacket) {
    const packetToDownload = packet ?? buildPartnerProofPacket(launchDossier);
    if (!packet) {
      storeAndOpenPacket(packetToDownload);
      void recordPacketReceipt(packetToDownload);
      void saveProofPacketToVault(packetToDownload);
    } else {
      setPacketViewMode('plain');
      setPacketOpen(true);
      scrollToId('partner-proof-packet-viewer');
    }
    const blob = new Blob([plainEnglishProofMarkdown(packetToDownload)], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vaerion-partner-proof-${packetToDownload.packet_id}.md`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setCopyStatus('Plain-English proof opened and Markdown download started.');
    reportAction({
      title: 'Plain-English proof export started',
      detail: 'The packet viewer opened and a Markdown proof artifact download was started by the browser.',
      artifact: `vaerion-partner-proof-${packetToDownload.packet_id}.md`,
      targetId: 'partner-proof-packet-viewer',
      tone: 'success',
    });
  }

  function downloadBase64Artifact(base64: string, filename: string, contentType: string) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    const blob = new Blob([bytes], { type: contentType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  async function downloadExecutiveProofPdf(packet = proofPacket) {
    const packetToDownload = packet ?? buildPartnerProofPacket(launchDossier);
    if (!packet) {
      storeAndOpenPacket(packetToDownload);
    }
    reportAction({
      title: 'Generating executive PDF',
      detail: 'Requesting a server-generated PDF artifact with SHA-256 metadata and non-PHI evidence rows.',
      targetId: 'partner-proof-packet-viewer',
      tone: 'running',
    });
    try {
      const response = await billingFetch(`/proof-packets/pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          packet_id: packetToDownload.packet_id,
          verdict:
            'VAERION-owned billing readiness is documented with receipt-level non-PHI evidence for partner review.',
          evidence_items: packetToDownload.evidence_dossier.map((item) => ({
            surface: item.surface,
            receipt_id: item.receipt_id,
            result: item.result,
          })),
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        error?: string;
        filename?: string;
        content_type?: string;
        pdf_base64?: string;
        sha256?: string;
        receipt_id?: string;
      };
      if (!response.ok || !payload.pdf_base64 || !payload.filename) {
        reportAction({
          title: 'Executive PDF unavailable',
          detail: payload.error ?? 'The PDF proof endpoint did not return a downloadable artifact.',
          targetId: 'partner-proof-packet-viewer',
          tone: 'blocked',
        });
        return;
      }
      downloadBase64Artifact(payload.pdf_base64, payload.filename, payload.content_type ?? 'application/pdf');
      setPacketReceipt(
        `${payload.receipt_id ?? 'PDF receipt'}: PDF artifact generated. SHA-256 ${payload.sha256 ?? 'not returned'}.`,
      );
      reportAction({
        title: 'Executive PDF proof downloaded',
        detail: 'The PDF proof packet downloaded and includes hash metadata for verification.',
        artifact: `${payload.filename} · SHA-256 ${payload.sha256}`,
        targetId: 'partner-proof-packet-viewer',
        tone: 'success',
      });
    } catch {
      reportAction({
        title: 'Executive PDF unavailable',
        detail: 'The PDF proof endpoint could not be reached. JSON and Markdown exports remain available.',
        targetId: 'partner-proof-packet-viewer',
        tone: 'blocked',
      });
    }
  }

  async function rotateSandboxApiKey() {
    setDeveloperOpsStatus('Rotating sandbox API key reference...');
    try {
      const response = await billingFetch(`/developer/api-keys/rotate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key_scope: 'partner_sandbox',
          rotation_reason: 'partner implementation readiness proof',
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as DeveloperOpsReceipt & { error?: string };
      if (!response.ok) {
        setDeveloperOpsStatus(payload.error ?? 'API key reference rotation could not be completed.');
        return;
      }
      setDeveloperOpsReceipt(payload);
      setDeveloperOpsStatus(
        `${payload.key_reference}: sandbox API key reference rotated. Secret value returned: ${payload.secret_value_returned ? 'yes' : 'no'}.`,
      );
      reportAction({
        title: 'Sandbox API key reference rotated',
        detail: 'A developer key lifecycle receipt was recorded without returning secret material.',
        artifact: payload.key_reference,
        targetId: 'developer-operations',
        tone: 'success',
      });
    } catch {
      setDeveloperOpsStatus('API key reference rotation endpoint could not be reached.');
    }
  }

  async function replayDeveloperWebhook() {
    setDeveloperOpsStatus('Replaying non-PHI webhook event...');
    try {
      const response = await billingFetch(`/developer/webhooks/replay`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_type: 'claim.validation.completed',
          endpoint_reference: 'partner-webhook-ref',
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as DeveloperOpsReceipt & { error?: string };
      if (!response.ok) {
        setDeveloperOpsStatus(payload.error ?? 'Webhook replay could not be completed.');
        return;
      }
      setDeveloperOpsReceipt(payload);
      setDeveloperOpsStatus(
        `${payload.receipt_id}: ${readableAuthorityState(payload.delivery?.status)}; DLQ ${readableAuthorityState(payload.dlq?.status)}.`,
      );
      reportAction({
        title: 'Webhook replay receipt recorded',
        detail: 'A non-PHI webhook replay receipt was recorded with signature-reference and DLQ proof.',
        artifact: payload.delivery?.idempotency_key,
        targetId: 'developer-operations',
        tone: 'success',
      });
    } catch {
      setDeveloperOpsStatus('Webhook replay endpoint could not be reached.');
    }
  }

  async function runCompanionGuideValidation() {
    setCompanionGuideStatus('Validating payer companion guide into an X12 profile...');
    try {
      const response = await billingFetch(`/payer/companion-guide/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payer_name: 'Aetna',
          payer_id: '60054',
          guide_text: companionGuideText,
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as CompanionGuideReceipt & { error?: string };
      if (!response.ok) {
        setCompanionGuideStatus(payload.error ?? 'Companion-guide validation could not be completed.');
        return;
      }
      setCompanionGuideReceipt(payload);
      setCompanionGuideStatus(
        `${payload.receipt_id}: ${payload.profile.payer_name} ${payload.profile.x12_guide}: ${payload.profile.transaction_sets.join(', ')} profile generated.`,
      );
      reportAction({
        title: 'Companion-guide profile generated',
        detail: 'Payer implementation text was converted into a payer-specific X12 validation profile.',
        artifact: `${payload.profile.payer_id} · ${payload.profile.transaction_sets.join(', ')}`,
        targetId: 'companion-guide-validator',
        tone: 'success',
      });
    } catch {
      setCompanionGuideStatus('Companion-guide validation endpoint could not be reached.');
    }
  }

  async function importDeidentifiedReplayManifest() {
    setReplayImportStatus('Importing de-identified replay manifest...');
    try {
      const response = await billingFetch(`/replay-vault/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          manifest_id: `partner-deidentified-export-${Date.now()}`,
          deidentification_attestation: 'Safe-harbor de-identified export manifest; no direct identifiers in this request.',
          artifacts: [
            { type: '837', count: 120, sha256: 'a'.repeat(64) },
            { type: '835', count: 118, sha256: 'b'.repeat(64) },
            { type: 'deposit', count: 25, sha256: 'c'.repeat(64) },
            { type: 'denial', count: 14, sha256: 'd'.repeat(64) },
          ],
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as ReplayImportReceipt & { error?: string };
      if (!response.ok) {
        setReplayImportStatus(payload.error ?? 'Replay manifest import could not be completed.');
        return;
      }
      setReplayImportReceipt(payload);
      setReplayImportStatus(
        `${payload.receipt_id}: ${payload.manifest_id}: ${payload.fixture_inventory.total_artifacts} artifacts and ${payload.fixture_inventory.total_records} records accepted for de-identified replay.`,
      );
      reportAction({
        title: 'De-identified replay manifest imported',
        detail: 'A replay-vault receipt was recorded with artifact hashes and zero raw PHI stored.',
        artifact: payload.manifest_id,
        targetId: 'replay-vault-importer',
        tone: 'success',
      });
    } catch {
      setReplayImportStatus('Replay manifest import endpoint could not be reached.');
    }
  }

  async function recordProofReviewDecision(packet = proofPacket) {
    const packetToReview = packet ?? buildPartnerProofPacket(launchDossier);
    if (!packet) {
      storeAndOpenPacket(packetToReview);
    }
    setProofReviewStatus(`Recording proof review decision: ${proofReviewDecision}...`);
    try {
      const response = await billingFetch(`/proof-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          object_type: 'proof_packet',
          object_id: packetToReview.packet_id,
          decision: proofReviewDecision,
          rationale:
            'Reviewed for partner diligence. Internal proof lifecycle state does not unlock payer, Epic/customer, hospital, or bank production authority.',
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as ProofReviewReceipt & { error?: string };
      if (!response.ok) {
        setProofReviewStatus(payload.error ?? 'Proof review decision could not be recorded.');
        return;
      }
      setProofReviewReceipt(payload);
      setProofReviewStatus(
        `${payload.object_id}: ${payload.lifecycle_state} by ${payload.reviewer}. External authority unlocked: ${payload.external_authority_unlocked ? 'yes' : 'no'}.`,
      );
      reportAction({
        title: 'Proof review lifecycle recorded',
        detail: 'A formal reviewer decision was recorded for the current proof packet.',
        artifact: `${payload.lifecycle_state} · ${payload.receipt_id}`,
        targetId: 'proof-review-lifecycle',
        tone: 'success',
      });
    } catch {
      setProofReviewStatus('Proof review endpoint could not be reached.');
    }
  }

  async function runAuthoritySuite(workaroundId = 'clinical-billing-proof') {
    setAuthorityReceipt('Running non-PHI authority proof suite...');
    try {
      const response = await billingFetch(`/authority-workarounds/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workaround_id: workaroundId,
          mode: 'synthetic_non_phi',
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        receipt_id?: string;
        status?: string;
        next_step?: string;
        error?: string;
      };
      if (!response.ok) {
        setAuthorityReceipt(payload.error ?? 'Authority suite could not be completed.');
        scrollToId('authority-workaround-lab');
        return;
      }
      const nextStep = payload.next_step
        ? readableAuthorityState(payload.next_step)
        : 'Counterparty authority evidence is required before live action.';
      setAuthorityReceipt(
        `Internal proof recorded: ${payload.receipt_id}. ${nextStep}`,
      );
      scrollToId('authority-workaround-lab');
    } catch {
      setAuthorityReceipt('Authority suite could not reach the billing edge. Keep external actions authority-gated.');
      scrollToId('authority-workaround-lab');
    }
  }

  function runCapabilityAction(capability: (typeof enterpriseCapabilityMap)[number]) {
    if (capability.section === 'developer') {
      selectSection(capability.section);
      openDeveloperDocs();
      void runConsoleWorkflow(capability.section, capability.action);
      return;
    }
    if (capability.section === 'payer') {
      selectSection(capability.section, 'carrier-certification-matrix');
      void runConsoleWorkflow(capability.section, capability.action);
      return;
    }
    if (capability.section === 'ehr') {
      selectSection(capability.section);
      void runEhrSandboxLab();
      return;
    }
    if (capability.section === 'audit') {
      void generatePartnerProofPacket();
      return;
    }
    if (capability.section === 'payments') {
      selectSection(capability.section);
      void runPaymentProofLab();
      return;
    }
    selectSection(capability.section);
    void runConsoleWorkflow(capability.section, capability.action);
  }

  if (sessionState === 'checking') {
    return (
      <main className="login-app">
        <div className="session-loading" role="status">
          <Loader2 className="spin" size={22} aria-hidden="true" />
          <span>Checking billing portal session</span>
        </div>
      </main>
    );
  }

  if (sessionState === 'unauthenticated') {
    return <EnterpriseSignIn onSignedIn={() => setSessionState('authenticated')} />;
  }

  const selectedApprovalPacket =
    preparedApprovalPackets[selectedApprovalPacketId] ?? preparedApprovalPacketFor(selectedApprovalPacketId);

  return (
    <main className="billing-app">
      <aside className="sidebar" aria-label="VAERION Billing Console navigation">
        <div className="brand-block">
          <img src="/vaerion-logo-horizontal.png" alt="VAERION Systems" />
          <span>Clinical Billing System</span>
        </div>
        <nav className="nav-list">
          {navSections.map((section) => {
            const Icon = section.icon;
            const selected = activeSection === section.id;
            return (
              <button
                key={section.id}
                className={selected ? 'nav-item selected' : 'nav-item'}
                onClick={() => selectSection(section.id)}
                type="button"
              >
                <Icon aria-hidden="true" size={19} />
                <span>
                  <strong>{section.label}</strong>
                  <small>{section.description}</small>
                </span>
              </button>
            );
          })}
        </nav>
        <div className="sidebar-proof">
          <ShieldAlert size={18} aria-hidden="true" />
          <div>
            <strong>Live billing guarded</strong>
            <p>No claim transmission, Epic write, appeal filing, or money movement until external authority proof exists.</p>
          </div>
        </div>
      </aside>

      <section className="workspace">
        <header className="topbar">
          <div>
            <strong>Partner Billing Console</strong>
            <span>DRAI Health implementation workspace</span>
          </div>
          <label className="search global-search">
            <Search size={18} aria-hidden="true" />
            <input
              aria-label="Search billing console"
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search claims, payers, proof, routes, APIs..."
              value={searchQuery}
            />
            {searchResults.length > 0 ? (
              <div className="search-results" role="listbox" aria-label="Billing console search results">
                {searchResults.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => {
                      selectSection(result.id);
                    }}
                    type="button"
                  >
                    <strong>{result.label}</strong>
                    <span>{result.description}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </label>
          <button className="button secondary" type="button" onClick={openDeveloperDocs}>
            <ExternalLink size={17} />
            Developer docs
          </button>
          <button className="button secondary" type="button" onClick={handleSignOut}>
            <LogOut size={17} />
            Sign out
          </button>
          <button className="button primary" type="button" onClick={() => void generatePartnerProofPacket({ download: true })}>
            <Download size={17} />
            Export proof
          </button>
        </header>

        <section className="guide-panel" data-testid="billing-console-guide" aria-label="How to use the billing console">
          <div className="guide-copy">
            <span>How to use this console</span>
            <h2>Run evidence, open the dossier, then review the exact authority boundary.</h2>
            <p>
              This console is not a static dashboard. Every primary action should either run VAERION-owned evidence,
              open a packet, copy a handoff artifact, download proof, or route you to the exact setup surface.
            </p>
            <div className="guide-steps">
              <article>
                <strong>1. Pick a surface</strong>
                <p>Use the left navigation or search to open Launch, EHR, payer, claim, payment, denial, audit, or developer work.</p>
              </article>
              <article>
                <strong>2. Run the evidence</strong>
                <p>Use the primary action on that surface. VAERION records non-PHI readiness receipts and keeps live action gated.</p>
              </article>
              <article>
                <strong>3. Open the packet</strong>
                <p>Generate the partner packet to inspect plain-English proof, evidence ledger, raw JSON, and saved vault records.</p>
              </article>
              <article>
                <strong>4. Review the gate</strong>
                <p>The UI always separates what VAERION proved from payer, Epic, bank, and hospital authority still required.</p>
              </article>
            </div>
          </div>
          <aside className={`action-center ${actionFeedback.tone}`} data-testid="action-center" role="status">
            <span>Latest action</span>
            <h3>{actionFeedback.title}</h3>
            <p>{actionFeedback.detail}</p>
            {actionFeedback.artifact ? (
              <div className="artifact-callout">
                <strong>Artifact / receipt</strong>
                <p>{actionFeedback.artifact}</p>
              </div>
            ) : null}
            <div className="action-center-buttons">
              {actionFeedback.targetId ? (
                <button className="button secondary" type="button" onClick={() => scrollToId(actionFeedback.targetId!, 'center')}>
                  Go to result
                </button>
              ) : null}
              <button className="button ghost" type="button" onClick={() => void generatePartnerProofPacket()}>
                Open evidence dossier
              </button>
            </div>
          </aside>
        </section>

        <section className="hero">
          <div className="hero-copy">
            <h1>Launch partner billing without handing them the clinical operating system.</h1>
            <p>
              A focused console for implementation teams to connect facilities, providers, EHR, payer routes,
              claim intake, payment posting, denials, and audit proof while VAERION keeps live external actions authority-gated.
            </p>
            <div className="hero-actions">
              <button className="button primary" type="button" onClick={() => void runReadinessPath()}>
                <Play size={17} />
                Run readiness path
              </button>
              <button className="button ghost" type="button" onClick={() => void generatePartnerProofPacket()}>
                <Sparkles size={17} />
                Generate partner packet
              </button>
            </div>
          </div>
          <div className="readiness-panel">
            <div className="readiness-score">
              <span>VAERION-owned readiness</span>
              <strong>Complete</strong>
              <small>External cutover approval remains separate.</small>
            </div>
            {launchGates.map((gate) => (
              <div className="gate-row" key={gate.label}>
                <span className={`status-dot ${gate.tone}`} />
                <div>
                  <strong>{gate.label}</strong>
                  <p>{gate.state}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="panel launch-dossier" data-testid="launch-dossier" id="launch-dossier">
          <div className="section-heading">
            <div>
              <span>Enterprise launch dossier</span>
              <h2>One proof source for partner diligence and hospital cutover review.</h2>
              <p>
                This dossier reads the server-side proof vault, counts recorded proof packets and enterprise deployment
                receipts, lists VAERION-owned evidence surfaces, and separates production cutover authority until outside
                approval evidence is recorded.
              </p>
            </div>
            <FileText size={32} aria-hidden="true" />
          </div>
          <div className="dossier-actions">
            <button className="button primary" type="button" onClick={() => void loadLaunchDossier()}>
              <ShieldCheck size={17} />
              Refresh launch dossier
            </button>
            <button className="button secondary" type="button" onClick={() => void runNextMissingSurface()}>
              <Play size={17} />
              Run next evidence surface
            </button>
            <button className="button ghost" type="button" onClick={() => void generatePartnerProofPacket()}>
              <Sparkles size={17} />
              Generate dossier packet
            </button>
          </div>
          <div className="authority-receipt" role="status">
            <strong>Server-side dossier status</strong>
            <p>{launchDossierStatus}</p>
          </div>
          {launchDossier ? (
            <>
              <div className="dossier-metrics" aria-label="Launch dossier readiness metrics">
                <div>
                  <span>Enterprise surfaces proved</span>
                  <strong>
                    {launchDossier.readiness.proven_surface_count} / {launchDossier.readiness.required_surface_count}
                  </strong>
                  <small>{readableAuthorityState(launchDossier.readiness.vaerion_owned_status)}</small>
                </div>
                <div>
                  <span>Server proof packets</span>
                  <strong>
                    {launchDossier.readiness.proof_packet_count} server packet
                    {launchDossier.readiness.proof_packet_count === 1 ? '' : 's'}
                  </strong>
                  <small>Saved outside browser local storage</small>
                </div>
                <div>
                  <span>Production cutover</span>
                  <strong>{readableAuthorityState(launchDossier.readiness.production_cutover_status)}</strong>
                  <small>{readableAuthorityState(launchDossier.live_external_action)}</small>
                </div>
              </div>
              <div className="dossier-grid">
                <article>
                  <span>Plain-English verdict</span>
                  <p>{launchDossier.verdict}</p>
                </article>
                <article>
                  <span>VAERION-owned evidence status</span>
                  <div className="dossier-chip-list">
                    {launchDossier.readiness.missing_surface_ids.length ? (
                      launchDossier.readiness.missing_surface_ids.map((surfaceId) => (
                        <button className="chip-button" key={surfaceId} type="button" onClick={() => void runEnterpriseSurface(surfaceId)}>
                          {surfaceId}
                        </button>
                      ))
                    ) : (
                      <strong>None awaiting evidence by the server-side dossier.</strong>
                    )}
                  </div>
                </article>
                <article>
                  <span>Counterparty authority required before live production</span>
                  <ul>
                    {launchDossier.remaining_external_authority.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              </div>
              <div className="dossier-receipts">
                {launchDossier.enterprise_surface_receipts.length ? (
                  launchDossier.enterprise_surface_receipts.slice(0, 4).map((receipt) => (
                    <article key={receipt.receipt_id}>
                      <span>{receipt.surface_id}</span>
                      <strong>{receipt.title}</strong>
                      <p>{receipt.verdict}</p>
                      <small>{receipt.receipt_id}</small>
                    </article>
                  ))
                ) : (
                  <article>
                    <span>No server enterprise receipts yet</span>
                    <p>Run the enterprise deployment surface proofs, then refresh the dossier.</p>
                  </article>
                )}
              </div>
              <div className="dossier-next-actions">
                <strong>Next actions</strong>
                <ul>
                  {launchDossier.next_actions.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <div className="dossier-empty">
              <strong>No server-side dossier loaded yet.</strong>
              <p>
                Refresh the dossier to verify what the AWS-backed proof vault can actually show. Browser-only receipts
                are useful during a demo, but they are not enough for enterprise deployment review.
              </p>
            </div>
          )}
        </section>

        <section className="panel enterprise-readiness-contract" data-testid="enterprise-readiness" id="enterprise-readiness">
          <div className="section-heading">
            <div>
              <span>Enterprise readiness contract</span>
              <h2>What is proved, how it was proved, and what live production still needs.</h2>
              <p>
                This contract comes from the billing API, not from browser-only state. It gives a partner reviewer a
                machine-readable answer for deployment modes, evidence surfaces, proof endpoints, and live-production
                authority owners.
              </p>
            </div>
            <ShieldCheck size={32} aria-hidden="true" />
          </div>
          <div className="dossier-actions">
            <button className="button primary" type="button" onClick={() => void loadEnterpriseReadiness()}>
              <ShieldCheck size={17} />
              Refresh enterprise readiness
            </button>
            <button className="button secondary" type="button" onClick={() => void generatePartnerProofPacket()}>
              <Download size={17} />
              Add to proof packet
            </button>
          </div>
          <div className="authority-receipt" role="status">
            <strong>Enterprise readiness status</strong>
            <p>{enterpriseReadinessStatus}</p>
          </div>
          {enterpriseReadiness ? (
            <>
              <div className="readiness-contract-summary">
                <article>
                  <span>Product boundary</span>
                  <p>{enterpriseReadiness.product_boundary}</p>
                </article>
                <article>
                  <span>Designed purpose</span>
                  <p>{enterpriseReadiness.designed_purpose}</p>
                </article>
              </div>
              <div className="dossier-metrics" aria-label="Enterprise readiness metrics">
                <div>
                  <span>VAERION-owned surfaces</span>
                  <strong>
                    {enterpriseReadiness.status.vaerion_owned_surface_count} /{' '}
                    {enterpriseReadiness.status.required_vaerion_owned_surface_count}
                  </strong>
                  <small>{readableAuthorityState(enterpriseReadiness.status.vaerion_owned_readiness)}</small>
                </div>
                <div>
                  <span>Proof endpoints</span>
                  <strong>{enterpriseReadiness.proof_endpoints.length}</strong>
                  <small>Callable implementation surfaces</small>
                </div>
                <div>
                  <span>Live production cutover</span>
                  <strong>{readableAuthorityState(enterpriseReadiness.status.live_production_cutover)}</strong>
                  <small>Authority evidence required</small>
                </div>
              </div>
              <div className="readiness-mode-grid">
                {enterpriseReadiness.deployment_modes.map((mode) => (
                  <article key={mode.mode}>
                    <span>{mode.mode.replace(/_/g, ' ')}</span>
                    <strong>{readableAuthorityState(mode.status)}</strong>
                    <p>{mode.what_it_proves}</p>
                    <small>{mode.limit}</small>
                  </article>
                ))}
              </div>
              <div className="readiness-surface-table">
                <div className="readiness-surface-header">
                  <span>Evidence surface</span>
                  <span>Server receipt</span>
                  <span>Proof / limit</span>
                </div>
                {enterpriseReadiness.evidence_surfaces.map((surface) => (
                  <article key={surface.surface_id}>
                    <div>
                      <strong>{surface.title}</strong>
                      <small>{surface.surface_id}</small>
                    </div>
                    <div>
                      <span className={surface.latest_receipt_id ? 'status-pill complete' : 'status-pill action'}>
                        {surface.latest_receipt_id ? 'Receipt recorded' : 'Runnable'}
                      </span>
                      <small>{surface.latest_receipt_id ?? 'Run this surface to record proof'}</small>
                    </div>
                    <div>
                      <p>{surface.proves}</p>
                      <small>{surface.does_not_prove}</small>
                    </div>
                  </article>
                ))}
              </div>
              <div className="readiness-authority-grid">
                {enterpriseReadiness.live_production_requirements.map((item) => (
                  <article key={item.requirement}>
                    <span>{item.requirement}</span>
                    <strong>{item.owner}</strong>
                    <p>{item.vaerion_can_prepare}</p>
                    <small>{item.authority_boundary}</small>
                  </article>
                ))}
              </div>
            </>
          ) : (
            <div className="dossier-empty">
              <strong>No enterprise readiness contract loaded yet.</strong>
              <p>
                Click Refresh enterprise readiness. The console will read the AWS billing runtime and show exactly what
                is provable now before the packet is generated for a partner review.
              </p>
            </div>
          )}
        </section>

        <section className="panel enterprise-proof-verifier" data-testid="enterprise-proof-verifier" id="enterprise-proof-verifier">
          <div className="section-heading">
            <div>
              <span>Enterprise Proof Verifier</span>
              <h2>Show what is proven, where the receipt is, and what authority evidence is still controlled by counterparties.</h2>
              <p>
                This is the partner-review inspection surface. It joins VAERION-owned receipts with non-PHI authority
                evidence references so a CEO, CTO, compliance officer, or hospital implementation team can inspect
                proof without seeing raw PHI, secrets, tokens, banking data, or unsupported production claims.
              </p>
            </div>
            <ShieldCheck size={32} aria-hidden="true" />
          </div>

          <div className="dossier-actions">
            <button className="button primary" type="button" onClick={() => void loadEnterpriseProofVerifier()}>
              <ShieldCheck size={17} />
              Refresh proof verifier
            </button>
            <button className="button secondary" type="button" onClick={() => void generatePartnerProofPacket()}>
              <Download size={17} />
              Add verifier to packet
            </button>
          </div>

          <div className="authority-receipt" role="status">
            <strong>Verifier status</strong>
            <p>{enterpriseProofVerifierStatus}</p>
          </div>

          {enterpriseProofVerifier ? (
            <>
              <div className="verifier-verdict">
                <article>
                  <span>{enterpriseProofVerifier.verifier_name}</span>
                  <strong>{enterpriseProofVerifier.verdict.plain_english}</strong>
                  <p>{enterpriseProofVerifier.boundary}</p>
                </article>
                <div className="verifier-counts" aria-label="Enterprise verifier proof counts">
                  <div>
                    <span>Proof packets</span>
                    <strong>{enterpriseProofVerifier.proof_counts.proof_packets}</strong>
                  </div>
                  <div>
                    <span>Enterprise receipts</span>
                    <strong>{enterpriseProofVerifier.proof_counts.enterprise_surface_receipts}</strong>
                  </div>
                  <div>
                    <span>Authority evidence</span>
                    <strong>{enterpriseProofVerifier.proof_counts.authority_evidence}</strong>
                  </div>
                  <div>
                    <span>Cutover state</span>
                    <strong>{readableAuthorityState(enterpriseProofVerifier.verdict.production_authority)}</strong>
                  </div>
                </div>
              </div>

              {enterpriseProofVerifier.production_activation_tracks?.length ? (
                <div className="activation-workflow" data-testid="production-activation-workflow">
                  <div className="section-heading compact">
                    <div>
                      <span>Production activation workflow</span>
                      <h2>From proof to live deployment, with the authority owner visible at every step.</h2>
                      <p>
                        Each track shows what VAERION can complete now, what must be sent to the payer, Epic/customer,
                        hospital, bank, processor, or treasury owner, and what evidence must be reviewed before final
                        cutover. No step claims live authority before the accepted evidence exists.
                      </p>
                    </div>
                  </div>
                  <div className="activation-track-grid">
                    {enterpriseProofVerifier.production_activation_tracks.map((track) => (
                      <article className="activation-track" key={track.track_id}>
                        <div className="activation-track-header">
                          <span>{readableAuthorityState(track.current_state)}</span>
                          <strong>{track.title}</strong>
                          <p>{track.purpose}</p>
                        </div>
                        <div className="activation-next-action">
                          <span>Next best action</span>
                          <p>{track.next_best_action}</p>
                        </div>
                        <div className="activation-steps">
                          {track.states.map((state) => (
                            <div className={`activation-step ${state.status}`} key={`${track.track_id}-${state.state_id}`}>
                              <div>
                                <span>{state.sequence}</span>
                                <strong>{state.label}</strong>
                                <small>{readableAuthorityState(state.status)}</small>
                              </div>
                              <p>{state.what_vaerion_can_do}</p>
                              <em>{state.proof_or_action}</em>
                              <button
                                className="button secondary slim"
                                type="button"
                                onClick={() => handleActivationTrackAction(track, state.state_id)}
                              >
                                {state.state_id === 'prepare_now'
                                  ? 'Open proof work'
                                  : state.state_id === 'send_to_counterparty'
                                    ? 'Build packet'
                                    : state.state_id === 'evidence_received'
                                      ? 'Record evidence'
                                      : state.state_id === 'review_evidence'
                                        ? 'Review evidence'
                                        : 'Open cutover packet'}
                              </button>
                            </div>
                          ))}
                        </div>
                        {track.latest_authority_evidence ? (
                          <div className="domain-authority-receipt">
                            <strong>{track.latest_authority_evidence.receipt_id}</strong>
                            <span>{readableAuthorityState(track.latest_authority_evidence.lifecycle_state)}</span>
                          </div>
                        ) : (
                          <div className="domain-authority-receipt pending">
                            <strong>{track.evidence_label}</strong>
                            <span>Counterparty evidence not recorded yet</span>
                          </div>
                        )}
                      </article>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="verifier-domain-grid">
                {enterpriseProofVerifier.proof_domains.map((domain) => (
                  <article key={domain.domain_id}>
                    <div className="verifier-domain-header">
                      <span>{domain.domain_id}</span>
                      <strong>{domain.title}</strong>
                    </div>
                    <dl>
                      <div>
                        <dt>VAERION can prove now</dt>
                        <dd>{formatList(domain.vaerion_proof_available_now, 'Run VAERION proof to record software-readiness evidence.')}</dd>
                      </div>
                      <div>
                        <dt>Inspectable receipts</dt>
                        <dd>{formatList(domain.inspectable_receipts, 'Run proof to record receipt evidence.')}</dd>
                      </div>
                      <div>
                        <dt>Authority evidence required</dt>
                        <dd>{formatList(domain.counterparty_authority_required, 'Counterparty authority evidence required before live production action.')}</dd>
                      </div>
                      <div>
                        <dt>Verification method</dt>
                        <dd>{domain.proof_method}</dd>
                      </div>
                    </dl>
                    {domain.verification_ladder?.length ? (
                      <div className="verification-ladder" aria-label={`${domain.title} verification ladder`}>
                        <strong>How this can be verified</strong>
                        {domain.verification_ladder.map((step, index) => (
                          <div className="verification-step" key={`${domain.domain_id}-${step.level}`}>
                            <span>{index + 1}</span>
                            <div>
                              <b>{step.level}</b>
                              <p>{step.proves}</p>
                              <small>{step.account_or_evidence_needed}</small>
                              <em>{step.production_meaning}</em>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {domain.latest_authority_evidence ? (
                      <div className="domain-authority-receipt">
                        <strong>{domain.latest_authority_evidence.receipt_id}</strong>
                        <span>{readableAuthorityState(domain.latest_authority_evidence.lifecycle_state)}</span>
                      </div>
                    ) : (
                      <div className="domain-authority-receipt pending">
                        <strong>Authority evidence required</strong>
                        <span>{domain.accepted_evidence_type}</span>
                      </div>
                    )}
                  </article>
                ))}
              </div>

              <div className="verifier-script">
                <strong>CTO / CEO review script</strong>
                <ol>
                  {enterpriseProofVerifier.cto_review_script.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ol>
              </div>
            </>
          ) : (
            <div className="dossier-empty">
              <strong>No verifier snapshot loaded yet.</strong>
              <p>
                Click Refresh proof verifier to pull the AWS-backed inspection surface. This gives partner leadership a
                domain-by-domain proof map instead of a vague “ready” claim.
              </p>
            </div>
          )}

          {activeSection === 'launch' || authorityEvidenceIntakeOpen ? (
            <form
              className="authority-evidence-form"
              data-testid="authority-evidence-intake"
              id="authority-evidence-intake"
              onSubmit={(event) => void recordAuthorityEvidence(event)}
            >
              <div className="section-heading compact">
                <div>
                  <span>Authority evidence intake</span>
                  <h2>Record counterparty evidence by reference and hash.</h2>
                  <p>
                    Use this when a payer, Epic/customer, hospital, bank, processor, or treasury owner issues evidence.
                    Store the raw document in the approved customer repository; VAERION records the reference, hash,
                    scope, issuer, and review state without PHI or secrets.
                  </p>
                </div>
              </div>
              <div className="authority-evidence-fields">
                <label className="field">
                  <span>Evidence type</span>
                  <select value={authorityEvidenceType} onChange={(event) => setAuthorityEvidenceType(event.target.value)}>
                    <option value="payer_trading_partner_acceptance">Payer trading-partner acceptance</option>
                    <option value="epic_customer_production_access">Epic/customer production access</option>
                    <option value="hospital_go_live_authorization">Hospital go-live authorization</option>
                    <option value="bank_processor_money_movement_authority">Bank/processor money-movement authority</option>
                  </select>
                </label>
                <label className="field">
                  <span>Authority evidence issuer</span>
                  <input
                    aria-label="Authority evidence issuer"
                    value={authorityEvidenceIssuer}
                    onChange={(event) => setAuthorityEvidenceIssuer(event.target.value)}
                    placeholder="Aetna EDI Enrollment"
                  />
                </label>
                <label className="field wide">
                  <span>Authority scope</span>
                  <input
                    aria-label="Authority scope"
                    value={authorityEvidenceScope}
                    onChange={(event) => setAuthorityEvidenceScope(event.target.value)}
                    placeholder="Exact payer route, Epic tenant, facility, or payment rail this evidence covers"
                  />
                </label>
                <label className="field">
                  <span>Artifact reference</span>
                  <input
                    aria-label="Artifact reference"
                    value={authorityEvidenceReference}
                    onChange={(event) => setAuthorityEvidenceReference(event.target.value)}
                    placeholder="payer-portal-case-AETNA-EDI-10001"
                  />
                </label>
                <label className="field">
                  <span>Artifact SHA-256</span>
                  <input
                    aria-label="Artifact SHA-256"
                    value={authorityEvidenceHash}
                    onChange={(event) => setAuthorityEvidenceHash(event.target.value)}
                    placeholder="64 lowercase hex characters"
                    pattern="[a-f0-9]{64}"
                    title="Enter a 64-character lowercase SHA-256 hash."
                  />
                </label>
              </div>
              <div className="dossier-actions">
                <button className="button primary" type="submit">
                  <FileText size={17} />
                  Record authority evidence
                </button>
                <button
                  className="button secondary"
                  type="button"
                  onClick={() => {
                    setAuthorityEvidenceIssuer('Aetna EDI Enrollment');
                    setAuthorityEvidenceScope('Aetna commercial 837P/999/277CA/835 production route for DRAI Health pilot');
                    setAuthorityEvidenceReference('payer-portal-case-AETNA-EDI-10001');
                    setAuthorityEvidenceHash('a'.repeat(64));
                  }}
                >
                  Use safe sample
                </button>
              </div>
            </form>
          ) : null}

          <div className="authority-receipt" role="status">
            <strong>Authority evidence status</strong>
            <p>{authorityEvidenceStatus}</p>
          </div>
          {authorityEvidenceReceipts.length ? (
            <div className="authority-evidence-list">
              {authorityEvidenceReceipts.map((receipt) => (
                <article key={receipt.receipt_id}>
                  <span>{receipt.evidence_type.replace(/_/g, ' ')}</span>
                  <strong>{receipt.receipt_id}</strong>
                  <p>{receipt.authority_scope}</p>
                  <dl>
                    <div>
                      <dt>Issuer</dt>
                      <dd>{receipt.issuer}</dd>
                    </div>
                    <div>
                      <dt>Artifact</dt>
                      <dd>{receipt.artifact_reference}</dd>
                    </div>
                    <div>
                      <dt>Final cutover review eligible</dt>
                      <dd>{receipt.final_cutover_review_eligible ? 'yes' : 'no'}</dd>
                    </div>
                    <div>
                      <dt>Production auto-activation</dt>
                      <dd>No</dd>
                    </div>
                    <div>
                      <dt>Raw PHI stored</dt>
                      <dd>{receipt.raw_phi_stored ? 'yes' : 'no'}</dd>
                    </div>
                    {receipt.reviewed_at ? (
                      <div>
                        <dt>Reviewed</dt>
                        <dd>
                          {receipt.reviewer ?? 'reviewer'} · {new Date(receipt.reviewed_at).toLocaleString()}
                        </dd>
                      </div>
                    ) : null}
                    {receipt.review_receipt_id ? (
                      <div>
                        <dt>Review receipt</dt>
                        <dd>{receipt.review_receipt_id}</dd>
                      </div>
                    ) : null}
                  </dl>
                  <small>{receipt.next_step}</small>
                  <div className="authority-review-actions">
                    <button
                      className="button secondary"
                      type="button"
                      onClick={() => void reviewAuthorityEvidence(receipt, 'reviewed')}
                    >
                      Mark reviewed
                    </button>
                    <button
                      className="button primary"
                      type="button"
                      onClick={() => void reviewAuthorityEvidence(receipt, 'accepted')}
                    >
                      Accept for cutover review
                    </button>
                    <button
                      className="button secondary"
                      type="button"
                      onClick={() => void reviewAuthorityEvidence(receipt, 'rejected')}
                    >
                      Reject
                    </button>
                    <button
                      className="button secondary"
                      type="button"
                      onClick={() => void reviewAuthorityEvidence(receipt, 'expired')}
                    >
                      Expire
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : null}
        </section>

        {docsOpen ? (
          <section className="panel developer-docs" id="developer-docs" data-testid="developer-docs">
            <div className="section-heading">
              <div>
                <span>Developer docs</span>
                <h2>Partner integration handoff</h2>
                <p>
                  These are the implementation surfaces a partner team can use today for non-PHI pilots, proof packet
                  review, direct payer-route preparation, and guarded production planning. Secrets are never displayed in
                  the browser, and live external actions remain authority-gated until authority evidence exists.
                </p>
              </div>
              <ExternalLink size={32} aria-hidden="true" />
            </div>
            <div className="docs-grid">
              <article>
                <h3>Authentication</h3>
                <p>
                  Partner access is session-gated. Production customers should map SSO/OIDC/SAML and MFA policy during
                  tenant launch before hospital PHI is enabled.
                </p>
              </article>
              <article>
                <h3>Direct payer routes</h3>
                <p>
                  VAERION can prepare payer-specific 837, 999, 277CA, and 835 lifecycle proof. The payer still controls
                  trading-partner acceptance and production endpoints.
                </p>
              </article>
              <article>
                <h3>FHIR and EHR</h3>
                <p>
                  Epic-first FHIR R4 discovery, SMART/OIDC simulation, and mapping receipts can be generated before
                  customer production credentials are issued.
                </p>
              </article>
              <article>
                <h3>Proof exports</h3>
                <p>
                  Generated packets are non-PHI implementation artifacts. Use them for security, revenue-cycle,
                  implementation, and payer-route review without representing them as external approval.
                </p>
              </article>
            </div>
            <button className="button secondary" type="button" onClick={() => void copyImplementationSurfaces()}>
              <Copy size={17} />
              Copy all API surfaces
            </button>
            <p className="copy-status" role="status">{copyStatus}</p>
            {manualCopyText ? (
              <textarea
                className="manual-copy-buffer"
                aria-label="Manual copy API buffer"
                readOnly
                value={manualCopyText}
                onFocus={(event) => event.currentTarget.select()}
              />
            ) : null}
          </section>
        ) : null}

        <section className="badge-strip" aria-label="Billing safety capabilities">
          {readinessBadges.map((badge) => {
            const Icon = badge.icon;
            return (
              <div key={badge.label}>
                <Icon size={18} aria-hidden="true" />
                <span>{badge.label}</span>
              </div>
            );
          })}
        </section>

        <div className="content-grid">
          <section className="panel workstream-panel" id="active-workstream">
            <div className="section-heading">
              <div>
                <span>{activeWorkstream.owner}</span>
                <h2>{activeWorkstream.title}</h2>
                <p>{activeWorkstream.summary}</p>
              </div>
              <ActiveWorkstreamIcon size={32} aria-hidden="true" />
            </div>
            <div className="metric-grid">
              {activeWorkstreamMetrics.map((metric) => (
                <div className="metric" key={metric.label}>
                  <span>{metric.label}</span>
                  <strong>{metric.value}</strong>
                </div>
              ))}
            </div>
            <div className="section-proof-guide" data-testid="active-section-proof-guide">
              <article>
                <span>What this screen proves</span>
                <p>{activeGuide.proves}</p>
              </article>
              <article>
                <span>How to use it</span>
                <p>{activeGuide.use}</p>
              </article>
              <article>
                <span>Authority evidence still required</span>
                <p>{activeGuide.limit}</p>
              </article>
            </div>
            <div className="step-list">
              {activeWorkstream.steps.map((step) => (
                <article className="step-card" key={step.label}>
                  <span className={`status-pill ${step.tone}`}>{toneLabel[step.tone]}</span>
                  <h3>{step.label}</h3>
                  <strong>{step.state}</strong>
                  <p>{step.detail}</p>
                </article>
              ))}
            </div>
            <div className="panel-actions">
              <button
                className="button primary"
                type="button"
                onClick={() => {
                  if (activeSection === 'authority') {
                    void runAuthoritySuite();
                    void runConsoleWorkflow(activeSection, activeWorkstream.primaryAction);
                    return;
                  }
                  if (activeSection === 'approvals') {
                    prepareApprovalPacket('payer-trading-partner');
                    return;
                  }
                  if (activeSection === 'claims') {
                    void validateClaimSandbox();
                    return;
                  }
                  if (activeSection === 'ehr') {
                    void runEhrSandboxLab();
                    return;
                  }
                  if (activeSection === 'payments') {
                    void runPaymentProofLab();
                    return;
                  }
                  if (activeSection === 'developer') {
                    void rotateSandboxApiKey();
                    return;
                  }
                  if (activeSection === 'audit' || activeSection === 'launch') {
                    void generatePartnerProofPacket();
                    return;
                  }
                  void runConsoleWorkflow(activeSection, activeWorkstream.primaryAction);
                }}
              >
                {activeWorkstream.primaryAction}
                <ArrowRight size={17} />
              </button>
              <button
                className="button secondary"
                onClick={() => {
                  if (activeSection === 'authority') {
                    void runAuthoritySuite();
                    void runConsoleWorkflow(activeSection, activeWorkstream.secondaryAction);
                    return;
                  }
                  if (activeSection === 'approvals') {
                    prepareAllApprovalPackets();
                    return;
                  }
                  if (activeSection === 'claims') {
                    selectSection('developer');
                    void runConsoleWorkflow('developer', activeWorkstream.secondaryAction);
                    return;
                  }
                  if (activeSection === 'payer') {
                    scrollToId('carrier-certification-matrix');
                  }
                  if (activeSection === 'ehr') {
                    if (!ehrSandboxReceipt) {
                      void runEhrSandboxLab();
                      return;
                    }
                    reportAction({
                      title: 'FHIR mapping receipt opened',
                      detail:
                        'The synthetic Patient, Coverage, Encounter, and DocumentReference mapping evidence is visible in the EHR lab.',
                      artifact:
                        'This mapping proof uses non-PHI fixtures and does not unlock production Epic/customer access.',
                      targetId: 'ehr-mapping-proof',
                      tone: 'success',
                    });
                    scrollToId('ehr-mapping-proof', 'center');
                    return;
                  }
                  if (activeSection === 'developer') {
                    void replayDeveloperWebhook();
                    return;
                  }
                  void runConsoleWorkflow(activeSection, activeWorkstream.secondaryAction);
                }}
                type="button"
              >
                {activeWorkstream.secondaryAction}
              </button>
            </div>
          </section>

          <aside className="panel action-panel">
            <div className="section-heading compact">
              <div>
                <span>Next best path</span>
                <h2>What the partner should do now</h2>
              </div>
            </div>
            <ol className="action-list">
              {nextActions.map((action) => (
                <li key={action}>
                  <CheckCircle2 size={17} aria-hidden="true" />
                  <span>{action}</span>
                </li>
              ))}
            </ol>
            <div className="claim-validator">
              <h3>Claim intake sandbox</h3>
              <textarea
                aria-label="Sample claim payload"
                onChange={(event) => setClaimPayload(event.target.value)}
                value={claimPayload}
              />
              <button className="button primary full" type="button" onClick={() => void validateClaimSandbox()}>
                Validate sample
              </button>
              <p className="validation-result">{claimStatus}</p>
            </div>
          </aside>
        </div>

        {activeSection === 'developer' ? (
          <section className="panel proof-packet-viewer" data-testid="developer-operations" id="developer-operations">
            <div className="section-heading">
              <div>
                <span>Developer operations</span>
                <h2>API keys, webhooks, replay, and DLQ proof without exposing secrets.</h2>
                <p>
                  These controls prove the implementation handoff surface a partner engineering team needs: key-reference
                  rotation, scoped access, signed webhook replay, idempotency, retry behavior, and DLQ handling. No raw
                  API key, signing secret, token, password, or PHI is returned to the browser.
                </p>
              </div>
              <KeyRound size={32} aria-hidden="true" />
            </div>
            <div className="packet-viewer-actions">
              <button className="button primary" type="button" onClick={() => void rotateSandboxApiKey()}>
                Rotate sandbox API key
              </button>
              <button className="button secondary" type="button" onClick={() => void replayDeveloperWebhook()}>
                Replay webhook
              </button>
              <button className="button secondary" type="button" onClick={() => void generatePartnerProofPacket()}>
                Attach to partner packet
              </button>
            </div>
            <div className="authority-receipt" role="status">
              <strong>Latest developer operations receipt</strong>
              <p>{developerOpsStatus}</p>
            </div>
            {developerOpsReceipt ? (
              <div className="packet-section-grid">
                <article>
                  <h3>Receipt</h3>
                  <p>
                    <strong>{developerOpsReceipt.receipt_id}</strong>
                  </p>
                  <p>{new Date(developerOpsReceipt.created_at).toLocaleString()}</p>
                </article>
                <article>
                  <h3>Key or webhook scope</h3>
                  <p>{developerOpsReceipt.key_reference ?? developerOpsReceipt.event_type ?? 'developer operation'}</p>
                  <p>{(developerOpsReceipt.allowed_scopes ?? []).join(', ') || developerOpsReceipt.key_scope || 'non-PHI replay'}</p>
                </article>
                <article>
                  <h3>Secret handling</h3>
                  <p>Secret value returned: {developerOpsReceipt.secret_value_returned ? 'yes' : 'no'}</p>
                  <p>Signing secret returned: {developerOpsReceipt.signature?.signing_secret_returned ? 'yes' : 'no'}</p>
                </article>
                <article>
                  <h3>Replay and DLQ</h3>
                  <p>{developerOpsReceipt.delivery ? `${readableAuthorityState(developerOpsReceipt.delivery.status)} · ${developerOpsReceipt.delivery.attempts} attempt(s)` : 'key lifecycle receipt'}</p>
                  <p>{developerOpsReceipt.dlq ? `${readableAuthorityState(developerOpsReceipt.dlq.status)} · replayable ${developerOpsReceipt.dlq.replayable ? 'yes' : 'no'}` : developerOpsReceipt.does_not_prove}</p>
                </article>
              </div>
            ) : null}
          </section>
        ) : null}

        {activeSection === 'approvals' ? (
          <>
            <section
              className="panel authority-acquisition-console"
              data-testid="authority-acquisition-console"
              id="authority-acquisition-console"
            >
              <div className="section-heading">
                <div>
                  <span>Live authority acquisition</span>
                  <h2>Start the real approval path without pretending authority already exists.</h2>
                  <p>
                    This is the operator console for the work VAERION can do today: run proof, prepare the right packet,
                    open the official counterparty path, copy the filing checklist, and record the returned reference when
                    the payer, Epic/customer, hospital, or financial owner issues it.
                  </p>
                </div>
                <ShieldCheck size={32} aria-hidden="true" />
              </div>
              <div className="packet-viewer-actions">
                <button className="button primary" type="button" onClick={() => downloadAuthorityAcquisitionDossier()}>
                  Download acquisition dossier
                </button>
                <button className="button secondary" type="button" onClick={() => prepareAllApprovalPackets()}>
                  Prepare all authority packets
                </button>
                <button className="button secondary" type="button" onClick={() => void generatePartnerProofPacket()}>
                  Attach to partner proof
                </button>
              </div>
              <div className="authority-receipt" data-testid="authority-acquisition-status" role="status">
                <strong>Authority acquisition status</strong>
                <p>{authorityAcquisitionStatus}</p>
              </div>
              <div className="authority-acquisition-grid">
                {authorityAcquisitionTracks.map((track) => (
                  <article className="authority-acquisition-card" data-testid={`authority-acquisition-${track.id}`} key={track.id}>
                    <div className="authority-card-header">
                      <span className="status-pill ready">Ready to file</span>
                      <a
                        aria-label={`Open ${track.portalLabel}`}
                        className="button secondary compact-button"
                        href={track.portalUrl}
                        onClick={() => {
                          setAuthorityAcquisitionStatus(
                            `${track.title}: opening ${track.portalLabel}. Return here to record the issuer reference and hash after the counterparty responds.`,
                          );
                          reportAction({
                            title: `${track.portalLabel} opened`,
                            detail: `${track.sourceLabel} is the counterparty-controlled path. VAERION does not submit legal attestations automatically.`,
                            artifact: track.portalUrl,
                            targetId: 'authority-acquisition-console',
                            tone: 'neutral',
                          });
                        }}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <ExternalLink size={16} aria-hidden="true" /> Open path
                      </a>
                    </div>
                    <h3>{track.title}</h3>
                    <p>{track.counterparty}</p>
                    <dl className="authority-acquisition-meta">
                      <div>
                        <dt>Official source</dt>
                        <dd>
                          <a href={track.sourceUrl} rel="noreferrer" target="_blank">
                            {track.sourceLabel}
                          </a>
                        </dd>
                      </div>
                      <div>
                        <dt>Returned evidence</dt>
                        <dd>{readableAuthorityState(track.authorityEvidenceType)}</dd>
                      </div>
                    </dl>
                    <div className="authority-acquisition-columns">
                      <div>
                        <h4>VAERION can do now</h4>
                        <ul>
                          {track.vaerionCanDoNow.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4>Issuer must return</h4>
                        <ul>
                          {track.counterpartyMustReturn.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="authority-next-step">
                      <strong>Best next action</strong>
                      <p>{track.proofToRunFirst}</p>
                    </div>
                    <div className="authority-card-actions">
                      <button className="button primary" type="button" onClick={() => void runAuthorityAcquisitionProof(track)}>
                        <Play size={16} aria-hidden="true" /> Run proof first
                      </button>
                      <button className="button secondary" type="button" onClick={() => prepareAuthorityAcquisitionPacket(track)}>
                        <FileText size={16} aria-hidden="true" /> Prepare packet
                      </button>
                      <button className="button secondary" type="button" onClick={() => void copyAuthorityAcquisitionChecklist(track)}>
                        <Copy size={16} aria-hidden="true" /> Copy checklist
                      </button>
                      <button className="button secondary" type="button" onClick={() => recordReturnedAuthorityEvidence(track)}>
                        Record returned evidence
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <section
              className="panel approval-packet-workbench"
              data-testid="approval-packet-workbench"
              id="approval-packet-workbench"
            >
            <div className="section-heading">
              <div>
                <span>Counterparty authority packets</span>
                <h2>Counterparty Approval Packets</h2>
                <p>
                  These packets turn every external authority dependency into a concrete submission checklist. VAERION
                  prepares the evidence, fields, and proof receipts; the payer, Epic/customer, hospital, or finance owner
                  still issues the authority evidence.
                </p>
              </div>
              <FileText size={32} aria-hidden="true" />
            </div>
            <div className="packet-viewer-actions">
              <button className="button primary" type="button" onClick={() => prepareApprovalPacket('payer-trading-partner')}>
                Prepare payer packet
              </button>
              <button className="button secondary" type="button" onClick={() => prepareAllApprovalPackets()}>
                Prepare all packets
              </button>
              <button className="button secondary" type="button" onClick={() => downloadAllApprovalPackets()}>
                Download all packets
              </button>
              <button className="button secondary" type="button" onClick={() => void generatePartnerProofPacket()}>
                Attach prepared packets to proof export
              </button>
            </div>
            <div className="authority-receipt" data-testid="approval-packet-status" role="status">
              <strong>Approval packet status</strong>
              <p>{approvalPacketStatus}</p>
            </div>
            <div className="approval-packet-grid">
              {approvalPacketTemplates.map((template) => {
                const prepared = preparedApprovalPackets[template.id];
                const selected = selectedApprovalPacketId === template.id;
                return (
                  <article className={`approval-packet-card ${selected ? 'selected' : ''}`} key={template.id}>
                    <div>
                      <span className={`status-pill ${prepared ? 'complete' : 'ready'}`}>
                        {prepared ? 'Prepared' : 'Ready'}
                      </span>
                      <h3>{template.title}</h3>
                      <p>{template.purpose}</p>
                    </div>
                    <dl>
                      <div>
                        <dt>Counterparty</dt>
                        <dd>{template.counterparty}</dd>
                      </div>
                      <div>
                        <dt>Evidence type</dt>
                        <dd>{readableAuthorityState(template.authorityEvidenceType)}</dd>
                      </div>
                    </dl>
                    <button
                      className="button secondary full"
                      type="button"
                      onClick={() => {
                        if (prepared) {
                          setSelectedApprovalPacketId(template.id);
                          setApprovalPacketStatus(`${template.title} selected. Use the detail actions to download, copy, or record evidence.`);
                          return;
                        }
                        prepareApprovalPacket(template.id);
                      }}
                    >
                      {prepared ? 'Open packet' : `Prepare ${template.id === 'payer-trading-partner' ? 'payer' : template.downloadName.replace(/-/g, ' ')} packet`}
                    </button>
                  </article>
                );
              })}
            </div>
            <section className="approval-packet-detail" data-testid="approval-packet-detail">
              <div className="section-heading compact">
                <div>
                  <span>Prepared for counterparty review</span>
                  <h3>{selectedApprovalPacket.title}</h3>
                  <p>{selectedApprovalPacket.nextStep}</p>
                </div>
              </div>
              <div className="packet-section-grid">
                <article>
                  <h3>Packet authority boundary</h3>
                  <p>Live submission not performed by VAERION.</p>
                  <p>{selectedApprovalPacket.submissionBoundary}</p>
                </article>
                <article>
                  <h3>Evidence issuer</h3>
                  <p>{selectedApprovalPacket.evidenceIssuer}</p>
                  <p>{selectedApprovalPacket.evidenceScope}</p>
                </article>
                <article>
                  <h3>Packet artifact</h3>
                  <p>{selectedApprovalPacket.packet_id}</p>
                  <p>{new Date(selectedApprovalPacket.generated_at).toLocaleString()}</p>
                </article>
                <article>
                  <h3>Readiness receipt</h3>
                  <p>{selectedApprovalPacket.readiness_receipt_id ?? 'Not recorded yet'}</p>
                  <p>No production authority is unlocked by this receipt.</p>
                </article>
              </div>
              <div className="approval-packet-columns">
                <article>
                  <h3>VAERION prefilled evidence</h3>
                  <ul>
                    {selectedApprovalPacket.vaerionPrefill.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
                <article>
                  <h3>Counterparty fields required</h3>
                  <ul>
                    {selectedApprovalPacket.externalFields.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
                <article>
                  <h3>Proof receipts to attach</h3>
                  <ul>
                    {selectedApprovalPacket.proofReceipts.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              </div>
              <div className="packet-viewer-actions">
                <button className="button primary" type="button" onClick={() => downloadApprovalPacket(selectedApprovalPacket)}>
                  Download packet
                </button>
                <button className="button secondary" type="button" onClick={() => void copyApprovalChecklist(selectedApprovalPacket)}>
                  Copy portal checklist
                </button>
                <button className="button secondary" type="button" onClick={() => openApprovalEvidenceIntake(selectedApprovalPacket)}>
                  Open evidence intake
                </button>
                <button className="button secondary" type="button" onClick={() => void markApprovalPacketReady(selectedApprovalPacket)}>
                  Mark ready for counterparty review
                </button>
              </div>
            </section>
            </section>
          </>
        ) : null}

        {activeSection === 'payer' ? (
          <>
            <CarrierCertificationMatrix onCutoverReview={openCutoverReview} />
            <section
              className="panel proof-packet-viewer"
              data-testid="companion-guide-validator"
              id="companion-guide-validator"
            >
              <div className="section-heading">
                <div>
                  <span>Companion-guide parser</span>
                  <h2>Convert payer implementation text into a payer-specific X12 validation profile.</h2>
                  <p>
                    Paste or review non-PHI companion-guide language. VAERION extracts transaction sets, acknowledgements,
                    required identifiers, and validation rules into a reusable route profile. This proves profile creation;
                    payer production approval remains issued by the payer.
                  </p>
                </div>
                <FileText size={32} aria-hidden="true" />
              </div>
              <label className="field companion-guide-field">
                <span>Aetna companion-guide excerpt</span>
                <textarea
                  aria-label="Companion guide text"
                  onChange={(event) => setCompanionGuideText(event.target.value)}
                  value={companionGuideText}
                />
              </label>
              <div className="packet-viewer-actions">
                <button className="button primary" type="button" onClick={() => void runCompanionGuideValidation()}>
                  Validate companion guide
                </button>
                <button className="button secondary" type="button" onClick={() => void generatePartnerProofPacket()}>
                  Attach to partner packet
                </button>
              </div>
              <div className="authority-receipt" role="status">
                <strong>Latest companion-guide receipt</strong>
                <p>{companionGuideStatus}</p>
              </div>
              {companionGuideReceipt ? (
                <div className="packet-section-grid">
                  <article>
                    <h3>Payer profile</h3>
                    <p>{companionGuideReceipt.profile.payer_name} · {companionGuideReceipt.profile.payer_id}</p>
                    <p>{companionGuideReceipt.profile.x12_guide}</p>
                  </article>
                  <article>
                    <h3>Transactions</h3>
                    <p>{companionGuideReceipt.profile.transaction_sets.join(', ')}</p>
                  </article>
                  <article>
                    <h3>Acknowledgements</h3>
                    <p>{companionGuideReceipt.profile.acknowledgements.join(', ')}</p>
                  </article>
                  <article>
                    <h3>Validation profile</h3>
                    {Object.entries(companionGuideReceipt.profile.validation_profile).map(([key, value]) => (
                      <p key={key}>
                        <strong>{readableAuthorityState(key)}:</strong> {value}
                      </p>
                    ))}
                  </article>
                </div>
              ) : null}
            </section>
          </>
        ) : null}
        {activeSection === 'ehr' ? (
          <section className="panel proof-packet-viewer" data-testid="ehr-sandbox-lab" id="ehr-sandbox-lab">
            <div className="section-heading">
              <div>
                <span>Epic public sandbox lab</span>
                <h2>FHIR conformance proof without PHI or production claims.</h2>
                <p>
                  This runner checks Epic public FHIR discovery and records a non-PHI receipt. It proves VAERION can
                  parse the sandbox contract; production patient access still requires the customer and Epic authority.
                </p>
              </div>
              <RadioTower size={32} aria-hidden="true" />
            </div>
            <div className="packet-viewer-actions">
              <button className="button primary" type="button" onClick={() => void runEhrSandboxLab()}>
                Run Epic sandbox proof
              </button>
              <button className="button secondary" type="button" onClick={() => void runEhrSandboxTokenTest()}>
                Run backend OAuth token proof
              </button>
              <button className="button secondary" type="button" onClick={() => void runEhrSandboxTokenTest(true)}>
                Run live sandbox FHIR reads
              </button>
              <button className="button secondary" type="button" onClick={() => void copyEpicRegistrationManifest()}>
                Copy Epic app manifest
              </button>
              <button className="button secondary" type="button" onClick={() => void generatePartnerProofPacket()}>
                Attach to partner packet
              </button>
            </div>
            <div className="authority-receipt" role="status">
              <strong>Latest EHR sandbox receipt</strong>
              <p>{ehrSandboxStatus}</p>
            </div>
            <div className="authority-receipt" role="status">
              <strong>Latest Epic backend OAuth receipt</strong>
              <p>{ehrSandboxTokenStatus}</p>
            </div>
            <div className="packet-section-grid">
              <article>
                <h3>Epic app registration package</h3>
                <p>
                  <strong>Application:</strong>{' '}
                  {(ehrSandboxReceipt?.epic_registration_package ?? billingEpicRegistrationPackage).application_name}
                </p>
                <p>
                  <strong>Audience:</strong>{' '}
                  {(ehrSandboxReceipt?.epic_registration_package ?? billingEpicRegistrationPackage).application_audience}
                </p>
                <p>
                  <strong>Use case:</strong>{' '}
                  {(ehrSandboxReceipt?.epic_registration_package ?? billingEpicRegistrationPackage).use_case}
                </p>
                <p>
                  <strong>Current proof status:</strong> {ehrSandboxTokenStatus}
                </p>
              </article>
              <article>
                <h3>Public JWKS URLs</h3>
                <p>
                  <strong>Non-production:</strong>{' '}
                  {(ehrSandboxReceipt?.epic_registration_package ?? billingEpicRegistrationPackage).non_production_jwks_url}
                </p>
                <p>
                  <strong>Production:</strong>{' '}
                  {(ehrSandboxReceipt?.epic_registration_package ?? billingEpicRegistrationPackage).production_jwks_url}
                </p>
              </article>
              <article>
                <h3>Requested FHIR R4 capabilities</h3>
                <ul>
                  {(ehrSandboxReceipt?.epic_registration_package ?? billingEpicRegistrationPackage).requested_fhir_r4_capabilities.map(
                    (scope) => (
                      <li key={scope}>{scope}</li>
                    ),
                  )}
                </ul>
              </article>
            </div>
            <div className="packet-section-grid epic-config-grid">
              <article>
                <h3>Epic sandbox configuration references</h3>
                <p>
                  Paste only non-secret Epic setup references. VAERION rejects client secrets, private keys, tokens,
                  authorization codes, passwords, and PHI before any proof receipt is stored.
                </p>
                <label className="field">
                  <span>Epic FHIR R4 base URL</span>
                  <div className="field-control">
                    <input
                      aria-label="Epic FHIR R4 base URL"
                      onChange={(event) => setEpicBaseUrl(event.target.value)}
                      type="url"
                      value={epicBaseUrl}
                    />
                  </div>
                </label>
                <label className="field">
                  <span>Epic app client ID reference</span>
                  <div className="field-control">
                    <input
                      aria-label="Epic app client ID reference"
                      onChange={(event) => setEpicClientIdReference(event.target.value)}
                      placeholder="Paste Epic sandbox client ID after app registration"
                      type="text"
                      value={epicClientIdReference}
                    />
                  </div>
                </label>
              </article>
              <article>
                <h3>JWKS and credential reference</h3>
                <label className="field">
                  <span>Public JWKS URL</span>
                  <div className="field-control">
                    <input
                      aria-label="Epic public JWKS URL"
                      onChange={(event) => setEpicPublicJwksUrl(event.target.value)}
                      type="url"
                      value={epicPublicJwksUrl}
                    />
                  </div>
                </label>
                <label className="field">
                  <span>AWS Secrets Manager private-key reference</span>
                  <div className="field-control">
                    <input
                      aria-label="Epic private key secret reference"
                      onChange={(event) => setEpicCredentialSecretRef(event.target.value)}
                      placeholder="aws-secretsmanager:vaerion/billing/epic/sandbox/private-key"
                      type="text"
                      value={epicCredentialSecretRef}
                    />
                  </div>
                </label>
                <button
                  className="button secondary"
                  type="button"
                  onClick={() => setEpicPublicJwksUrl(billingEpicRegistrationPackage.non_production_jwks_url)}
                >
                  Use VAERION sandbox JWKS
                </button>
              </article>
            </div>
            {ehrSandboxReceipt ? (
              <div className="packet-section-grid" id="ehr-mapping-proof">
                <article>
                  <h3>CapabilityStatement</h3>
                  <p>{ehrSandboxReceipt.capability_statement.endpoint}</p>
                  <p>
                    <strong>FHIR:</strong> {ehrSandboxReceipt.capability_statement.fhir_version}
                  </p>
                  <p>
                    <strong>Software:</strong> {ehrSandboxReceipt.capability_statement.software}
                  </p>
                  <p>
                    <strong>Status:</strong> {readableAuthorityState(ehrSandboxReceipt.capability_statement.status)}
                  </p>
                  <p>
                    <strong>Required billing resources:</strong>{' '}
                    {ehrSandboxReceipt.capability_statement.advertised_required_resource_count ?? 0}/
                    {ehrSandboxReceipt.capability_statement.required_resource_count ?? 4} advertised
                  </p>
                </article>
                <article>
                  <h3>Resource and interaction coverage</h3>
                  {(ehrSandboxReceipt.capability_statement.interaction_coverage?.length
                    ? ehrSandboxReceipt.capability_statement.interaction_coverage
                    : Object.entries(ehrSandboxReceipt.resource_support).map(([resource, supported]) => ({
                        resource,
                        required_interaction: 'read',
                        resource_advertised: supported,
                        advertised_interactions: [],
                        required_interaction_advertised: false,
                        status: supported ? 'resource_advertised' : 'not_advertised',
                      }))
                  ).map((entry) => (
                    <p key={entry.resource}>
                      <strong>{entry.resource}:</strong> {readableAuthorityState(entry.status)}
                      {entry.advertised_interactions.length
                        ? ` · ${entry.advertised_interactions.join(', ')}`
                        : ' · interaction list not reported'}
                    </p>
                  ))}
                </article>
                <article>
                  <h3>SMART/OIDC discovery</h3>
                  <p>{ehrSandboxReceipt.smart_configuration.endpoint}</p>
                  <p>
                    <strong>Authorization:</strong> {ehrSandboxReceipt.smart_configuration.authorization_endpoint}
                  </p>
                  <p>
                    <strong>Token:</strong> {ehrSandboxReceipt.smart_configuration.token_endpoint}
                  </p>
                  <p>
                    <strong>JWKS:</strong> {ehrSandboxReceipt.smart_configuration.jwks_uri ?? 'not reported'}
                  </p>
                </article>
                <article>
                  <h3>Epic backend OAuth readiness</h3>
                  <p>
                    <strong>Status:</strong>{' '}
                    {readableAuthorityState(ehrSandboxReceipt.backend_oauth_readiness?.status)}
                  </p>
                  <p>
                    <strong>Client ID reference:</strong>{' '}
                    {ehrSandboxReceipt.backend_oauth_readiness?.client_id_reference_present ? 'present' : 'not configured'}
                  </p>
                  <p>
                    <strong>Public JWKS URL:</strong>{' '}
                    {ehrSandboxReceipt.backend_oauth_readiness?.public_jwks_url_present ? 'present' : 'not configured'}
                  </p>
                  <p>
                    <strong>Private-key secret reference:</strong>{' '}
                    {ehrSandboxReceipt.backend_oauth_readiness?.credential_secret_reference_present
                      ? 'present'
                      : 'not configured'}
                  </p>
                </article>
                <article>
                  <h3>Conformance checklist</h3>
                  {(ehrSandboxReceipt.conformance_checklist ?? []).map((item) => (
                    <p key={item.step}>
                      <strong>{item.step}:</strong> {readableAuthorityState(item.status)} · {item.evidence}
                    </p>
                  ))}
                </article>
                <article>
                  <h3>Synthetic mapping validation</h3>
                  {(ehrSandboxReceipt.mapping_validation ?? []).map((item) => (
                    <p key={item.resource}>
                      <strong>{item.resource}:</strong> {readableAuthorityState(item.status)} · {item.fixture_id}
                    </p>
                  ))}
                </article>
                <article>
                  <h3>Authenticated Epic tests</h3>
                  {Object.entries(ehrSandboxReceipt.authenticated_resource_tests ?? {}).map(([testName, value]) => {
                    if (typeof value === 'string') {
                      return (
                        <p key={testName}>
                          <strong>{readableAuthorityState(testName)}:</strong> {readableAuthorityState(value)}
                        </p>
                      );
                    }
                    return (
                      <p key={testName}>
                        <strong>{readableAuthorityState(testName)}:</strong> {readableAuthorityState(value.status)}
                        {value.required_scope ? ` · ${value.required_scope}` : ''}
                      </p>
                    );
                  })}
                </article>
                <article>
                  <h3>What it proves</h3>
                  <p>{ehrSandboxReceipt.proves}</p>
                  <h3>What it does not prove</h3>
                  <p>{ehrSandboxReceipt.does_not_prove}</p>
                </article>
              </div>
            ) : null}
            {ehrSandboxTokenReceipt ? (
              <div className="packet-section-grid">
                <article>
                  <h3>Backend OAuth token exchange</h3>
                  <p>
                    <strong>Status:</strong> {readableAuthorityState(ehrSandboxTokenReceipt.token_exchange.status)}
                  </p>
                  <p>
                    <strong>HTTP status:</strong> {ehrSandboxTokenReceipt.token_exchange.http_status}
                  </p>
                  <p>
                    <strong>Token endpoint:</strong> {ehrSandboxTokenReceipt.token_exchange.token_endpoint}
                  </p>
                  <p>
                    <strong>Granted scope:</strong> {ehrSandboxTokenReceipt.token_exchange.granted_scope}
                  </p>
                </article>
                <article>
                  <h3>Secret handling proof</h3>
                  <p>
                    <strong>Assertion kid:</strong> {ehrSandboxTokenReceipt.client_assertion.kid}
                  </p>
                  <p>
                    <strong>Private key exported:</strong>{' '}
                    {ehrSandboxTokenReceipt.client_assertion.private_key_exported ? 'yes' : 'no'}
                  </p>
                  <p>
                    <strong>Raw assertion stored:</strong>{' '}
                    {ehrSandboxTokenReceipt.client_assertion.raw_assertion_stored ? 'yes' : 'no'}
                  </p>
                  <p>
                    <strong>Access token stored:</strong>{' '}
                    {ehrSandboxTokenReceipt.token_exchange.access_token_stored ? 'yes' : 'no'}
                  </p>
                  <p>
                    <strong>Raw token response stored:</strong>{' '}
                    {ehrSandboxTokenReceipt.token_exchange.raw_response_stored ? 'yes' : 'no'}
                  </p>
                </article>
                <article>
                  <h3>Live sandbox resource receipts</h3>
                  {ehrSandboxTokenReceipt.sandbox_fixture ? (
                    <p>
                      <strong>Fixture:</strong> {ehrSandboxTokenReceipt.sandbox_fixture.patient_label} ·{' '}
                      {ehrSandboxTokenReceipt.sandbox_fixture.patient_id}
                    </p>
                  ) : null}
                  {Object.entries(ehrSandboxTokenReceipt.live_resource_tests).map(([testName, test]) => (
                    <p key={testName}>
                      <strong>{readableAuthorityState(testName)}:</strong> {readableAuthorityState(test.status)}
                      {test.http_status ? ` · HTTP ${test.http_status}` : ''}
                      {test.returned_resource_type ? ` · ${test.returned_resource_type}` : ''}
                      {typeof test.entry_count === 'number' ? ` · ${test.entry_count} entries` : ''}
                      {test.raw_payload_stored === false ? ' · raw payload not stored' : ''}
                    </p>
                  ))}
                </article>
                <article>
                  <h3>Token proof verdict</h3>
                  <p>{ehrSandboxTokenReceipt.proves}</p>
                  <p>{ehrSandboxTokenReceipt.does_not_prove}</p>
                </article>
              </div>
            ) : null}
          </section>
        ) : null}
        {activeSection === 'payments' ? (
          <section className="panel proof-packet-viewer" data-testid="payment-proof-lab" id="payment-proof-lab">
            <div className="section-heading">
              <div>
                <span>End-to-end payment proof</span>
                <h2>Claim, acknowledgement, ERA, posting, and deposit proof without moving money.</h2>
                <p>
                  This runner uses synthetic non-PHI billing fixtures to prove VAERION payment logic. Real settlement,
                  EFT, ACH, lockbox, refund, void, and reversal actions stay authority-gated until authority evidence exists.
                </p>
              </div>
              <Table2 size={32} aria-hidden="true" />
            </div>
            <div className="packet-viewer-actions">
              <button className="button primary" type="button" onClick={() => void runPaymentProofLab()}>
                Run payment proof
              </button>
              <button className="button secondary" type="button" onClick={() => void runPaymentRailSandbox()}>
                Run rail sandbox
              </button>
              <button className="button secondary" type="button" onClick={() => void generatePartnerProofPacket()}>
                Attach to partner packet
              </button>
            </div>
            <div className="authority-receipt" role="status">
              <strong>Latest payment proof receipt</strong>
              <p>{paymentProofStatus}</p>
            </div>
            {paymentProofReceipt ? (
              <div className="packet-section-grid">
                <article>
                  <h3>Claim and payer lifecycle</h3>
                  <p>
                    <strong>837 validation:</strong> {paymentProofReceipt.claim_validation.status}
                  </p>
                  <p>
                    <strong>Acknowledgement:</strong> {paymentProofReceipt.payer_acknowledgement.transaction_set}{' '}
                    {paymentProofReceipt.payer_acknowledgement.status}
                  </p>
                  <p>{paymentProofReceipt.payer_acknowledgement.artifact}</p>
                </article>
                <article>
                  <h3>ERA and posting</h3>
                  <p>
                    <strong>835 status:</strong> {paymentProofReceipt.era.posting_status}
                  </p>
                  <p>
                    <strong>Paid:</strong> ${(paymentProofReceipt.era.paid_cents / 100).toFixed(2)}
                  </p>
                  <p>
                    <strong>Adjustment:</strong> ${(paymentProofReceipt.era.adjustment_cents / 100).toFixed(2)}
                  </p>
                </article>
                <article>
                  <h3>Deposit reconciliation</h3>
                  <p>
                    <strong>Status:</strong> {paymentProofReceipt.deposit_reconciliation.status}
                  </p>
                  <p>
                    <strong>Deposit reference:</strong> {paymentProofReceipt.deposit_reconciliation.deposit_reference}
                  </p>
                  <p>
                    <strong>Duplicate remittance:</strong> {paymentProofReceipt.duplicate_remittance_guard.status}
                  </p>
                </article>
                <article>
                  <h3>Real money movement requires financial authority</h3>
                  <p>{paymentProofReceipt.refund_void_reversal.artifact}</p>
                  <p>{paymentProofReceipt.does_not_prove}</p>
                </article>
              </div>
            ) : null}
          </section>
        ) : null}
        {activeSection === 'payments' ? (
          <section
            className="panel proof-packet-viewer"
            data-testid="payment-rail-sandbox-lab"
            id="payment-rail-sandbox-lab"
          >
            <div className="section-heading">
              <div>
                <span>Payment rail sandbox</span>
                <h2>Bank-style payment proof without bank settlement.</h2>
                <p>
                  This lab validates VAERION-owned payment rail behavior: NACHA-style file controls, bank-response
                  emulation, de-identified ERA/deposit replay, reconciliation, and accounting approvals. It is a proof
                  sandbox, not an ODFI, bank, processor, or live funds network.
                </p>
              </div>
              <ShieldCheck size={32} aria-hidden="true" />
            </div>
            <div className="packet-viewer-actions">
              <button className="button primary" type="button" onClick={() => void runPaymentRailSandbox()}>
                Run rail sandbox
              </button>
              <button className="button secondary" type="button" onClick={() => void generatePartnerProofPacket()}>
                Attach to partner packet
              </button>
            </div>
            <div className="authority-receipt" role="status">
              <strong>Latest payment rail sandbox receipt</strong>
              <p>{paymentRailSandboxStatus}</p>
            </div>
            {paymentRailSandboxReceipt ? (
              <div className="packet-section-grid">
                <article>
                  <h3>NACHA-style file validation</h3>
                  <p>
                    <strong>Status:</strong> {paymentRailSandboxReceipt.nacha_file_validation.status}
                  </p>
                  <p>{paymentRailSandboxReceipt.nacha_file_validation.artifact}</p>
                </article>
                <article>
                  <h3>Bank response emulator</h3>
                  <p>
                    <strong>Status:</strong> {paymentRailSandboxReceipt.bank_response_emulator.status}
                  </p>
                  <p>{paymentRailSandboxReceipt.bank_response_emulator.artifact}</p>
                </article>
                <article>
                  <h3>Replay and deposit ledger</h3>
                  <p>
                    <strong>Replay:</strong> {paymentRailSandboxReceipt.replay_vault.status}
                  </p>
                  <p>
                    <strong>Deposit:</strong> {paymentRailSandboxReceipt.deposit_ledger.status}
                  </p>
                  <p>{paymentRailSandboxReceipt.deposit_ledger.artifact}</p>
                </article>
                <article>
                  <h3>Settlement remains externally gated</h3>
                  <p>
                    <strong>Controls:</strong> {paymentRailSandboxReceipt.accounting_controls.status}
                  </p>
                  <p>{paymentRailSandboxReceipt.does_not_prove}</p>
                </article>
              </div>
            ) : null}
          </section>
        ) : null}
        {activeSection === 'payments' ? (
          <section className="panel proof-packet-viewer" data-testid="replay-vault-importer" id="replay-vault-importer">
            <div className="section-heading">
              <div>
                <span>De-identified replay vault</span>
                <h2>Import partner sample manifests and prove replay readiness without storing PHI.</h2>
                <p>
                  This import accepts a de-identified artifact manifest with record counts and SHA-256 hashes. It proves
                  VAERION can prepare real-world replay evidence for 837, 835, deposit, denial, and appeal scenarios while
                  raw PHI remains outside the packet.
                </p>
              </div>
              <ShieldCheck size={32} aria-hidden="true" />
            </div>
            <div className="packet-viewer-actions">
              <button className="button primary" type="button" onClick={() => void importDeidentifiedReplayManifest()}>
                Import replay manifest
              </button>
              <button className="button secondary" type="button" onClick={() => void generatePartnerProofPacket()}>
                Attach to partner packet
              </button>
            </div>
            <div className="authority-receipt" role="status">
              <strong>Latest replay import receipt</strong>
              <p>{replayImportStatus}</p>
            </div>
            {replayImportReceipt ? (
              <div className="packet-section-grid">
                <article>
                  <h3>Manifest</h3>
                  <p>{replayImportReceipt.manifest_id}</p>
                  <p>{replayImportReceipt.deidentification_attestation}</p>
                </article>
                <article>
                  <h3>Inventory</h3>
                  <p>{replayImportReceipt.fixture_inventory.total_artifacts} artifact type(s)</p>
                  <p>{replayImportReceipt.fixture_inventory.total_records} de-identified record(s)</p>
                </article>
                <article>
                  <h3>Artifact hashes</h3>
                  {replayImportReceipt.fixture_inventory.artifact_hashes.slice(0, 4).map((hash) => (
                    <code key={hash}>{hash}</code>
                  ))}
                </article>
                <article>
                  <h3>Replay readiness</h3>
                  <p>{readableAuthorityState(replayImportReceipt.replay_readiness.status)}</p>
                  <p>{replayImportReceipt.replay_readiness.next_step}</p>
                  <p>Raw PHI stored: {replayImportReceipt.raw_phi_stored ? 'yes' : 'no'}</p>
                </article>
              </div>
            ) : null}
          </section>
        ) : null}

        <section className={`panel workflow-result ${workflowRun.status}`} data-testid="workflow-result" id="workflow-result">
          <div className="section-heading compact">
            <div>
              <span>Workflow result</span>
              <h2>{workflowRun.title}</h2>
              <p>{workflowRun.summary}</p>
            </div>
            <span className={`status-pill ${workflowRun.status === 'recorded' ? 'complete' : workflowRun.status === 'running' ? 'ready' : 'blocked'}`}>
              {workflowRun.status === 'recorded' ? 'Recorded' : workflowRun.status === 'running' ? 'Running' : 'Authority gated'}
            </span>
          </div>
          <div className="workflow-columns">
            <article>
              <h3>Evidence checked</h3>
              <ul>
                {workflowRun.evidence.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <article>
              <h3>Next actions</h3>
              <ul>
                {workflowRun.nextSteps.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <article>
              <h3>Authority evidence</h3>
              <ul>
                {(workflowRun.blockers.length > 0
                  ? workflowRun.blockers
                  : ['No VAERION-owned issue was found. External authority gates still apply before live action.']
                ).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          </div>
          <div className="authority-receipt">
            <strong>Workflow receipt</strong>
            <p>{workflowRun.receipt}</p>
          </div>
        </section>

        <section className="panel authority-lab" data-testid="authority-workaround-lab" id="authority-workaround-lab">
          <div className="section-heading">
            <div>
              <span>No-signature proof paths</span>
              <h2>Work around waiting, not authority.</h2>
              <p>
                VAERION can prove readiness with synthetic, sandbox, de-identified, and packet-based evidence before
                signatures arrive. These workflows never unlock live payer submission, Epic writes, money movement, or
                hospital PHI access by themselves.
              </p>
            </div>
            <ShieldCheck size={32} aria-hidden="true" />
          </div>
          <div className="authority-grid">
            {authorityWorkarounds.map((workaround) => (
              <article className="authority-card" key={workaround.id}>
                <span className={`status-pill ${workaround.tone}`}>{toneLabel[workaround.tone]}</span>
                <h3>{workaround.authority}</h3>
                <dl>
                  <div>
                    <dt>Cannot bypass</dt>
                    <dd>{workaround.cannotBypass}</dd>
                  </div>
                  <div>
                    <dt>VAERION-owned path</dt>
                    <dd>{workaround.vaerionOwnedPath}</dd>
                  </div>
                  <div>
                    <dt>Proof receipt</dt>
                    <dd>{workaround.proofReceipt}</dd>
                  </div>
                </dl>
                <button className="button secondary full" type="button" onClick={() => runAuthoritySuite(workaround.id)}>
                  Run non-PHI proof
                </button>
              </article>
            ))}
          </div>
          <div className="authority-receipt" data-testid="authority-latest-receipt" role="status">
            <strong>Latest authority receipt</strong>
            <p>{authorityReceipt}</p>
          </div>
        </section>

        <section className="lower-grid">
          <div className="panel">
            <div className="section-heading compact">
              <div>
                <span>API handoff</span>
                <h2>Implementation surfaces</h2>
              </div>
              <button className="button secondary compact-button" type="button" onClick={() => void copyImplementationSurfaces()}>
                <Copy size={17} />
                Copy all
              </button>
            </div>
            <div className="api-list">
              {implementationApi.map((endpoint) => (
                <div className="api-row" key={endpoint}>
                  <code>{endpoint}</code>
                  <button
                    className="button secondary api-copy-button"
                    type="button"
                    aria-label={`Copy ${endpoint}`}
                    onClick={() => void copyImplementationEndpoint(endpoint)}
                  >
                    <Copy size={15} />
                    Copy
                  </button>
                </div>
              ))}
            </div>
            <p className="copy-status" role="status" data-testid="api-copy-status">{copyStatus}</p>
            {manualCopyText ? (
              <textarea
                className="manual-copy-buffer"
                aria-label="Manual copy API buffer"
                readOnly
                value={manualCopyText}
                onFocus={(event) => event.currentTarget.select()}
              />
            ) : null}
          </div>
          <div className="panel">
            <div className="section-heading compact">
              <div>
                <span>Audit proof</span>
                <h2>Evidence stream preview</h2>
              </div>
            </div>
            <div className="proof-list">
              {proofEvents.map((proof) => (
                <article key={proof.event}>
                  <strong>{proof.event}</strong>
                  <p>{proof.detail}</p>
                </article>
              ))}
            </div>
          </div>
          <div className="panel packet-card">
            <span className={generatedPacket ? 'status-pill complete' : 'status-pill action'}>
              {generatedPacket ? 'Generated' : 'Ready'}
            </span>
            <h2>Partner proof packet</h2>
            <p>
              Includes launch gates, route proof, EHR conformance receipts, claim validation stages, payment simulation,
              denial learning, and external authority requirements.
            </p>
            <p className="packet-storage-note">
              Full packet: browser local storage, AWS proof vault when reachable, and JSON download. Durable proof:
              non-PHI AWS receipt plus saved packet record when recording succeeds.
            </p>
            <div className="packet-actions">
              <button className="button secondary full" type="button" onClick={() => void generatePartnerProofPacket()}>
                {generatedPacket ? 'Regenerate packet' : 'Generate packet'}
              </button>
              <button
                className="button ghost full"
                disabled={!proofPacket}
                type="button"
                onClick={() => {
                  setPacketOpen(true);
                  scrollToId('partner-proof-packet-viewer');
                }}
              >
                Open packet
              </button>
              <button className="button ghost full" type="button" onClick={() => downloadPartnerProofPacket()}>
                Download JSON
              </button>
            </div>
          </div>
        </section>

        <section
          className="panel enterprise-deployment-surface"
          data-testid="enterprise-deployment-surface"
          id="enterprise-deployment-surface"
        >
          <div className="section-heading">
            <div>
              <span>Enterprise deployment proof surface</span>
              <h2>Everything VAERION can prove before outside signatures.</h2>
              <p>
                Each surface runs a real non-PHI proof receipt through the billing API. A receipt proves VAERION-owned
                readiness for that domain; it never unlocks payer production transmission, Epic production access,
                hospital PHI cutover, appeal filing, or money movement by itself.
              </p>
            </div>
            <ShieldCheck size={32} aria-hidden="true" />
          </div>
          <div className="authority-receipt" role="status">
            <strong>Latest enterprise deployment receipt</strong>
            <p>{enterpriseSurfaceStatus}</p>
          </div>
          <div className="enterprise-surface-grid">
            {enterpriseDeploymentSurfaces.map((surface) => {
              const receipt = enterpriseSurfaceReceipts[surface.id];
              return (
                <article
                  className={receipt ? 'enterprise-surface-card recorded' : 'enterprise-surface-card'}
                  data-testid={`enterprise-surface-${surface.id}`}
                  id={`enterprise-surface-${surface.id}`}
                  key={surface.id}
                >
                  <div className="enterprise-surface-card-header">
                    <span className={receipt ? 'status-pill complete' : 'status-pill action'}>
                      {receipt ? 'Receipt recorded' : 'Proof runnable'}
                    </span>
                    <strong>{surface.title}</strong>
                  </div>
                  <p>{surface.summary}</p>
                  {receipt ? (
                    <div className="surface-receipt">
                      <h3>{receipt.receipt_id}</h3>
                      <p>{receipt.plain_english_verdict}</p>
                      <dl>
                        <div>
                          <dt>Checks</dt>
                          <dd>
                            {receipt.checks.map((check) => (
                              <span key={check.label}>
                                {check.label}: {check.status}
                              </span>
                            ))}
                          </dd>
                        </div>
                        <div>
                          <dt>Remaining authority</dt>
                          <dd>{receipt.remaining_external_authority.join('; ')}</dd>
                        </div>
                        <div>
                          <dt>Counterparty authority not included</dt>
                          <dd>{receipt.does_not_prove}</dd>
                        </div>
                      </dl>
                    </div>
                  ) : (
                    <div className="surface-empty">
                      <strong>Ready for receipt</strong>
                      <p>Run the proof to record checks, artifacts, authority boundaries, and guardrails.</p>
                    </div>
                  )}
                  <button className="button secondary full" type="button" onClick={() => void runEnterpriseSurface(surface.id)}>
                    {surface.cta}
                  </button>
                </article>
              );
            })}
          </div>
        </section>

        <section className="panel capability-map" data-testid="enterprise-capability-map" id="enterprise-capability-map">
          <div className="section-heading">
            <div>
              <span>Enterprise replacement map</span>
              <h2>What must exist for a hospital-grade billing-system replacement.</h2>
              <p>
                This map is intentionally plain English: what VAERION can run, what remains externally controlled, and
                what action a partner or implementation owner should take next. It prevents the UI from hiding authority requirements
                behind generic status badges.
              </p>
              <p className="capability-note">
                VAERION-owned proof actions can be completed from this console. Counterparty authority required means
                a payer, hospital, Epic/customer, or banking authority must issue evidence before live production action.
              </p>
            </div>
            <ShieldCheck size={32} aria-hidden="true" />
          </div>
          <div className="capability-grid">
            {enterpriseCapabilityMap.map((capability) => (
              <article key={capability.area}>
                <span>{capability.area}</span>
                <strong>{capability.state}</strong>
                <p>{capability.capability}</p>
                <div>
                  <b>Counterparty authority required before live production</b>
                  <p>{capability.gap}</p>
                </div>
                <button
                  className="button secondary full"
                  type="button"
                  onClick={() => runCapabilityAction(capability)}
                >
                  {capability.action}
                </button>
              </article>
            ))}
          </div>
        </section>

        {packetOpen && proofPacket ? (
          <section
            className="panel proof-packet-viewer"
            data-testid="partner-proof-packet-viewer"
            id="partner-proof-packet-viewer"
          >
            <div className="section-heading">
              <div>
                <span>Partner proof packet viewer</span>
                <h2>Open, inspect, and download the generated packet.</h2>
                <p>
                  This is a non-PHI implementation artifact. It proves what VAERION generated internally; it does not
                  prove payer production acceptance, Epic production access, hospital go-live approval, or money
                  movement authority.
                </p>
              </div>
              <FileText size={32} aria-hidden="true" />
            </div>

            <div className="packet-viewer-actions">
              <button className="button primary" type="button" onClick={() => downloadPartnerProofPacket(proofPacket)}>
                <Download size={17} />
                Download JSON
              </button>
              <button className="button primary" type="button" onClick={() => downloadPlainEnglishProof(proofPacket)}>
                <Download size={17} />
                Download plain-English proof
              </button>
              <button className="button primary" type="button" onClick={() => void downloadExecutiveProofPdf(proofPacket)}>
                <Download size={17} />
                Download executive PDF
              </button>
              <button className="button secondary" type="button" onClick={() => void generatePartnerProofPacket()}>
                Regenerate
              </button>
              <button className="button ghost" type="button" onClick={() => setPacketOpen(false)}>
                Close viewer
              </button>
            </div>

            <div className="packet-meta-grid">
              <div>
                <span>Packet ID</span>
                <strong>{proofPacket.packet_id}</strong>
              </div>
              <div>
                <span>Generated</span>
                <strong>{new Date(proofPacket.generated_at).toLocaleString()}</strong>
              </div>
              <div>
                <span>Current location</span>
                <strong>Browser local storage</strong>
                <small>{proofPacketStorageKey}</small>
              </div>
              <div>
                <span>AWS proof receipt</span>
                <strong>{packetReceipt}</strong>
              </div>
              <div>
                <span>Full packet artifact</span>
                <strong>{packetVaultStatus}</strong>
                <small>Download JSON remains available even when the proof vault cannot be reached.</small>
              </div>
            </div>

            <div className="packet-viewer-actions">
              <button className="button secondary" type="button" onClick={() => void saveProofPacketToVault(proofPacket)}>
                Save to proof vault
              </button>
              <button className="button ghost" type="button" onClick={() => void loadProofPacketVault()}>
                Load saved packets
              </button>
            </div>

            <section className="proof-review-panel" data-testid="proof-review-lifecycle" id="proof-review-lifecycle">
              <div>
                <span>Formal proof review lifecycle</span>
                <h3>Record whether this proof packet has been reviewed, accepted, rejected, or expired.</h3>
                <p>
                  This is an internal compliance lifecycle receipt. It makes reviewer accountability visible without
                  implying payer, Epic/customer, hospital, or bank production authority.
                </p>
              </div>
              <label className="field proof-review-field">
                <span>Review decision</span>
                <select
                  aria-label="Proof review decision"
                  onChange={(event) =>
                    setProofReviewDecision(event.target.value as 'reviewed' | 'accepted' | 'rejected' | 'expired')
                  }
                  value={proofReviewDecision}
                >
                  <option value="reviewed">Reviewed</option>
                  <option value="accepted">Accepted for partner diligence</option>
                  <option value="rejected">Rejected for correction</option>
                  <option value="expired">Expired</option>
                </select>
              </label>
              <button className="button secondary" type="button" onClick={() => void recordProofReviewDecision(proofPacket)}>
                Record review decision
              </button>
              <div className="authority-receipt" role="status">
                <strong>Latest proof review receipt</strong>
                <p>{proofReviewStatus}</p>
              </div>
              {proofReviewReceipt ? (
                <div className="packet-section-grid">
                  <article>
                    <h3>Lifecycle state</h3>
                    <p>{readableAuthorityState(proofReviewReceipt.lifecycle_state)}</p>
                  </article>
                  <article>
                    <h3>Reviewer</h3>
                    <p>{proofReviewReceipt.reviewer}</p>
                    <p>{new Date(proofReviewReceipt.reviewed_at).toLocaleString()}</p>
                  </article>
                  <article>
                    <h3>Receipt</h3>
                    <p>{proofReviewReceipt.receipt_id}</p>
                  </article>
                  <article>
                    <h3>Authority effect</h3>
                    <p>External authority unlocked: {proofReviewReceipt.external_authority_unlocked ? 'yes' : 'no'}</p>
                    <p>{proofReviewReceipt.does_not_prove}</p>
                  </article>
                </div>
              ) : null}
            </section>

            {savedPackets.length > 0 ? (
              <div className="proof-ledger-view">
                <h3>Saved proof packets</h3>
                {savedPackets.map((packet) => (
                  <article key={packet.packet_id}>
                    <strong>{packet.packet_id}</strong>
                    <p>{packet.summary}</p>
                    <p>
                      {packet.created_at} · {readableAuthorityState(packet.live_external_action)}
                    </p>
                    <button className="button ghost" type="button" onClick={() => void openSavedProofPacket(packet.packet_id)}>
                      Open saved packet
                    </button>
                  </article>
                ))}
              </div>
            ) : null}

            <div className="packet-mode-tabs" role="tablist" aria-label="Proof packet view mode">
              <button
                aria-selected={packetViewMode === 'plain'}
                className={packetViewMode === 'plain' ? 'active' : ''}
                onClick={() => setPacketViewMode('plain')}
                role="tab"
                type="button"
              >
                Plain-English proof
              </button>
              <button
                aria-selected={packetViewMode === 'ledger'}
                className={packetViewMode === 'ledger' ? 'active' : ''}
                onClick={() => setPacketViewMode('ledger')}
                role="tab"
                type="button"
              >
                Evidence ledger
              </button>
              <button
                aria-selected={packetViewMode === 'json'}
                className={packetViewMode === 'json' ? 'active' : ''}
                onClick={() => setPacketViewMode('json')}
                role="tab"
                type="button"
              >
                Raw JSON
              </button>
            </div>

            {packetViewMode === 'plain' ? (
              <div className="plain-proof-dossier" data-testid="plain-english-proof">
                <article className="proof-verdict">
                  <div>
                    <span>Plain-English evidence verdict</span>
                    <h3>VAERION-owned billing readiness has receipt-level evidence; production authority evidence is separate.</h3>
                    <p>
                      This packet is designed for a hospital executive, compliance officer, revenue-cycle leader, and
                      implementation engineer to read without interpreting JSON. It contains receipt IDs, sources,
                      methods, artifacts, results, and authority limits for each VAERION-owned proof surface.
                    </p>
                  </div>
                  <ShieldAlert size={34} aria-hidden="true" />
                </article>

                <div className="proof-dossier-grid">
                  <article>
                    <h3>Evidence dossier</h3>
                    <ul>
                      {proofPacket.evidence_dossier.slice(0, 8).map((item) => (
                        <li key={`${item.surface}-${item.receipt_id}`}>
                          <strong>{item.surface}:</strong> {item.receipt_id}. {item.result}
                        </li>
                      ))}
                    </ul>
                  </article>
                  {proofPacket.launch_dossier_snapshot ? (
                    <article>
                      <h3>Server launch dossier</h3>
                      <dl>
                        <div>
                          <dt>Tenant</dt>
                          <dd>{proofPacket.launch_dossier_snapshot.tenant}</dd>
                        </div>
                        <div>
                          <dt>Enterprise evidence</dt>
                          <dd>
                            {proofPacket.launch_dossier_snapshot.proven_surface_count} /{' '}
                            {proofPacket.launch_dossier_snapshot.required_surface_count}
                          </dd>
                        </div>
                        <div>
                          <dt>Server packets</dt>
                          <dd>{proofPacket.launch_dossier_snapshot.proof_packet_count}</dd>
                        </div>
                        <div>
                          <dt>Production authority state</dt>
                          <dd>{proofPacket.launch_dossier_snapshot.production_cutover_status}</dd>
                        </div>
                      </dl>
                    </article>
                  ) : null}
                  <article>
                    <h3>What this proves</h3>
                    <ul>
                      <li>VAERION generated this partner launch packet with a unique packet ID and timestamp.</li>
                      <li>Launch gates, workstreams, carrier routes, EHR/FHIR readiness, claim validation, payments, denials, and audit surfaces are represented in one packet.</li>
                      <li>The AWS proof receipt records non-PHI evidence when the billing edge accepts the proof event.</li>
                      <li>The full machine-readable packet is retained in browser local storage, saved to the AWS proof vault when available, and downloadable as JSON.</li>
                    </ul>
                  </article>
                  <article>
                  <h3>Counterparty authority not included</h3>
                    <ul>
                      <li>This does not prove payer production acceptance.</li>
                      <li>This does not prove Epic production access or customer-approved PHI access.</li>
                      <li>This does not prove hospital go-live approval.</li>
                      <li>This does not prove bank, processor, EFT, ACH, lockbox, or treasury authority.</li>
                    </ul>
                  </article>
                  <article>
                    <h3>Evidence records</h3>
                    <dl>
                      <div>
                        <dt>Packet ID</dt>
                        <dd>{proofPacket.packet_id}</dd>
                      </div>
                      <div>
                        <dt>Generated by</dt>
                        <dd>{proofPacket.generated_by}</dd>
                      </div>
                      <div>
                        <dt>AWS proof receipt</dt>
                        <dd>{packetReceipt}</dd>
                      </div>
                      <div>
                        <dt>Full packet artifact</dt>
                        <dd>{packetVaultStatus}</dd>
                      </div>
                      <div>
                        <dt>Evidence rows</dt>
                        <dd>{proofPacket.evidence_dossier.length}</dd>
                      </div>
                    </dl>
                  </article>
                  <article>
                    <h3>Enterprise deployment receipts</h3>
                    {(proofPacket.enterprise_surface_receipts?.length ?? 0) > 0 ? (
                      <ul>
                        {proofPacket.enterprise_surface_receipts?.map((receipt) => (
                          <li key={`${receipt.surface_id}-${receipt.receipt_id}`}>
                            <strong>{receipt.title}:</strong> {receipt.receipt_id}. {receipt.verdict}{' '}
                            Remaining authority: {receipt.remaining_external_authority.join('; ')}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>No enterprise deployment surface receipts were loaded into this packet. Run the surface proofs above, then regenerate this packet.</p>
                    )}
                  </article>
                  <article>
                    <h3>Next exact actions</h3>
                    <ul>
                      <li>Download the JSON artifact for the technical diligence room.</li>
                      <li>Open the saved server-side proof packet before partner review.</li>
                      <li>Attach carrier, Epic/customer, hospital, and treasury authority receipts as each outside party issues them.</li>
                      <li>Run cutover review only after every external authority receipt is recorded.</li>
                    </ul>
                  </article>
                </div>
              </div>
            ) : null}

            {packetViewMode === 'ledger' ? (
              <div className="proof-ledger-view" data-testid="proof-ledger">
                <h3>Evidence dossier rows</h3>
                <div className="evidence-table-wrap">
                  <table className="evidence-table">
                    <thead>
                      <tr>
                        <th scope="col">Surface</th>
                        <th scope="col">Receipt</th>
                        <th scope="col">Method</th>
                        <th scope="col">Result</th>
                        <th scope="col">Authority boundary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proofPacket.evidence_dossier.map((item) => (
                        <tr key={`${item.surface}-${item.receipt_id}`}>
                          <td>{item.surface}</td>
                          <td>{item.receipt_id}</td>
                          <td>{item.method}</td>
                          <td>{item.result}</td>
                          <td>{item.authority_limit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="packet-section-grid">
                  <article>
                    <h3>Launch gates</h3>
                    {proofPacket.launch_gates.map((gate) => (
                      <p key={gate.label}>
                        <strong>{gate.label}:</strong> {gate.state}. {gate.proof}
                      </p>
                    ))}
                  </article>
                  <article>
                    <h3>Carrier route proof</h3>
                    {proofPacket.carrier_routes.slice(0, 5).map((route) => (
                      <p key={route.carrier}>
                        <strong>{route.carrier}:</strong> {route.transactions.join(', ')}. {route.production_gate}
                      </p>
                    ))}
                  </article>
                  <article>
                    <h3>Authority workarounds</h3>
                    {proofPacket.authority_workarounds.map((workaround) => (
                      <p key={workaround.authority}>
                        <strong>{workaround.authority}:</strong> {workaround.vaerion_owned_path}
                      </p>
                    ))}
                  </article>
                  <article>
                    <h3>Implementation API</h3>
                    {proofPacket.implementation_api.map((endpoint) => (
                      <code key={endpoint}>{endpoint}</code>
                    ))}
                  </article>
                </div>
              </div>
            ) : null}

            {packetViewMode === 'json' ? (
              <pre className="packet-json" aria-label="Partner proof packet JSON">
                {JSON.stringify(proofPacket, null, 2)}
              </pre>
            ) : null}
          </section>
        ) : null}
      </section>
    </main>
  );
}
